import { isAwaitingPreparation, scheduleLabel, orderSchedule } from './beclickSchedule';
import StockRecipeChoices, { stockChoiceFor, stockChoiceValid, stockChoiceSignature, stockChoiceSummary, withInitialStockChoice, menuStockSignature, stockRecipeError } from './StockRecipeChoices';
import { stockSupplementSelections } from './stockOrderComposition';
import BefidDeviceCustomer from './BefidDeviceCustomer';
import { getCustomerSessionAccountId, getCustomerOperatorScope } from './kioskCustomerSession';
import BefidAddonEntry from './BefidAddonEntry';
import WorkstationSettings, { useWorkstation } from './WorkstationSettings';
import DevicePeripheralFields, { EMPTY_DEVICE_PERIPHERALS, devicePeripheralPayload } from './DevicePeripheralFields';
import { consumeDrawerOperation, paymentIncludesCash } from './cashDrawer';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_URL, buildKioskAuthHeaders, resolveMediaUrl } from './api';
import { printDailySummary, printOrderTickets } from './thermalPrinter';
import becaisseLogo from './assets/branding-v4/becaisse-on-dark.svg';
import CaisseReservationsView from './CaisseReservationsView';
import TerminalPaymentDialog from './TerminalPaymentDialog';
import { getTerminalConfig, getTerminalReceipt, getTerminalStorageKey, paymentIncludesCard, persistPendingTerminalPayment, readPendingTerminalPayment } from './terminalPayments';
import { downloadAuthenticatedFile } from './downloadAuthenticatedFile';
import { getOfflineAccountScope, scopedOfflineKey, isOfflineScopeCurrent, newOfflineId, readOfflineEntries } from './scopedOfflineQueue';

const TABLES_STORAGE_KEY = 'becaisseTables';
const TABLE_LAYOUT_STORAGE_KEY = 'becaisseTableLayout';
const TABLE_VIEW_MODE_STORAGE_KEY = 'becaisseTableViewMode';
const CAISSE_PIN_STORAGE_KEY = 'becaissePinCode';
const CAISSE_AUTOLOCK_MINUTES_STORAGE_KEY = 'becaisseAutoLockMinutes';
const CAISSE_OFFLINE_QUEUE_KEY = 'becaisseOfflineQueue';
const CAISSE_OFFLINE_CONFLICTS_KEY = 'becaisseOfflineConflicts';
const CAISSE_OFFLINE_HISTORY_KEY = 'becaisseOfflineHistory';
const CAISSE_TABLE_COVERS_KEY = 'becaisseTableCovers';
const CAISSE_BEGUEST_DISMISSED_ALERTS_KEY = 'becaisseBeguestDismissedAlerts';
const DEFAULT_TABLES = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
const DEFAULT_PAYMENT_METHODS = [
  { id: 'cb', label: 'CB' },
  { id: 'espece', label: 'Espèces' },
  { id: 'ticket_resto', label: 'Ticket resto' },
  { id: 'cheque_vacances', label: 'Chèque vacances' },
];

function formatCurrency(value) {
  return `${Number(value || 0).toFixed(2)} €`;
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizePaymentMethodsConfig(value) {
  const input = safeArray(value);
  const merged = [...DEFAULT_PAYMENT_METHODS, ...input];
  const seen = new Set();
  const normalized = [];
  merged.forEach((entry) => {
    const raw = typeof entry === 'string' ? { id: entry, label: entry } : entry;
    const id = String(raw?.id || '')
      .trim()
      .toLowerCase()
      .replace(/[^\w]+/g, '_')
      .replace(/^_+|_+$/g, '');
    if (!id || seen.has(id)) {
      return;
    }
    seen.add(id);
    normalized.push({
      id,
      label: String(raw?.label || raw?.id || id).trim() || id.toUpperCase(),
    });
  });
  return normalized;
}

function normalizeText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function getProductPrice(product, orderMode) {
  if (orderMode === 'sur_place' && product?.dineInPrice !== null && product?.dineInPrice !== undefined && product?.dineInPrice !== '') {
    return Number(product.dineInPrice || 0);
  }
  return Number(product?.price || 0);
}

function buildCartLineKey(product, selectedSupplements = [], note = '', orderMode = 'a_emporter') {
  return [
    product.id,
    orderMode,
    selectedSupplements.map((supplement) => supplement.id).sort().join('-'),
    normalizeText(note),
    stockChoiceSignature(stockChoiceFor(product)),
  ].join(':');
}

function getMenuRequiredQuantity(section) {
  const value = Number(section?.MenuSections?.requiredQuantity ?? section?.requiredQuantity ?? 1);
  return Math.max(1, Number.isFinite(value) ? Math.round(value) : 1);
}

function getMenuProductAdditionalPrice(product) {
  return Number(product?.MenuProducts?.additionalPrice || 0);
}

function buildMenuLineKey(menu, selections = {}, orderMode = 'a_emporter') {
  const parts = Object.keys(selections)
    .sort((a, b) => Number(a) - Number(b))
    .map((sectionId) => `${sectionId}-${safeArray(selections[sectionId]).map(String).sort().join('.')}`);
  return ['menu', menu?.id || '', orderMode, parts.join(':')].join(':');
}

function computeLineTotal(unitPrice, quantity, discountPercent = 0, isComped = false) {
  if (isComped) {
    return 0;
  }
  const gross = Number(unitPrice || 0) * Number(quantity || 0);
  const discount = Math.max(0, Math.min(100, Number(discountPercent || 0)));
  return Number((gross * (1 - (discount / 100))).toFixed(2));
}

function getOrderTableNumber(order) {
  const comment = String(order?.customerComment || '');
  return comment.match(/TABLE\s+([A-Z0-9-]+)/i)?.[1] || '';
}

function getOrderItems(order) {
  return safeArray(order?.OrderItems || order?.items);
}

function formatDateTime(value) {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatTimeShort(value) {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatElapsed(dateValue) {
  if (!dateValue) return '-';
  const start = new Date(dateValue);
  if (Number.isNaN(start.getTime())) return '-';
  const diffMin = Math.max(0, Math.floor((Date.now() - start.getTime()) / 60000));
  const hours = Math.floor(diffMin / 60);
  const mins = diffMin % 60;
  return hours > 0 ? `${hours}h${String(mins).padStart(2, '0')}` : `${mins} min`;
}

function formatElapsedClock(startValue, endValue = null, nowTs = Date.now()) {
  const start = new Date(startValue || '');
  if (Number.isNaN(start.getTime())) return '--:--';
  const end = endValue ? new Date(endValue) : null;
  const endTs = end && !Number.isNaN(end.getTime()) ? end.getTime() : nowTs;
  const elapsedSec = Math.max(0, Math.floor((endTs - start.getTime()) / 1000));
  const mins = Math.floor(elapsedSec / 60);
  const secs = elapsedSec % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

function getElapsedSeconds(startValue, endValue = null, nowTs = Date.now()) {
  const start = new Date(startValue || '');
  if (Number.isNaN(start.getTime())) return 0;
  const end = endValue ? new Date(endValue) : null;
  const endTs = end && !Number.isNaN(end.getTime()) ? end.getTime() : nowTs;
  return Math.max(0, Math.floor((endTs - start.getTime()) / 1000));
}

function getSentLineLogicalKey(line) {
  return `${line?.orderId || ''}::${normalizeText(line?.name || '')}::${Number(line?.unitPrice || 0).toFixed(2)}`;
}

function loadStoredTables() {
  try {
    const parsed = JSON.parse(localStorage.getItem(TABLES_STORAGE_KEY) || '[]');
    const values = safeArray(parsed).map((value) => String(value || '').trim()).filter(Boolean);
    return values.length ? values : DEFAULT_TABLES;
  } catch (error) {
    return DEFAULT_TABLES;
  }
}

function normalizeTableValues(values) {
  return [...new Set(safeArray(values).map((value) => String(value || '').trim()).filter(Boolean))];
}

function buildDefaultTableLayout(tables, rows = 4, cols = 4) {
  const normalizedRows = Math.max(1, Math.min(20, Number(rows) || 4));
  const normalizedCols = Math.max(1, Math.min(20, Number(cols) || 4));
  const size = normalizedRows * normalizedCols;
  const normalizedTables = normalizeTableValues(tables);
  const cells = new Array(size).fill('');
  normalizedTables.slice(0, size).forEach((table, index) => {
    cells[index] = table;
  });
  return {
    rows: normalizedRows,
    cols: normalizedCols,
    cells,
  };
}

function loadStoredTableLayout(tables) {
  const fallback = buildDefaultTableLayout(tables);
  try {
    const parsed = JSON.parse(localStorage.getItem(TABLE_LAYOUT_STORAGE_KEY) || 'null');
    if (!parsed || typeof parsed !== 'object') {
      return fallback;
    }
    const rows = Math.max(1, Math.min(20, Number(parsed.rows) || fallback.rows));
    const cols = Math.max(1, Math.min(20, Number(parsed.cols) || fallback.cols));
    const size = rows * cols;
    const rawCells = Array.isArray(parsed.cells) ? parsed.cells : [];
    const cells = new Array(size).fill('').map((_, index) => String(rawCells[index] || '').trim());
    return { rows, cols, cells };
  } catch (error) {
    return fallback;
  }
}

function loadJsonArrayStorage(key) {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

function saveJsonArrayStorage(key, value) {
  localStorage.setItem(key, JSON.stringify(Array.isArray(value) ? value : []));
}

function getBeguestAlertTitle(alert) {
  return String(alert?.type || '') === 'payment' ? 'Paiement demandé' : 'Appel serveur';
}

function getBeguestAlertMessage(alert) {
  const table = String(alert?.tableId || alert?.deviceName || '').trim() || '-';
  return String(alert?.type || '') === 'payment'
    ? `La table ${table} souhaite payer avec un serveur.`
    : `La table ${table} appelle un serveur.`;
}

function playBeguestAlertFeedback(isPad = false) {
  if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
    navigator.vibrate(isPad ? [160, 80, 160] : [90, 60, 90]);
  }
  const AudioContextCtor = typeof window !== 'undefined' && (window.AudioContext || window.webkitAudioContext);
  if (!AudioContextCtor) {
    return;
  }
  try {
    const context = new AudioContextCtor();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = 'sine';
    oscillator.frequency.value = 880;
    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(isPad ? 0.24 : 0.14, context.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.22);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.24);
    window.setTimeout(() => context.close().catch(() => null), 400);
  } catch (error) {
    // Some browsers block audio until the first user gesture.
  }
}

function getPreferredClientPrinter(printers) {
  if (!Array.isArray(printers) || printers.length === 0) {
    return null;
  }
  return (
    printers.find((printer) => /principale|caisse|client/i.test(printer.name || '')) ||
    printers[0]
  );
}

function mapOrderToPrintableItems(order) {
  return (order?.OrderItems || []).map((item) => ({
    id: item.productId || item.id,
    name: item.name,
    kitchenName: item.kitchenName || item.Product?.kitchenName || '',
    image: item.Product?.image || '',
    totalPrice: Number(item.unitPrice || item.totalPrice || 0),
    quantity: Number(item.quantity || 1),
    isMenu: !!item.isMenu,
    printerId: item.printerId || item.Printer?.id || item.Product?.printerId || null,
    productionZoneId: item.productionZoneId || item.ProductionZone?.id || item.Product?.productionZoneId || null,
    customSelections: Array.isArray(item.selections)
      ? item.selections.filter((selection) => selection?.sectionName)
      : [],
    selectedSupplements: Array.isArray(item.selections)
      ? item.selections.filter((selection) => selection?.kind === 'supplement')
      : [],
  }));
}

function PaymentModal({
  allowMixedPayment = true,
  totals,
  initialMethod,
  availablePaymentMethods,
  paymentTarget,
  cart,
  splitDraft,
  onSplitQtyChange,
  discountMode,
  discountValue,
  onDiscountModeChange,
  onDiscountValueChange,
  maxGlobalDiscountPercent,
  loading,
  onClose,
  onSubmit,
}) {
  const paymentMethods = useMemo(
    () => normalizePaymentMethodsConfig(availablePaymentMethods),
    [availablePaymentMethods]
  );
  const selectableMethods = useMemo(
    () => paymentMethods.filter((entry) => entry.id !== 'mixte'),
    [paymentMethods]
  );
  const [method, setMethod] = useState(initialMethod || selectableMethods[0]?.id || 'cb');
  const [amountInput, setAmountInput] = useState('');
  const [paymentLines, setPaymentLines] = useState([]);
  const total = Number(totals.total || 0);
  const paidAmount = Number(paymentLines.reduce((sum, entry) => sum + Number(entry.amount || 0), 0).toFixed(2));
  const remaining = Math.max(0, Number((total - paidAmount).toFixed(2)));
  const selectedMethodLabel = selectableMethods.find((entry) => entry.id === method)?.label || method.toUpperCase();
  const inputAmountRaw = Number(String(amountInput || '0').replace(',', '.')) || 0;
  const inputAmount = Math.max(0, Number(inputAmountRaw.toFixed(2)));
  const displayedNumpadAmount = amountInput
    ? `${String(amountInput).replace('.', ',')} €`
    : '0,00 €';
  const hasCashLine = paymentLines.some((entry) => entry.method === 'espece') || method === 'espece';
  const effectiveInputAmount = method === 'espece'
    ? inputAmount
    : Math.min(inputAmount, remaining);
  const canAddLine = !!method && effectiveInputAmount > 0 && (allowMixedPayment || !paymentLines.length);
  const canSubmit = paymentLines.length > 0 && paidAmount >= total && (allowMixedPayment || paymentLines.length === 1);
  const changeDue = hasCashLine ? Math.max(0, Number((paidAmount - total).toFixed(2))) : 0;

  const quickCashValues = [
    remaining || total,
    Math.ceil((remaining || total) / 5) * 5,
    Math.ceil((remaining || total) / 10) * 10,
    Math.ceil((remaining || total) / 20) * 20,
  ].filter((value, index, array) => value > 0 && array.indexOf(value) === index);

  useEffect(() => {
    if (!selectableMethods.length) {
      return;
    }
    if (!selectableMethods.some((entry) => entry.id === method)) {
      setMethod(selectableMethods[0].id);
    }
  }, [method, selectableMethods]);

  const addPaymentLine = () => {
    if (!canAddLine) {
      return;
    }
    const amount = Number(effectiveInputAmount.toFixed(2));
    if (amount <= 0) return;
    setPaymentLines((current) => [
      ...current,
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        method,
        label: selectedMethodLabel,
        amount,
      },
    ]);
    setAmountInput('');
  };

  const removePaymentLine = (id) => {
    setPaymentLines((current) => current.filter((entry) => entry.id !== id));
  };

  const pressNumpadKey = (key) => {
    const current = String(amountInput || '').replace(',', '.');
    if (key === 'clear') {
      setAmountInput('');
      return;
    }
    if (key === 'back') {
      setAmountInput(current.slice(0, -1));
      return;
    }
    if (key === '.') {
      if (!current) {
        setAmountInput('0.');
        return;
      }
      if (current.includes('.')) return;
      setAmountInput(`${current}.`);
      return;
    }
    if (!/^\d+$/.test(String(key))) return;
    const next = `${current}${key}`;
    const [intPart = '', decPart = ''] = next.split('.');
    if (intPart.replace(/^0+(?=\d)/, '').length > 6) return;
    if (decPart.length > 2) return;
    setAmountInput(next);
  };

  const submit = () => {
    if (!canSubmit || loading) {
      return;
    }
    const normalizedLines = paymentLines
      .map((entry) => ({
        method: String(entry.method || '').trim().toLowerCase(),
        amount: Number(Number(entry.amount || 0).toFixed(2)),
      }))
      .filter((entry) => entry.method && entry.amount > 0);
    if (!normalizedLines.length) return;
    const isSingle = normalizedLines.length === 1;
    const finalMethod = isSingle ? normalizedLines[0].method : 'mixte';
    const finalAmountPaid = Number(normalizedLines.reduce((sum, entry) => sum + Number(entry.amount || 0), 0).toFixed(2));
    onSubmit({
      paymentMethod: finalMethod,
      amountPaid: finalAmountPaid,
      paymentSplits: isSingle ? undefined : normalizedLines,
    });
  };

  return (
    <div className="becaisse-modal-backdrop" role="presentation" onClick={onClose}>
      <section className="becaisse-payment-modal becaisse-payment-modal--checkout" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <header className="becaisse-payment-head">
          <div>
            <span>Encaissement</span>
            <h2>{formatCurrency(total)}</h2>
          </div>
          <button type="button" onClick={onClose}>Fermer</button>
        </header>

        <div className="becaisse-payment-layout">
          <div className="becaisse-payment-main">
            {paymentTarget === 'cart' && cart.length ? (
              <>
                <div className="becaisse-discount-control">
                  <div className="becaisse-payment-tabs">
                    <button type="button" className={discountMode === 'amount' ? 'is-active' : ''} onClick={() => onDiscountModeChange('amount')}>Remise €</button>
                    <button type="button" className={discountMode === 'percent' ? 'is-active' : ''} onClick={() => onDiscountModeChange('percent')}>%</button>
                  </div>
                  <div className="becaisse-discount-row">
                    {discountMode === 'percent' ? (
                      <select value={discountValue || '0'} onChange={(event) => onDiscountValueChange(event.target.value)}>
                        {Array.from({ length: 11 }).map((_, index) => index * 10).map((percent) => (
                          <option key={percent} value={percent}>{percent}%</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        value={discountValue}
                        onChange={(event) => onDiscountValueChange(event.target.value)}
                        placeholder="Ex: 5.00"
                        inputMode="decimal"
                      />
                    )}
                    <button type="button" onClick={() => onDiscountValueChange('0')}>OK</button>
                  </div>
                  {discountMode === 'percent' ? <small>Plafond: {maxGlobalDiscountPercent}%</small> : null}
                </div>

                <div className="becaisse-payment-box">
                  <div className="becaisse-split-lines">
                    {cart.map((item) => {
                      const qty = Math.max(0, Math.min(Number(item.quantity || 0), Number(splitDraft?.[item.key] || 0)));
                      return (
                        <div key={item.key} className="becaisse-split-line">
                          <div>
                            <strong>{item.name}</strong>
                            <small>{formatCurrency(item.unitPrice)} · dispo {item.quantity}</small>
                          </div>
                          <div className="becaisse-qty-mini">
                            <button type="button" onClick={() => onSplitQtyChange(item.key, qty - 1)}>-</button>
                            <strong>{qty}</strong>
                            <button type="button" onClick={() => onSplitQtyChange(item.key, qty + 1)}>+</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : null}

            <div className="becaisse-discount-control">
              <div className="becaisse-payment-split">
                <label className="becaisse-field">
                  <span>Montant à ajouter</span>
                  <input
                    value={amountInput}
                    inputMode="decimal"
                    onChange={(event) => setAmountInput(event.target.value)}
                    placeholder={remaining > 0 ? formatCurrency(remaining) : '0.00'}
                  />
                </label>
                <button type="button" className="becaisse-secondary-wide" onClick={addPaymentLine} disabled={!canAddLine}>
                  Ajouter {selectedMethodLabel}
                </button>
              </div>
            </div>

            <div className="becaisse-payment-box">
              <strong>Lignes de paiement</strong>
              <div className="becaisse-split-lines">
                {paymentLines.length ? paymentLines.map((line) => (
                  <div key={line.id} className="becaisse-split-line">
                    <div>
                      <strong>{line.label}</strong>
                      <small>{formatCurrency(line.amount)}</small>
                    </div>
                    <button type="button" className="becaisse-top-mini" onClick={() => removePaymentLine(line.id)}>Retirer</button>
                  </div>
                )) : <p className="becaisse-empty-cart">Aucun paiement ajouté.</p>}
              </div>
            </div>
            <div className="becaisse-payment-box becaisse-payment-status-box">
              {method === 'espece' ? (
                <>
                  <strong>Raccourcis espèces</strong>
                  <div className="becaisse-cash-shortcuts">
                    {quickCashValues.map((value) => (
                      <button key={value} type="button" onClick={() => setAmountInput(String(value.toFixed(2)))}>
                        {formatCurrency(value)}
                      </button>
                    ))}
                  </div>
                </>
              ) : null}
              <div className={`becaisse-change ${paidAmount < total ? 'is-missing' : ''}`}>
                <span>{paidAmount < total ? 'Reste à recevoir' : 'Rendu monnaie'}</span>
                <strong>{formatCurrency(paidAmount < total ? remaining : changeDue)}</strong>
              </div>
            </div>

            <button type="button" className="becaisse-checkout becaisse-payment-submit" onClick={submit} disabled={!canSubmit || loading}>
              <span>{loading ? 'Encaissement...' : 'Valider paiement'}</span>
              <strong>{formatCurrency(total)}</strong>
            </button>
          </div>

          <aside className="becaisse-payment-methods-side">
            <strong>Moyens</strong>
            {!allowMixedPayment ? <p role="status">Le paiement mixte de plusieurs tickets n'est pas disponible. Utilisez un seul moyen ou encaissez chaque ticket séparément.</p> : null}
            <div className="becaisse-payment-methods is-vertical">
              {selectableMethods.map((entry) => (
                <button type="button" key={entry.id} className={method === entry.id ? 'is-active' : ''} onClick={() => setMethod(entry.id)}>
                  {entry.label}
                </button>
              ))}
            </div>
            <div className="becaisse-payment-numpad">
              <strong>Pavé numérique</strong>
              <div className={`becaisse-payment-numpad-display ${amountInput ? 'has-value' : ''}`}>
                <span>{displayedNumpadAmount}</span>
                <small>Reste {formatCurrency(remaining)}</small>
              </div>
              <div className="becaisse-payment-numpad-grid">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
                  <button key={`num-${digit}`} type="button" onClick={() => pressNumpadKey(String(digit))}>{digit}</button>
                ))}
                <button type="button" onClick={() => pressNumpadKey('.')}>.</button>
                <button type="button" onClick={() => pressNumpadKey('0')}>0</button>
                <button type="button" onClick={() => pressNumpadKey('back')}>⌫</button>
              </div>
              <div className="becaisse-payment-numpad-actions">
                <button type="button" onClick={() => pressNumpadKey('clear')}>Effacer</button>
                <button type="button" className="is-primary" onClick={addPaymentLine} disabled={!canAddLine}>Ajouter</button>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function CancelReasonModal({
  open,
  loading,
  value,
  onChange,
  onClose,
  onSubmit,
}) {
  if (!open) {
    return null;
  }
  const presets = [
    'Erreur de saisie',
    'Demande client',
    'Rupture produit',
    'Doublon ticket',
    'Problème cuisine',
  ];
  return (
    <div className="becaisse-modal-backdrop" role="presentation" onClick={onClose}>
      <section className="becaisse-payment-modal becaisse-cancel-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <header className="becaisse-payment-head">
          <div>
            <span>Annulation ticket</span>
            <h2>Motif obligatoire</h2>
          </div>
          <button type="button" onClick={onClose}>Fermer</button>
        </header>
        <div className="becaisse-cancel-presets">
          {presets.map((preset) => (
            <button key={preset} type="button" onClick={() => onChange(preset)}>{preset}</button>
          ))}
        </div>
        <label className="becaisse-field">
          <span>Motif</span>
          <textarea value={value} onChange={(event) => onChange(event.target.value)} placeholder="Saisir le motif d’annulation" />
        </label>
        <button
          type="button"
          className="becaisse-checkout becaisse-payment-submit"
          disabled={loading || !String(value || '').trim()}
          onClick={onSubmit}
        >
          <span>{loading ? 'Annulation...' : 'Valider annulation'}</span>
          <strong>OK</strong>
        </button>
      </section>
    </div>
  );
}

function SentLineCancelModal({
  open,
  loading,
  line,
  value,
  onChange,
  onClose,
  onConfirm,
}) {
  if (!open || !line) {
    return null;
  }
  return (
    <div className="becaisse-modal-backdrop" role="presentation" onClick={onClose}>
      <section className="becaisse-payment-modal becaisse-sent-cancel-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <header className="becaisse-payment-head">
          <div>
            <span>Produit déjà envoyé</span>
            <h2>Annuler en cuisine ?</h2>
          </div>
          <button type="button" onClick={onClose} disabled={loading}>Fermer</button>
        </header>
        <div className="becaisse-sent-cancel-summary">
          <strong>{line.quantity} x {line.name}</strong>
          <span>Ticket #{line.ticketNumber || line.orderId} · {formatCurrency(line.totalPrice)}</span>
        </div>
        <p className="becaisse-sent-cancel-warning">
          Ça retire le produit du ticket et imprime en cuisine un ticket avec <b>PRODUIT ANNULE</b>.
        </p>
        <label className="becaisse-field">
          <span>Motif</span>
          <textarea value={value} onChange={(event) => onChange(event.target.value)} placeholder="Motif d’annulation" />
        </label>
        <button
          type="button"
          className="becaisse-checkout becaisse-payment-submit is-danger"
          disabled={loading || !String(value || '').trim()}
          onClick={onConfirm}
        >
          <span>{loading ? 'Annulation...' : 'Confirmer annulation'}</span>
          <strong>OK</strong>
        </button>
      </section>
    </div>
  );
}

function LockScreenModal({
  open,
  pinInput,
  error,
  onChange,
  onUnlock,
}) {
  if (!open) {
    return null;
  }
  return (
    <div className="becaisse-lock-overlay">
      <section className="becaisse-lock-card" role="dialog" aria-modal="true">
        <h2>Caisse verrouillée</h2>
        <p>Entre le code PIN opérateur pour reprendre le service.</p>
        <input
          type="password"
          inputMode="numeric"
          maxLength={8}
          value={pinInput}
          onChange={(event) => onChange(event.target.value)}
          placeholder="PIN"
          autoFocus
        />
        {error ? <span className="becaisse-lock-error">{error}</span> : null}
        <button type="button" className="becaisse-checkout" onClick={onUnlock}>
          <span>Déverrouiller</span>
          <strong>OK</strong>
        </button>
      </section>
    </div>
  );
}

function CoversModal({
  open,
  tableNumber,
  value,
  onChange,
  onClose,
  onConfirm,
}) {
  if (!open) return null;
  const pressDigit = (digit) => {
    const next = `${String(value || '').replace(/\D/g, '')}${digit}`.slice(0, 2);
    onChange(next);
  };
  const clear = () => onChange('');
  const back = () => onChange(String(value || '').slice(0, -1));
  return (
    <div className="becaisse-modal-backdrop" role="presentation" onClick={onClose}>
      <section className="becaisse-payment-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <header className="becaisse-payment-head">
          <div>
            <span>Table {tableNumber || '-'}</span>
            <h2>Nombre de couverts</h2>
          </div>
          <button type="button" onClick={onClose}>Fermer</button>
        </header>
        <div className="becaisse-covers-value">{Number(String(value || '0')) || 0}</div>
        <div className="becaisse-covers-pad">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
            <button key={digit} type="button" onClick={() => pressDigit(digit)}>{digit}</button>
          ))}
          <button type="button" onClick={clear}>C</button>
          <button type="button" onClick={() => pressDigit(0)}>0</button>
          <button type="button" onClick={back}>⌫</button>
        </div>
        <button type="button" className="becaisse-checkout becaisse-payment-submit" onClick={onConfirm}>
          <span>Valider</span>
          <strong>OK</strong>
        </button>
      </section>
    </div>
  );
}

function QtyPadModal({
  open,
  itemName,
  value,
  onChange,
  onClose,
  onConfirm,
}) {
  if (!open) return null;
  const pressDigit = (digit) => {
    const next = `${String(value || '').replace(/\D/g, '')}${digit}`.slice(0, 3);
    onChange(next);
  };
  const clear = () => onChange('');
  const back = () => onChange(String(value || '').slice(0, -1));
  return (
    <div className="becaisse-modal-backdrop" role="presentation" onClick={onClose}>
      <section className="becaisse-payment-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <header className="becaisse-payment-head">
          <div>
            <span>Modifier la quantité</span>
            <h2>{itemName || 'Produit'}</h2>
          </div>
          <button type="button" onClick={onClose}>Fermer</button>
        </header>
        <div className="becaisse-covers-value">{Math.max(1, parseInt(String(value || '1'), 10) || 1)}</div>
        <div className="becaisse-covers-pad">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((digit) => (
            <button key={digit} type="button" onClick={() => pressDigit(digit)}>{digit}</button>
          ))}
          <button type="button" onClick={clear}>C</button>
          <button type="button" onClick={() => pressDigit(0)}>0</button>
          <button type="button" onClick={back}>⌫</button>
        </div>
        <button type="button" className="becaisse-checkout becaisse-payment-submit" onClick={onConfirm}>
          <span>Valider</span>
          <strong>OK</strong>
        </button>
      </section>
    </div>
  );
}

function NoteLineModal({
  open,
  itemName,
  value,
  onChange,
  onClose,
  onConfirm,
}) {
  if (!open) return null;
  return (
    <div className="becaisse-modal-backdrop" role="presentation" onClick={onClose}>
      <section className="becaisse-payment-modal becaisse-note-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <header className="becaisse-payment-head">
          <div>
            <span>Note produit</span>
            <h2>{itemName}</h2>
          </div>
          <button type="button" onClick={onClose}>Fermer</button>
        </header>
        <label className="becaisse-field">
          <span>Note cuisine / service</span>
          <textarea
            autoFocus
            value={value}
            onChange={(event) => onChange(event.target.value)}
            placeholder="Ex: sans sauce, bien cuit, allergie..."
          />
        </label>
        <div className="becaisse-note-actions">
          <button type="button" className="becaisse-top-mini" onClick={() => onChange('')}>Effacer</button>
          <button type="button" className="becaisse-checkout becaisse-payment-submit" onClick={onConfirm}>
            <span>Enregistrer</span>
            <strong>OK</strong>
          </button>
        </div>
      </section>
    </div>
  );
}

function MergeTableModal({
  open,
  currentTable,
  mergeSourceTable,
  options,
  loading,
  onClose,
  onSelect,
  onConfirm,
}) {
  if (!open) return null;
  return (
    <div className="becaisse-modal-backdrop" role="presentation" onClick={onClose}>
      <section className="becaisse-payment-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <header className="becaisse-payment-head">
          <div>
            <span>Fusion de table</span>
            <h2>Vers table {currentTable || '-'}</h2>
          </div>
          <button type="button" onClick={onClose}>Fermer</button>
        </header>
        <div className="becaisse-merge-list">
          {options.length ? options.map((table) => (
            <button
              key={table.number}
              type="button"
              className={String(mergeSourceTable) === String(table.number) ? 'is-active' : ''}
              onClick={() => onSelect(String(table.number))}
            >
              <span>Table {table.number}</span>
              <strong>{formatCurrency(table.total)}</strong>
            </button>
          )) : <p className="becaisse-empty-cart">Aucune table à fusionner.</p>}
        </div>
        <button type="button" className="becaisse-checkout becaisse-payment-submit" onClick={onConfirm} disabled={loading || !mergeSourceTable}>
          <span>{loading ? 'Fusion...' : 'Fusionner maintenant'}</span>
          <strong>OK</strong>
        </button>
      </section>
    </div>
  );
}

function TablePickerModal({
  open,
  currentTable,
  options,
  onClose,
  onSelect,
}) {
  if (!open) return null;
  return (
    <div className="becaisse-modal-backdrop" role="presentation" onClick={onClose}>
      <section className="becaisse-payment-modal becaisse-table-picker-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <header className="becaisse-payment-head">
          <div>
            <span>Changer de table</span>
            <h2>Table actuelle: {currentTable || '-'}</h2>
          </div>
          <button type="button" className="becaisse-modal-close-btn" onClick={onClose}>✕</button>
        </header>
        <div className="becaisse-merge-list">
          {options.map((entry) => {
            const table = typeof entry === 'object'
              ? entry
              : { number: String(entry), total: 0, status: 'libre', itemsCount: 0, openedAt: null };
            const status = table.total > 0 ? (table.status || 'ouverte') : 'libre';
            return (
              <button
                key={String(table.number)}
                type="button"
                className={[
                  String(currentTable) === String(table.number) ? 'is-active' : '',
                  table.total > 0 ? 'has-order' : '',
                  `is-${status}`,
                ].filter(Boolean).join(' ')}
                onClick={() => onSelect(String(table.number))}
              >
                <span>Table {table.number}</span>
                <strong>{formatCurrency(table.total)}</strong>
                <small>
                  {table.total > 0
                    ? `${table.itemsCount || 0} art. · ouverte ${formatTimeShort(table.openedAt) || '-'}`
                    : 'Libre'}
                </small>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function AccountSwitchModal({
  open,
  loading,
  form,
  accountOptions,
  error,
  onClose,
  onChange,
  onSubmit,
}) {
  if (!open) return null;
  return (
    <div className="becaisse-modal-backdrop" role="presentation" onClick={onClose}>
      <section className="becaisse-payment-modal becaisse-account-switch-modal" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
        <header className="becaisse-payment-head">
          <div>
            <span>Connexion caisse</span>
            <h2>Changer de compte</h2>
          </div>
          <button type="button" onClick={onClose}>Fermer</button>
        </header>
        <div className="becaisse-account-switch-fields">
          <label className="becaisse-field">
            <span>Utilisateur</span>
            <select
              autoFocus
              value={form.username}
              onChange={(event) => onChange('username', event.target.value)}
            >
              <option value="">Choisir un utilisateur</option>
              {safeArray(accountOptions).map((option) => (
                <option key={String(option.id)} value={option.name}>{option.name}</option>
              ))}
            </select>
          </label>
          <label className="becaisse-field">
            <span>Mot de passe</span>
            <input
              type="password"
              value={form.password}
              onChange={(event) => onChange('password', event.target.value)}
              placeholder="Code utilisateur"
            />
          </label>
          {error ? <p className="becaisse-lock-error">{error}</p> : null}
        </div>
        <button type="button" className="becaisse-checkout becaisse-payment-submit" onClick={onSubmit} disabled={loading}>
          <span>{loading ? 'Connexion...' : 'Se connecter'}</span>
          <strong>OK</strong>
        </button>
      </section>
    </div>
  );
}

function BeguestServiceAlertModal({ alert, loading, onDismiss, onClaim }) {
  if (!alert) return null;
  const table = String(alert.tableId || alert.deviceName || '').trim() || '-';
  const isPayment = String(alert.type || '') === 'payment';
  return (
    <div className="becaisse-beguest-alert-backdrop" role="presentation">
      <section className={`becaisse-beguest-alert ${isPayment ? 'is-payment' : 'is-call'}`} role="dialog" aria-modal="true" aria-live="assertive">
        <div className="becaisse-beguest-alert-icon">{isPayment ? '€' : '!'}</div>
        <div className="becaisse-beguest-alert-body">
          <p>{getBeguestAlertTitle(alert)}</p>
          <h2>Table {table}</h2>
          <span>{getBeguestAlertMessage(alert)}</span>
        </div>
        <div className="becaisse-beguest-alert-actions">
          <button type="button" className="becaisse-secondary" onClick={onDismiss} disabled={loading}>
            Fermer
          </button>
          <button type="button" className="becaisse-checkout" onClick={onClaim} disabled={loading}>
            <span>{loading ? 'En cours...' : "J'y vais"}</span>
          </button>
        </div>
      </section>
    </div>
  );
}

function CaisseView({ mode = 'desktop' }) {
  const [offlineScope] = useState(getOfflineAccountScope);
  const queueKey = scopedOfflineKey(CAISSE_OFFLINE_QUEUE_KEY, offlineScope);
  const conflictsKey = scopedOfflineKey(CAISSE_OFFLINE_CONFLICTS_KEY, offlineScope);
  const historyKey = scopedOfflineKey(CAISSE_OFFLINE_HISTORY_KEY, offlineScope);
  const pendingOrderRef = useRef(null);
  const checkoutBusyRef = useRef(false);
  const [terminalOrderId, setTerminalOrderId] = useState(() => readPendingTerminalPayment()?.orderId || null);
  const terminalDraftKey = getTerminalStorageKey('berestoTerminalOrderDraft', offlineScope);
  const orderMutationBusyRef = useRef(false);
  const orderMutationRequestsRef = useRef(new Map());
  const getStableOrderMutation = (route, body, version) => {
    const key = JSON.stringify([offlineScope, route, body, version]);
    const requests = orderMutationRequestsRef.current;
    if (!requests.has(key)) {
      if (requests.size >= 100) requests.delete(requests.keys().next().value);
      requests.set(key, { ...body, clientRequestId: newOfflineId() });
    }
    return { key, body: requests.get(key) };
  };
  const explicitSplitKeysRef = useRef(new Set());
  const replayRunningRef = useRef(false);
  const replayQueueRef = useRef(null);
  const hasLegacyQueue = readOfflineEntries(CAISSE_OFFLINE_QUEUE_KEY).length > 0;
  const isPad = mode === 'pad';
  const [products, setProducts] = useState([]);
  const [menus, setMenus] = useState([]);
  const [sections, setSections] = useState([]);
  const [cart, setCart] = useState([]);
  const [activeSectionId, setActiveSectionId] = useState('all');
  const [search, setSearch] = useState('');
  const [orderMode, setOrderMode] = useState('sur_place');
  const [tableNumber, setTableNumber] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [befidCustomer, setBefidCustomer] = useState(null);
  const befidCustomerScope = useRef(getCustomerOperatorScope());
  const [befidTicketGeneration, setBefidTicketGeneration] = useState(0);
  useEffect(() => {
    const reset = () => { if (!isOfflineScopeCurrent(offlineScope) || befidCustomerScope.current !== getCustomerOperatorScope()) { setBefidCustomer(null); befidCustomerScope.current = getCustomerOperatorScope(); setBefidTicketGeneration((value) => value + 1); } };
    window.addEventListener('storage', reset); window.addEventListener('focus', reset);
    return () => { window.removeEventListener('storage', reset); window.removeEventListener('focus', reset); };
  }, [offlineScope]);
  useEffect(() => { setBefidCustomer(null); setBefidTicketGeneration((value) => value + 1); }, [tableNumber, orderMode]);
  const [orderNote, setOrderNote] = useState('');
  const [discountValue, setDiscountValue] = useState('');
  const [discountMode, setDiscountMode] = useState('amount');
  const [paymentMethod, setPaymentMethod] = useState('cb');
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [refundDraft, setRefundDraft] = useState(null);
  const refundBusyRef = useRef(false);
  const [paymentTarget, setPaymentTarget] = useState('cart');
  const [activeView, setActiveView] = useState('sale');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [openOrders, setOpenOrders] = useState([]);
  const [ordersHistory, setOrdersHistory] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersFilter, setOrdersFilter] = useState('all');
  const [ordersDateFrom, setOrdersDateFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [ordersDateTo, setOrdersDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedOrderForPayment, setSelectedOrderForPayment] = useState(null);
  const [splitLinesDraft, setSplitLinesDraft] = useState({});
  const [cancelLinesDraft, setCancelLinesDraft] = useState({});
  const [orderAuditLogs, setOrderAuditLogs] = useState([]);
  const [orderAuditLoading, setOrderAuditLoading] = useState(false);
  const [orderAuditFilter, setOrderAuditFilter] = useState('all');
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReasonValue, setCancelReasonValue] = useState('');
  const [sentLineCancelTarget, setSentLineCancelTarget] = useState(null);
  const [sentLineCancelReason, setSentLineCancelReason] = useState('Annulation produit');
  const [sentLineCancelLoading, setSentLineCancelLoading] = useState(false);
  const [capabilities, setCapabilities] = useState(null);
  const [pinCode, setPinCode] = useState(() => localStorage.getItem(CAISSE_PIN_STORAGE_KEY) || '');
  const [pinDraft, setPinDraft] = useState(() => localStorage.getItem(CAISSE_PIN_STORAGE_KEY) || '');
  const [autoLockMinutes, setAutoLockMinutes] = useState(() => {
    const raw = parseInt(localStorage.getItem(CAISSE_AUTOLOCK_MINUTES_STORAGE_KEY) || '15', 10);
    return Number.isFinite(raw) ? Math.max(1, Math.min(120, raw)) : 15;
  });
  const [isLocked, setIsLocked] = useState(false);
  const [unlockPinInput, setUnlockPinInput] = useState('');
  const [unlockError, setUnlockError] = useState('');
  const [offlineQueue, setOfflineQueue] = useState(() => loadJsonArrayStorage(queueKey));
  const [offlineConflicts, setOfflineConflicts] = useState(() => loadJsonArrayStorage(conflictsKey));
  const [offlineHistory, setOfflineHistory] = useState(() => loadJsonArrayStorage(historyKey));
  const [isOnline, setIsOnline] = useState(() => (typeof navigator !== 'undefined' ? navigator.onLine : true));
  const [syncRunning, setSyncRunning] = useState(false);
  const [controlSummary, setControlSummary] = useState(null);
  const [controlSummaryLoading, setControlSummaryLoading] = useState(false);
  const [transferTargetTable, setTransferTargetTable] = useState('');
  const [mergeSourceTable, setMergeSourceTable] = useState('');
  const [selectedTableOrderIds, setSelectedTableOrderIds] = useState([]);
  const [cashSessions, setCashSessions] = useState([]);
  const [cashSessionsLoading, setCashSessionsLoading] = useState(false);
  const [openingFloatInput, setOpeningFloatInput] = useState('');
  const [countedCashInput, setCountedCashInput] = useState('');
  const [reportDate, setReportDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dailySummary, setDailySummary] = useState(null);
  const [dailySummaryLoading, setDailySummaryLoading] = useState(false);
  const [dailySummaryPrinting, setDailySummaryPrinting] = useState(false);
  const [clientPrinters, setClientPrinters] = useState([]);
  const [productionZones, setProductionZones] = useState([]);
  const [ticketSettings, setTicketSettings] = useState({});
  const [tableSettings, setTableSettings] = useState(() => loadStoredTables());
  const [tablesDraft, setTablesDraft] = useState(() => loadStoredTables().join(', '));
  const [tableViewMode, setTableViewMode] = useState(() => {
    const value = String(localStorage.getItem(TABLE_VIEW_MODE_STORAGE_KEY) || 'plan').toLowerCase();
    return value === 'list' ? 'list' : 'plan';
  });
  const [tableLayout, setTableLayout] = useState(() => loadStoredTableLayout(loadStoredTables()));
  const [tableLayoutDraft, setTableLayoutDraft] = useState(() => loadStoredTableLayout(loadStoredTables()));
  const [reservationConfigDraft, setReservationConfigDraft] = useState({
    reservationOpeningHour: 12,
    reservationClosingHour: 22,
    reservationSlotMinutes: 30,
    reservationMaxPartySize: 12,
    reservationTableCapacity: 40,
    reservationDefaultDurationMinutes: 120,
    kdsItemTimerLimitEnabled: false,
    kdsItemTimerLimitSeconds: 900,
  });
  const [paymentMethodsDraft, setPaymentMethodsDraft] = useState('CB, Espèces, Ticket resto, Chèque vacances');
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [profilesList, setProfilesList] = useState([]);
  const [profilesZones, setProfilesZones] = useState([]);
  const workstation = useWorkstation();
  const [drawerBusy, setDrawerBusy] = useState(false);
  const [drawerMessage, setDrawerMessage] = useState('');
  const [drawerReason, setDrawerReason] = useState('');
  const [drawerFormOpen, setDrawerFormOpen] = useState(false);
  const drawerBusyRef = useRef(false);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [restaurantDevices, setRestaurantDevices] = useState([]);
  const [deviceLimits, setDeviceLimits] = useState({});
  const [deviceEditor, setDeviceEditor] = useState({
    id: null,
    name: '',
    deviceType: 'borne',
    ...EMPTY_DEVICE_PERIPHERALS,
    productionZoneId: '',
    isActive: true,
  });
  const [profileEditor, setProfileEditor] = useState({
    id: null,
    username: '',
    password: '',
    userCode: '',
    role: 'operator',
    profileType: 'pad',
    caisseEnabled: false,
    borneEnabled: true,
    isActive: true,
    permissions: {
      canOpenCloseCashSession: true,
      canOpenCashDrawer: false,
      canManageUsers: false,
      canManageProducts: false,
      canManageReservations: true,
      canManageReports: true,
      canRefund: false,
      canApplyDiscountOver20: false,
    },
    printZoneIds: [],
  });
  const [customersSearch, setCustomersSearch] = useState('');
  const [customersList, setCustomersList] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [padMenuOpen, setPadMenuOpen] = useState(false);
  const [splitDraft, setSplitDraft] = useState({});
  const [tableCovers, setTableCovers] = useState(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem(CAISSE_TABLE_COVERS_KEY) || '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (error) {
      return {};
    }
  });
  const [coversModalOpen, setCoversModalOpen] = useState(false);
  const [coversDraft, setCoversDraft] = useState('');
  const [coversTargetTable, setCoversTargetTable] = useState('');
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [tablePickerOpen, setTablePickerOpen] = useState(false);
  const [reclaimedSentLineIds, setReclaimedSentLineIds] = useState([]);
  const [reclaimedSentLineKeys, setReclaimedSentLineKeys] = useState([]);
  const [timerNow, setTimerNow] = useState(() => Date.now());
  const [padCartExpanded, setPadCartExpanded] = useState(false);
  const [qtyModalOpen, setQtyModalOpen] = useState(false);
  const [qtyModalItemKey, setQtyModalItemKey] = useState('');
  const [qtyModalItemName, setQtyModalItemName] = useState('');
  const [qtyModalValue, setQtyModalValue] = useState('1');
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteModalItemKey, setNoteModalItemKey] = useState('');
  const [noteModalItemName, setNoteModalItemName] = useState('');
  const [noteModalValue, setNoteModalValue] = useState('');
  const [recipeProduct, setRecipeProduct] = useState(null);
  const [menuComposer, setMenuComposer] = useState({
    open: false,
    menu: null,
    selections: {},
    error: '',
  });
  const [currentKioskAccount, setCurrentKioskAccount] = useState(() => {
    try {
      const account = JSON.parse(localStorage.getItem('kioskAccount') || '{}');
      return account && typeof account === 'object' ? account : {};
    } catch (error) {
      return {};
    }
  });
  const [accountSwitchOpen, setAccountSwitchOpen] = useState(false);
  const [accountSwitchLoading, setAccountSwitchLoading] = useState(false);
  const [accountSwitchError, setAccountSwitchError] = useState('');
  const [accountSwitchForm, setAccountSwitchForm] = useState({ username: '', password: '' });
  const [accountSwitchOptions, setAccountSwitchOptions] = useState([]);
  const [beguestAlerts, setBeguestAlerts] = useState([]);
  const [dismissedBeguestAlertIds, setDismissedBeguestAlertIds] = useState(() => loadJsonArrayStorage(CAISSE_BEGUEST_DISMISSED_ALERTS_KEY));
  const [beguestAlertLoading, setBeguestAlertLoading] = useState(false);
  const seenBeguestAlertIdsRef = useRef(new Set());
  const cartLinesRef = useRef(null);
  const pendingCartScrollKeyRef = useRef('');
  const pendingCartScrollNodeRef = useRef(null);

  const fetchWithAuth = useCallback((url, options = {}) =>
    fetch(url, {
      ...options,
      cache: 'no-store',
      headers: buildKioskAuthHeaders(options.headers),
    }), []);

  useEffect(() => {
    let draft;
    try { draft = JSON.parse(localStorage.getItem(terminalDraftKey) || 'null'); } catch { return undefined; }
    if (!draft?.body || readPendingTerminalPayment(offlineScope)) return undefined;
    let active = true;
    pendingOrderRef.current = draft;
    // Retrying the persisted creation request cannot create a second ticket.
    fetchWithAuth(`${API_URL}/api/orders`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(draft.body) })
      .then(async response => {
        const order = await response.json();
        if (!active || !isOfflineScopeCurrent(offlineScope)) return;
        if (!response.ok) throw new Error(order?.error || 'Ticket en attente de récupération.');
        if (draft.kind === 'cash') {
          setSelectedOrderForPayment(order); setPaymentTarget('order');
          setNotice(`Ticket #${order.ticketNumber || order.id} récupéré. Vérifiez son encaissement avant de continuer.`);
        } else {
          persistPendingTerminalPayment({ orderId: Number(order.id), state: 'ready', amountCents: Math.round(Number(order.totalTtc) * 100), context: { source: 'caisse', printProductionTickets: true } }, offlineScope);
          setTerminalOrderId(order.id);
        }
        localStorage.removeItem(terminalDraftKey);
        pendingOrderRef.current = { ...draft, order };
      }).catch(failure => { if (active) setError(`${failure.message} Rechargez pour reprendre le même ticket.`); });
    return () => { active = false; };
  }, [fetchWithAuth, offlineScope, terminalDraftKey]);

  const fetchBeguestAlerts = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/beguest/alerts`);
      if (!response.ok) {
        return;
      }
      const payload = await response.json();
      const alerts = safeArray(payload);
      setBeguestAlerts(alerts);
      const dismissedSet = new Set(dismissedBeguestAlertIds);
      const visibleAlerts = alerts.filter((alert) => !dismissedSet.has(String(alert.id || '')));
      const newVisibleAlert = visibleAlerts.find((alert) => !seenBeguestAlertIdsRef.current.has(String(alert.id || '')));
      alerts.forEach((alert) => {
        if (alert?.id) {
          seenBeguestAlertIdsRef.current.add(String(alert.id));
        }
      });
      if (newVisibleAlert) {
        playBeguestAlertFeedback(isPad);
      }
    } catch (pollError) {
      // Keep the sales screen usable if the alert endpoint is temporarily unavailable.
    }
  }, [dismissedBeguestAlertIds, fetchWithAuth, isPad]);

  const dismissBeguestAlert = useCallback((alertId) => {
    const normalizedId = String(alertId || '').trim();
    if (!normalizedId) {
      return;
    }
    setDismissedBeguestAlertIds((current) => {
      const next = [...new Set([...current, normalizedId])].slice(-80);
      saveJsonArrayStorage(CAISSE_BEGUEST_DISMISSED_ALERTS_KEY, next);
      return next;
    });
  }, []);

  const claimBeguestAlert = useCallback(async (alert) => {
    if (!alert?.id) {
      return;
    }
    setBeguestAlertLoading(true);
    try {
      const response = await fetchWithAuth(`${API_URL}/api/beguest/alerts/${encodeURIComponent(alert.id)}/claim`, {
        method: 'POST',
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || 'Alerte BEGuest impossible à prendre.');
      }
      setBeguestAlerts((current) => current.filter((entry) => String(entry.id) !== String(alert.id)));
      dismissBeguestAlert(alert.id);
      setNotice(`${getBeguestAlertTitle(alert)} table ${alert.tableId || alert.deviceName || '-'} pris en charge.`);
    } catch (claimError) {
      setError(claimError.message || 'Erreur alerte BEGuest');
    } finally {
      setBeguestAlertLoading(false);
    }
  }, [dismissBeguestAlert, fetchWithAuth]);

  const pushOfflineAction = useCallback((action) => {
    setOfflineQueue((current) => {
      const next = [
        ...current,
        {
          id: `offline-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          createdAt: new Date().toISOString(),
          retries: 0,
          ...action,
          accountScope: offlineScope,
        },
      ];
      saveJsonArrayStorage(queueKey, next);
      return next;
    });
  }, [offlineScope, queueKey]);

  const pushOfflineConflict = useCallback((entry) => {
    setOfflineConflicts((current) => {
      const next = [
        {
          at: new Date().toISOString(),
          ...entry,
        },
        ...current,
      ].slice(0, 100);
      saveJsonArrayStorage(conflictsKey, next);
      return next;
    });
  }, [conflictsKey]);

  const pushOfflineHistory = useCallback((entry) => {
    setOfflineHistory((current) => {
      const next = [{ at: new Date().toISOString(), ...entry }, ...current].slice(0, 200);
      saveJsonArrayStorage(historyKey, next);
      return next;
    });
  }, [historyKey]);

  const removeOfflineActionById = useCallback((id) => {
    setOfflineQueue((current) => {
      const next = current.filter((item) => item.id !== id);
      saveJsonArrayStorage(queueKey, next);
      return next;
    });
  }, [queueKey]);

  const upsertOfflineAction = useCallback((id, patch) => {
    setOfflineQueue((current) => {
      const next = current.map((item) => (item.id === id ? { ...item, ...patch } : item));
      saveJsonArrayStorage(queueKey, next);
      return next;
    });
  }, [queueKey]);

  const accountRole = useMemo(
    () => String(currentKioskAccount?.role || '').trim().toLowerCase(),
    [currentKioskAccount]
  );
  const currentAccountName = String(currentKioskAccount?.name || '').trim() || 'Caisse 1';
  const canManageSensitiveOrderActions = capabilities?.canManageSensitiveOrderActions ?? (accountRole === 'admin' || accountRole === 'manager');
  const canRefundOrder = capabilities?.canRefundOrder ?? (accountRole === 'admin' || accountRole === 'manager');
  const canSplitOrder = capabilities?.canSplitOrder ?? (accountRole === 'admin' || accountRole === 'manager' || accountRole === 'operator');
  const canOpenCashDrawer = capabilities?.canOpenCashDrawer === true;
  const canOpenCloseCashSession = capabilities?.canOpenCloseCashSession ?? (accountRole === 'admin');
  const canManageReports = capabilities?.canManageReports ?? (accountRole === 'admin' || accountRole === 'manager' || accountRole === 'operator');
  const canManageReservations = capabilities?.canManageReservations ?? true;
  const canManageUsers = capabilities?.canManageUsers ?? (accountRole === 'admin');
  const canManageProducts = capabilities?.canManageProducts ?? (accountRole === 'admin' || accountRole === 'manager');
  const canPrintDailyZ = capabilities?.canPrintDailyZ ?? (accountRole === 'admin');
  const canCompLine = capabilities?.canCompLine ?? (accountRole === 'admin' || accountRole === 'manager');
  const maxGlobalDiscountPercent = Number(capabilities?.maxGlobalDiscountPercent ?? (accountRole === 'admin' ? 100 : accountRole === 'manager' ? 50 : 20));
  const maxLineDiscountPercent = Number(capabilities?.maxLineDiscountPercent ?? (accountRole === 'admin' ? 100 : accountRole === 'manager' ? 50 : 20));
  const canOpenSettingsView = canManageUsers || canManageProducts || canOpenCloseCashSession;

  const loadProfiles = useCallback(async () => {
    try {
      setProfilesLoading(true);
      const response = await fetchWithAuth(`${API_URL}/api/caisse/profiles`);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || 'Chargement profils impossible.');
      }
      setProfilesList(safeArray(payload?.profiles));
      setProfilesZones(safeArray(payload?.zones));
    } catch (loadError) {
      setError(loadError.message || 'Chargement profils impossible.');
    } finally {
      setProfilesLoading(false);
    }
  }, [fetchWithAuth]);

  const loadRestaurantDevices = useCallback(async () => {
    try {
      setDevicesLoading(true);
      const response = await fetchWithAuth(`${API_URL}/api/caisse/devices`);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || 'Chargement appareils impossible.');
      }
      setRestaurantDevices(safeArray(payload?.devices));
      setDeviceLimits(payload?.limits || {});
      if (Array.isArray(payload?.zones)) {
        setProfilesZones((current) => current.length ? current : safeArray(payload.zones));
      }
    } catch (loadError) {
      setError(loadError.message || 'Chargement appareils impossible.');
    } finally {
      setDevicesLoading(false);
    }
  }, [fetchWithAuth]);

  const openAccountSwitch = useCallback(() => {
    setAccountSwitchError('');
    setAccountSwitchForm({
      username: String(currentKioskAccount?.name || '').trim() || 'Caisse 1',
      password: '',
    });
    setAccountSwitchOpen(true);
    fetchWithAuth(`${API_URL}/api/admin/accounts`)
      .then((response) => response.json().then((payload) => ({ ok: response.ok, payload })))
      .then(({ ok, payload }) => {
        if (!ok) {
          throw new Error(payload?.error || 'Chargement comptes impossible.');
        }
        setAccountSwitchOptions(safeArray(payload?.accounts));
      })
      .catch(() => {
        setAccountSwitchOptions([]);
      });
  }, [currentKioskAccount, fetchWithAuth]);

  const submitAccountSwitch = useCallback(async () => {
    try {
      setAccountSwitchLoading(true);
      setAccountSwitchError('');
      const username = String(accountSwitchForm.username || '').trim();
      const password = String(accountSwitchForm.password || '').trim();
      if (!username || !password) {
        throw new Error('Utilisateur et mot de passe requis.');
      }
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload?.success || !payload?.token) {
        throw new Error(payload?.message || 'Connexion impossible.');
      }
      localStorage.setItem('authToken', payload.token);
      localStorage.setItem('kioskAccount', JSON.stringify(payload.account || {}));
      setCurrentKioskAccount(payload.account || {});
      setAccountSwitchOpen(false);
      window.location.reload();
    } catch (switchError) {
      setAccountSwitchError(switchError.message || 'Connexion impossible.');
    } finally {
      setAccountSwitchLoading(false);
    }
  }, [accountSwitchForm]);

  const resetProfileEditor = useCallback(() => {
    setProfileEditor({
      id: null,
      username: '',
      password: '',
      userCode: '',
      role: 'operator',
      profileType: 'pad',
      caisseEnabled: false,
      borneEnabled: true,
      isActive: true,
      permissions: {
        canOpenCloseCashSession: true,
        canOpenCashDrawer: false,
        canManageUsers: false,
        canManageProducts: false,
        canManageReservations: true,
        canManageReports: true,
        canRefund: false,
        canApplyDiscountOver20: false,
      },
      printZoneIds: [],
    });
  }, []);

  useEffect(() => {
    if (activeView !== 'settings' || !canOpenCloseCashSession) {
      return;
    }
    loadProfiles();
    loadRestaurantDevices();
  }, [activeView, canOpenCloseCashSession, loadProfiles, loadRestaurantDevices]);

  useEffect(() => {
    if (activeView === 'settings' && !canOpenSettingsView) {
      setActiveView('sale');
      return;
    }
    if (activeView === 'control' && !canManageReports) {
      setActiveView('sale');
      return;
    }
    if (activeView === 'reservations' && !canManageReservations) {
      setActiveView('sale');
    }
  }, [activeView, canManageReports, canManageReservations, canOpenSettingsView]);

  const lockNow = useCallback(() => {
    if (!pinCode) {
      return;
    }
    setIsLocked(true);
    setUnlockPinInput('');
    setUnlockError('');
  }, [pinCode]);

  const fetchOpenOrders = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/api/orders?paymentStatus=unpaid`);
      const payload = await response.json();
      if (response.ok) {
        setOpenOrders(safeArray(payload).filter((order) => (
          String(order.paymentStatus || '').toLowerCase() === 'unpaid'
          && String(order.status || '').toLowerCase() !== 'annulee'
        )));
      }
    } catch (ordersError) {
      // The sales screen must stay usable even if the table overview cannot refresh.
    }
  }, [fetchWithAuth]);

  const fetchOrdersHistory = useCallback(async () => {
    try {
      setOrdersLoading(true);
      const response = await fetchWithAuth(`${API_URL}/api/orders`);
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Chargement commandes impossible.');
      }
      const orders = safeArray(payload);
      setOrdersHistory(orders);
      if (!selectedOrderId && orders.length) {
        setSelectedOrderId(orders[0].id);
      }
    } catch (historyError) {
      setError(historyError.message || 'Chargement commandes impossible.');
    } finally {
      setOrdersLoading(false);
    }
  }, [fetchWithAuth, selectedOrderId]);

  const fetchCustomers = useCallback(async (searchValue = '') => {
    try {
      setCustomersLoading(true);
      const params = new URLSearchParams();
      if (String(searchValue || '').trim()) {
        params.set('search', String(searchValue || '').trim());
      }
      params.set('limit', '150');
      const response = await fetchWithAuth(`${API_URL}/api/customers?${params.toString()}`);
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Chargement clients impossible.');
      }
      setCustomersList(safeArray(payload));
    } catch (customersError) {
      setCustomersList([]);
      setError(customersError.message || 'Chargement clients impossible.');
    } finally {
      setCustomersLoading(false);
    }
  }, [fetchWithAuth]);

  const fetchCashSessions = useCallback(async () => {
    try {
      setCashSessionsLoading(true);
      const response = await fetchWithAuth(`${API_URL}/api/cash-register/sessions?limit=30`);
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Chargement sessions caisse impossible.');
      }
      setCashSessions(safeArray(payload?.sessions));
    } catch (sessionError) {
      setError(sessionError.message || 'Chargement sessions caisse impossible.');
    } finally {
      setCashSessionsLoading(false);
    }
  }, [fetchWithAuth]);

  const fetchDailySummary = useCallback(async (date) => {
    try {
      setDailySummaryLoading(true);
      const response = await fetchWithAuth(`${API_URL}/api/reports/daily-summary?date=${encodeURIComponent(date || '')}`);
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Chargement récap impossible.');
      }
      setDailySummary(payload);
      return payload;
    } catch (summaryError) {
      setError(summaryError.message || 'Chargement récap impossible.');
      return null;
    } finally {
      setDailySummaryLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setLoading(true);
        const [sectionsResponse, productsResponse, menusResponse, printersResponse, settingsResponse, zonesResponse] = await Promise.all([
          fetchWithAuth(`${API_URL}/api/sections`),
          fetchWithAuth(`${API_URL}/api/products`),
          fetchWithAuth(`${API_URL}/api/menus`),
          fetchWithAuth(`${API_URL}/api/printers`),
          fetchWithAuth(`${API_URL}/api/printer-settings`),
          fetchWithAuth(`${API_URL}/api/production-zones`),
        ]);
        const [sectionsPayload, productsPayload, menusPayload, printersPayload, settingsPayload, zonesPayload] = await Promise.all([
          sectionsResponse.json(),
          productsResponse.json(),
          menusResponse.json(),
          printersResponse.json(),
          settingsResponse.json(),
          zonesResponse.json(),
        ]);
        if (!active) {
          return;
        }
        setSections(safeArray(sectionsPayload));
        setClientPrinters(safeArray(printersPayload));
        setTicketSettings(settingsPayload || {});
        setProductionZones(safeArray(zonesPayload));
        setProducts(safeArray(productsPayload)
          .filter((product) => product.isAvailable !== false && product.isCaisseAvailable !== false)
          .map((product) => ({
            ...product,
            image: resolveMediaUrl(product.image, { width: 480, height: 320, fit: 'cover', quality: 82 }),
            Supplements: safeArray(product.Supplements).map((supplement) => ({
              ...supplement,
              image: resolveMediaUrl(supplement.image, { width: 120, height: 120, fit: 'cover' }),
            })),
          })));
        setMenus(safeArray(menusPayload)
          .filter((menu) => menu.isCurrentlyAvailable !== false)
          .map((menu) => ({
            ...menu,
            image: resolveMediaUrl(menu.image, { width: 480, height: 320, fit: 'cover', quality: 82 }),
            Sections: safeArray(menu.Sections),
            Products: safeArray(menu.Products)
              .filter((product) => product.isAvailable !== false && product.isCaisseAvailable !== false)
              .map((product) => ({
                ...product,
                image: resolveMediaUrl(product.image, { width: 480, height: 320, fit: 'cover', quality: 82 }),
              })),
          }))
          .filter((menu) => safeArray(menu.Sections).length && safeArray(menu.Products).length));
        try {
          const capabilitiesResponse = await fetchWithAuth(`${API_URL}/api/caisse/capabilities`);
          const capabilitiesPayload = await capabilitiesResponse.json();
          if (capabilitiesResponse.ok && active) {
            setCapabilities(capabilitiesPayload || null);
          }
        } catch (capabilitiesError) {
          // Fallback to local role-derived guards.
        }
      } catch (loadError) {
        if (active) {
          setError('Chargement caisse impossible.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    load();
    fetchOpenOrders();
    fetchOrdersHistory();
    fetchCashSessions();
    fetchDailySummary(new Date().toISOString().slice(0, 10));
    return () => {
      active = false;
    };
  }, [fetchCashSessions, fetchDailySummary, fetchOpenOrders, fetchOrdersHistory, fetchWithAuth]);

  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  useEffect(() => {
    if (!pinCode || isLocked) {
      return undefined;
    }
    let timeoutId = null;
    const idleMs = Math.max(1, Number(autoLockMinutes || 15)) * 60 * 1000;
    const arm = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsLocked(true);
        setUnlockPinInput('');
        setUnlockError('');
      }, idleMs);
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach((eventName) => window.addEventListener(eventName, arm, { passive: true }));
    arm();
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      events.forEach((eventName) => window.removeEventListener(eventName, arm));
    };
  }, [autoLockMinutes, isLocked, pinCode]);

  const fetchOrderAuditLogs = useCallback(async (orderId) => {
    if (!orderId) {
      setOrderAuditLogs([]);
      return;
    }
    try {
      setOrderAuditLoading(true);
      const response = await fetchWithAuth(`${API_URL}/api/orders/${orderId}/audit-log`);
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Chargement journal impossible.');
      }
      setOrderAuditLogs(safeArray(payload));
    } catch (auditError) {
      setOrderAuditLogs([]);
    } finally {
      setOrderAuditLoading(false);
    }
  }, [fetchWithAuth]);

  const replayOfflineQueue = useCallback(async () => {
    if (!isOnline || replayRunningRef.current || !isOfflineScopeCurrent(offlineScope) || !offlineQueue.length) {
      return;
    }
    replayRunningRef.current = true;
    setSyncRunning(true);
    try {
      for (const action of offlineQueue) {
        if (!isOfflineScopeCurrent(offlineScope)) break;
        if (action.accountScope !== offlineScope) continue;
        try {
          let response;
          let payload = {};

          if (action.type === 'create_order') {
            // eslint-disable-next-line no-await-in-loop
            response = await fetchWithAuth(`${API_URL}/api/orders`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(action.body || {}),
            });
            // eslint-disable-next-line no-await-in-loop
            payload = await response.json().catch(() => ({}));
          } else if (action.type === 'create_order_and_pay') {
            // eslint-disable-next-line no-await-in-loop
            const createResponse = action.orderId ? { ok: true, json: async () => ({ id: action.orderId }) } : await fetchWithAuth(`${API_URL}/api/orders`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(action.body?.order || {}),
            });
            // eslint-disable-next-line no-await-in-loop
            const createPayload = await createResponse.json().catch(() => ({}));
            if (!createResponse.ok || !createPayload?.id) {
              response = createResponse;
              payload = createPayload;
            } else {
              upsertOfflineAction(action.id, { orderId: createPayload.id });
              if (!isOfflineScopeCurrent(offlineScope)) break;
              // eslint-disable-next-line no-await-in-loop
              const payResponse = await fetchWithAuth(`${API_URL}/api/orders/${createPayload.id}/payment`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(action.body?.payment || {}),
              });
              // eslint-disable-next-line no-await-in-loop
              payload = await payResponse.json().catch(() => ({}));
              response = payResponse;
            }
          } else {
            // eslint-disable-next-line no-await-in-loop
            response = await fetchWithAuth(`${API_URL}${action.path}`, {
              method: action.method || 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: action.body ? JSON.stringify(action.body) : undefined,
            });
            // eslint-disable-next-line no-await-in-loop
            payload = await response.json().catch(() => ({}));
          }

          if (response?.ok) {
            pushOfflineHistory({
              actionId: action.id,
              status: 'synced',
              method: action.method || '',
              path: action.path || action.type || '',
            });
            removeOfflineActionById(action.id);
            continue;
          }
          if (response.status >= 400 && response.status < 500) {
            pushOfflineConflict({
              actionId: action.id,
              path: action.path || action.type || '',
              method: action.method || '',
              reason: payload?.error || `HTTP ${response.status}`,
              body: action.body || null,
              originalAction: action,
            });
            pushOfflineHistory({
              actionId: action.id,
              status: 'conflict',
              method: action.method || '',
              path: action.path || action.type || '',
              reason: payload?.error || `HTTP ${response.status}`,
            });
            removeOfflineActionById(action.id);
            continue;
          }
          upsertOfflineAction(action.id, { retries: Number(action.retries || 0) + 1 });
        } catch (error) {
          upsertOfflineAction(action.id, { retries: Number(action.retries || 0) + 1 });
          break;
        }
      }
      await Promise.all([fetchOpenOrders(), fetchOrdersHistory()]);
    } finally {
      replayRunningRef.current = false;
      setSyncRunning(false);
    }
  }, [
    fetchOpenOrders,
    fetchOrdersHistory,
    fetchWithAuth,
    isOnline,
    offlineQueue,
    offlineScope,
    pushOfflineConflict,
    pushOfflineHistory,
    removeOfflineActionById,
    upsertOfflineAction,
  ]);

  replayQueueRef.current = replayOfflineQueue;
  useEffect(() => {
    if (!isOnline || !offlineQueue.length) return undefined;
    replayQueueRef.current();
    const timer = window.setInterval(() => replayQueueRef.current(), 10000);
    return () => window.clearInterval(timer);
  }, [isOnline, offlineQueue.length]);

  useEffect(() => {
    fetchBeguestAlerts();
    const intervalId = setInterval(fetchBeguestAlerts, 3000);
    return () => clearInterval(intervalId);
  }, [fetchBeguestAlerts]);

  useEffect(() => {
    const intervalId = setInterval(() => setTimerNow(Date.now()), 1000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (activeView !== 'clients') {
      return;
    }
    const timeoutId = setTimeout(() => {
      fetchCustomers(customersSearch);
    }, 180);
    return () => clearTimeout(timeoutId);
  }, [activeView, customersSearch, fetchCustomers]);

  useEffect(() => {
    if (orderMode !== 'sur_place' || !String(tableNumber || '').trim()) {
      return undefined;
    }
    const pollId = setInterval(() => {
      fetchOpenOrders();
    }, 2500);
    return () => clearInterval(pollId);
  }, [fetchOpenOrders, orderMode, tableNumber]);

  useEffect(() => {
    localStorage.setItem(TABLE_VIEW_MODE_STORAGE_KEY, tableViewMode);
  }, [tableViewMode]);

  const categories = useMemo(() =>
    sections.map((section) => ({
      id: String(section.id),
      name: section.name,
      icon: section.emoji || '⌁',
    })),
  [sections]);

  const activeBeguestAlert = useMemo(() => {
    const dismissed = new Set(dismissedBeguestAlertIds.map((value) => String(value)));
    return beguestAlerts.find((alert) => alert?.id && !dismissed.has(String(alert.id))) || null;
  }, [beguestAlerts, dismissedBeguestAlertIds]);

  const getMenuProductsForSection = useCallback((menu, sectionId) => {
    const targetId = String(sectionId || '');
    return safeArray(menu?.Products).filter((product) => String(product.sectionId || product.Section?.id || '') === targetId);
  }, []);

  const getMenuSelectedProducts = useCallback((menu, selections = {}) => {
    const productsById = new Map(safeArray(menu?.Products).map((product) => [String(product.id), product]));
    return safeArray(menu?.Sections).flatMap((section) => (
      safeArray(selections[String(section.id)]).map((productId) => ({
        section,
        product: productsById.get(String(productId)),
      })).filter((entry) => entry.product)
    ));
  }, []);

  const getMenuUnitPrice = useCallback((menu, selections = {}) => {
    const basePrice = Number(menu?.price || 0);
    const additionalTotal = getMenuSelectedProducts(menu, selections)
      .reduce((sum, entry) => sum + getMenuProductAdditionalPrice(entry.product), 0);
    return Number((basePrice + additionalTotal).toFixed(2));
  }, [getMenuSelectedProducts]);

  const filteredMenus = useMemo(() => {
    const query = normalizeText(search);
    return menus.filter((menu) => {
      if (activeSectionId === 'favorites') {
        return false;
      }
      if (activeSectionId !== 'all') {
        const matchesSection = safeArray(menu.Sections).some((section) => String(section.id) === String(activeSectionId))
          || safeArray(menu.Products).some((product) => String(product.sectionId || product.Section?.id || '') === String(activeSectionId));
        if (!matchesSection) {
          return false;
        }
      }
      if (!query) {
        return true;
      }
      return normalizeText([
        menu.name,
        menu.description,
        safeArray(menu.Sections).map((section) => section.name).join(' '),
        safeArray(menu.Products).map((product) => `${product.name} ${product.description || ''}`).join(' '),
      ].join(' ')).includes(query);
    });
  }, [activeSectionId, menus, search]);

  const filteredProducts = useMemo(() => {
    const query = normalizeText(search);
    return products.filter((product) => {
      if (activeSectionId === 'favorites' && !product.isDealOfDay) {
        return false;
      }
      if (!['all', 'favorites'].includes(activeSectionId) && String(product.sectionId) !== String(activeSectionId)) {
        return false;
      }
      if (!query) {
        return true;
      }
      return normalizeText(`${product.name} ${product.kitchenName || ''} ${product.description || ''} ${product.Section?.name || ''}`).includes(query);
    });
  }, [activeSectionId, products, search]);

  const groupedFilteredProducts = useMemo(() => {
    const groupsMap = new Map();
    filteredProducts.forEach((product) => {
      const subcategory = product.Subcategory || {};
      const key = String(product.subcategoryId || subcategory.id || 'none');
      if (!groupsMap.has(key)) {
        groupsMap.set(key, {
          key,
          name: subcategory.name || 'Autres',
          order: Number.isFinite(Number(subcategory.displayOrder)) ? Number(subcategory.displayOrder) : 9999,
          products: [],
        });
      }
      groupsMap.get(key).products.push(product);
    });
    return Array.from(groupsMap.values()).sort((a, b) => (
      a.order - b.order || a.name.localeCompare(b.name, 'fr')
    ));
  }, [filteredProducts]);

  const totals = useMemo(() => {
    const subtotal = cart.reduce((sum, item) => sum + Number(item.totalPrice || 0), 0);
    const rawDiscount = Number(String(discountValue || '0').replace(',', '.')) || 0;
    const discountAmount = discountMode === 'percent'
      ? Math.max(0, Math.min(subtotal, subtotal * (Math.min(maxGlobalDiscountPercent, rawDiscount) / 100)))
      : Math.max(0, Math.min(subtotal, rawDiscount));
    const total = Math.max(0, subtotal - discountAmount);
    return {
      subtotal: Number(subtotal.toFixed(2)),
      discountAmount: Number(discountAmount.toFixed(2)),
      total: Number(total.toFixed(2)),
      count: cart.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
    };
  }, [cart, discountMode, discountValue, maxGlobalDiscountPercent]);

  const tableOrders = useMemo(() =>
    openOrders.filter((order) => getOrderTableNumber(order) === String(tableNumber || '').trim()),
  [openOrders, tableNumber]);

  const tableDueTotal = useMemo(() =>
    Number(tableOrders.reduce((sum, order) => sum + Number(order.totalTtc || 0), 0).toFixed(2)),
  [tableOrders]);

  const selectedTableOrders = useMemo(() => (
    tableOrders.filter((order) => selectedTableOrderIds.includes(Number(order.id)))
  ), [selectedTableOrderIds, tableOrders]);

  const selectedTableDueTotal = useMemo(() =>
    Number(selectedTableOrders.reduce((sum, order) => sum + Number(order.totalTtc || 0), 0).toFixed(2)),
  [selectedTableOrders]);

  const selectedCartItems = useMemo(() => {
    return cart
      .map((item) => {
        const qty = Math.max(0, Math.min(Number(item.quantity || 0), Number(splitDraft[item.key] || 0)));
        if (!qty) return null;
        const ratio = qty / Number(item.quantity || 1);
        return {
          ...item,
          quantity: qty,
          totalPrice: Number((Number(item.totalPrice || 0) * ratio).toFixed(2)),
        };
      })
      .filter(Boolean);
  }, [cart, splitDraft]);

  const selectedCartTotals = useMemo(() => {
    const subtotal = selectedCartItems.reduce((sum, item) => sum + Number(item.totalPrice || 0), 0);
    const sourceSubtotal = Number(totals.subtotal || 0);
    const sourceDiscount = Number(totals.discountAmount || 0);
    const prorata = sourceSubtotal > 0 ? Math.min(1, Math.max(0, subtotal / sourceSubtotal)) : 0;
    const discountAmount = Number((sourceDiscount * prorata).toFixed(2));
    return {
      subtotal: Number(subtotal.toFixed(2)),
      discountAmount,
      total: Number(Math.max(0, subtotal - discountAmount).toFixed(2)),
      count: selectedCartItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
    };
  }, [selectedCartItems, totals.discountAmount, totals.subtotal]);

  const activePaymentTotals = paymentTarget === 'table'
    ? {
        subtotal: selectedTableDueTotal || tableDueTotal,
        discountAmount: 0,
        total: selectedTableDueTotal || tableDueTotal,
        count: selectedTableOrders.length || tableOrders.length,
      }
    : paymentTarget === 'order' && selectedOrderForPayment
      ? {
          subtotal: Number(selectedOrderForPayment.totalTtc || 0),
          discountAmount: 0,
        total: Number(selectedOrderForPayment.totalTtc || 0),
        count: 1,
      }
      : paymentTarget === 'cart'
        ? (() => {
            const selectedCount = selectedCartItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
            const cartCount = cart.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
            return (selectedCount > 0 && selectedCount < cartCount) ? selectedCartTotals : totals;
          })()
        : totals;

  const displayedTotals = useMemo(() => {
    if (orderMode === 'sur_place' && tableOrders.length) {
      const tableTotal = selectedTableDueTotal > 0 ? selectedTableDueTotal : tableDueTotal;
      return {
        subtotal: tableTotal,
        discountAmount: 0,
        total: tableTotal,
      };
    }
    return totals;
  }, [orderMode, selectedTableDueTotal, tableDueTotal, tableOrders.length, totals]);

  const tableCards = useMemo(() => {
    const map = new Map(tableSettings.map((table) => [String(table), {
      number: String(table),
      total: 0,
      orders: [],
      itemsCount: 0,
      status: 'libre',
      openedAt: null,
    }]));
    openOrders.forEach((order) => {
      const number = getOrderTableNumber(order);
      if (!number) {
        return;
      }
      if (!map.has(number)) {
        map.set(number, { number, total: 0, orders: [], itemsCount: 0 });
      }
      const table = map.get(number);
      table.total = Number((table.total + Number(order.totalTtc || 0)).toFixed(2));
      table.itemsCount += getOrderItems(order).reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      table.orders.push(order);
      const orderDate = new Date(order.orderedAt || order.createdAt || Date.now());
      if (!table.openedAt || orderDate < table.openedAt) {
        table.openedAt = orderDate;
      }
      const status = String(order.status || '').toLowerCase();
      if (status === 'prete') {
        table.status = 'servie';
      } else {
        table.status = 'ouverte';
      }
    });
    return [...map.values()].sort((a, b) => Number(a.number) - Number(b.number) || a.number.localeCompare(b.number));
  }, [openOrders, tableSettings]);

  const tableCardsMap = useMemo(() => new Map(tableCards.map((table) => [String(table.number), table])), [tableCards]);
  const sentTableLines = useMemo(() => (
    tableOrders.flatMap((order) => getOrderItems(order).map((item) => ({
      id: `${order.id}-${item.id || item.name}`,
      itemId: item.id || null,
      orderId: order.id,
      ticketNumber: order.ticketNumber || order.id,
      name: item.name || item.kitchenName || 'Article',
      kitchenName: item.kitchenName || '',
      followUp: /a\s*suivre/i.test(String(item.kitchenName || '')),
      complaint: /reclamation/i.test(String(item.kitchenName || '')),
      quantity: Number(item.quantity || 0),
      totalPrice: Number(item.totalPrice || 0),
      unitPrice: Number(item.quantity || 0) > 0 ? Number(item.totalPrice || 0) / Number(item.quantity || 1) : Number(item.totalPrice || 0),
      taxRate: Number(item.taxRate || 10),
      productId: item.productId || item.Product?.id || null,
      printerId: item.printerId || item.Printer?.id || null,
      productionZoneId: item.productionZoneId || item.ProductionZone?.id || null,
      orderedAt: order.orderedAt || order.createdAt || item.createdAt || null,
      lineCreatedAt: item.createdAt || order.orderedAt || order.createdAt || null,
      orderReadyAt: order.readyAt || null,
      orderStatus: String(order.status || '').toLowerCase(),
      workflowStatus: String(item.kitchenWorkflowStatus || 'pending').toLowerCase(),
      workflowUpdatedAt: item.kitchenWorkflowUpdatedAt || null,
    })))
  ), [tableOrders]);
  const visibleSentTableLines = useMemo(() => {
    const complaintKeys = new Set(
      sentTableLines
        .filter((line) => line.complaint)
        .map((line) => `${line.orderId}::${normalizeText(line.name || '')}::${line.unitPrice}`)
    );

    return sentTableLines.filter((line) => {
      const lineId = String(line.id);
      const logicalKey = getSentLineLogicalKey(line);
      if ((reclaimedSentLineIds.includes(lineId) || reclaimedSentLineKeys.includes(logicalKey)) && !line.complaint) {
        return false;
      }
      if (!line.complaint) {
        const key = `${line.orderId}::${normalizeText(line.name || '')}::${line.unitPrice}`;
        if (complaintKeys.has(key)) {
          return false;
        }
      }
      return true;
    });
  }, [reclaimedSentLineIds, reclaimedSentLineKeys, sentTableLines]);
  const reclaimableSentLines = useMemo(
    () => visibleSentTableLines.filter((line) => line.followUp && !line.complaint && !reclaimedSentLineIds.includes(String(line.id))),
    [reclaimedSentLineIds, visibleSentTableLines],
  );

  const filteredOrdersHistory = useMemo(() => {
    const from = String(ordersDateFrom || '').trim();
    const to = String(ordersDateTo || '').trim();
    return ordersHistory.filter((order) => {
      if (ordersFilter === 'upcoming') return isAwaitingPreparation(order);
      const orderDate = String(order.orderedAt || order.createdAt || '').slice(0, 10);
      if (from && orderDate && orderDate < from) {
        return false;
      }
      if (to && orderDate && orderDate > to) {
        return false;
      }
      if (ordersFilter === 'all') {
        return true;
      }
      if (ordersFilter === 'en_cours' && isAwaitingPreparation(order)) return false;
      if (ordersFilter === 'unpaid') {
        return String(order.paymentStatus || '').toLowerCase() !== 'paid';
      }
      return String(order.status || '').toLowerCase() === ordersFilter;
    });
  }, [ordersDateFrom, ordersDateTo, ordersFilter, ordersHistory]);

  const filteredOrderAuditLogs = useMemo(() => {
    if (orderAuditFilter === 'all') {
      return orderAuditLogs;
    }
    return orderAuditLogs.filter((log) => {
      const action = String(log?.action || '').toLowerCase();
      if (orderAuditFilter === 'cancel') {
        return action.includes('cancel');
      }
      if (orderAuditFilter === 'reopen') {
        return action.includes('reopen');
      }
      if (orderAuditFilter === 'payment') {
        return action.includes('payment');
      }
      return true;
    });
  }, [orderAuditFilter, orderAuditLogs]);

  const selectedOrder = useMemo(() =>
    ordersHistory.find((order) => Number(order.id) === Number(selectedOrderId)) || null,
  [ordersHistory, selectedOrderId]);
  const openCashSession = useMemo(() =>
    cashSessions.find((session) => String(session.status || '').toLowerCase() === 'open') || null,
  [cashSessions]);
  const controlStats = useMemo(() => {
    if (controlSummary) {
      return {
        ...controlSummary,
      };
    }
    const today = new Date().toISOString().slice(0, 10);
    const todayOrders = ordersHistory.filter((order) => String(order.orderedAt || order.createdAt || '').slice(0, 10) === today);
    const paidToday = todayOrders.filter((order) => String(order.paymentStatus || '').toLowerCase() === 'paid');
    const refundedToday = todayOrders.reduce((sum, order) => sum + Number(order.refundedTtc || 0), 0);
    const cashToday = paidToday
      .filter((order) => ['espece', 'cash'].includes(String(order.paymentMethod || '').toLowerCase()))
      .reduce((sum, order) => sum + Number(order.totalTtc || 0), 0);
    const cardToday = paidToday
      .filter((order) => String(order.paymentMethod || '').toLowerCase() === 'cb')
      .reduce((sum, order) => sum + Number(order.totalTtc || 0), 0);
    const mixteToday = paidToday
      .filter((order) => String(order.paymentMethod || '').toLowerCase() === 'mixte')
      .reduce((sum, order) => sum + Number(order.totalTtc || 0), 0);
    const avgTicket = paidToday.length
      ? paidToday.reduce((sum, order) => sum + Number(order.totalTtc || 0), 0) / paidToday.length
      : 0;
    const lastClosedSession = cashSessions.find((session) => String(session.status || '').toLowerCase() === 'closed') || null;
    return {
      todayOrdersCount: todayOrders.length,
      todayPaidCount: paidToday.length,
      todayCancelledCount: todayOrders.filter((order) => String(order.status || '').toLowerCase() === 'annule').length,
      refundedToday: Number(refundedToday.toFixed(2)),
      cashToday: Number(cashToday.toFixed(2)),
      cardToday: Number(cardToday.toFixed(2)),
      mixteToday: Number(mixteToday.toFixed(2)),
      avgTicket: Number(avgTicket.toFixed(2)),
      lastClosedSession,
    };
  }, [cashSessions, controlSummary, ordersHistory]);

  const currentCovers = Number(tableCovers[String(tableNumber || '')] || 0);
  const configuredPaymentMethods = useMemo(
    () => normalizePaymentMethodsConfig(ticketSettings?.paymentMethods || []),
    [ticketSettings]
  );
  const liveOpenTablesCount = useMemo(
    () => tableCards.filter((table) => Number(table.total || 0) > 0).length,
    [tableCards]
  );

  const fetchControlSummary = useCallback(async (dateValue) => {
    try {
      setControlSummaryLoading(true);
      const date = String(dateValue || new Date().toISOString().slice(0, 10));
      const response = await fetchWithAuth(`${API_URL}/api/caisse/control-summary?date=${encodeURIComponent(date)}`);
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Chargement centre de contrôle impossible.');
      }
      setControlSummary(payload || null);
      return payload;
    } catch (error) {
      setControlSummary(null);
      return null;
    } finally {
      setControlSummaryLoading(false);
    }
  }, [fetchWithAuth]);

  const openMenuComposer = (menu) => {
    const initialSelections = safeArray(menu?.Sections).reduce((acc, section) => ({
      ...acc,
      [String(section.id)]: [],
    }), {});
    setMenuComposer({
      open: true,
      menu,
      selections: initialSelections,
      stockChoices: {},
      error: '',
    });
  };

  const closeMenuComposer = () => {
    setMenuComposer({
      open: false,
      menu: null,
      selections: {},
      error: '',
    });
  };

  const toggleMenuComposerProduct = (section, product) => {
    setMenuComposer((current) => {
      const sectionId = String(section?.id || '');
      if (!current.open || !sectionId || !product?.id) {
        return current;
      }
      const requiredQuantity = getMenuRequiredQuantity(section);
      const currentValues = safeArray(current.selections?.[sectionId]).map(String);
      const productId = String(product.id);
      const exists = currentValues.includes(productId);
      let nextValues = exists
        ? currentValues.filter((value) => value !== productId)
        : [...currentValues, productId];
      if (!exists && nextValues.length > requiredQuantity) {
        nextValues = requiredQuantity === 1 ? [productId] : nextValues.slice(nextValues.length - requiredQuantity);
      }
      return {
        ...current,
        selections: {
          ...current.selections,
          [sectionId]: nextValues,
        },
        error: '',
      };
    });
  };

  const addMenuToCart = (menu, selections, choices = {}) => {
    const selectedEntries = getMenuSelectedProducts(menu, selections).map(entry => ({ ...entry, product: { ...entry.product, stockChoice: choices[`${entry.section.id}:${entry.product.id}`] || stockChoiceFor(entry.product) } }));
    const unitPrice = getMenuUnitPrice(menu, selections);
    const key = `${buildMenuLineKey(menu, selections, orderMode)}:${menuStockSignature(selectedEntries.map(entry => ({ sectionId: entry.section.id, products: [entry.product] })))}`;
    const selectionGroups = safeArray(menu.Sections).map((section) => ({
      sectionId: section.id,
      sectionName: section.name,
      productIds: safeArray(selections[String(section.id)]).map((value) => Number(value)).filter(Boolean),
    }));
    const menuSelections = safeArray(menu.Sections).map((section) => ({
      sectionId: section.id,
      sectionName: section.name,
      products: selectedEntries
        .filter((entry) => String(entry.section.id) === String(section.id))
        .map((entry) => entry.product),
    })).filter((group) => group.products.length);
    const expandedOrderItems = selectedEntries.map((entry, index) => {
      const product = entry.product;
      const sectionName = entry.section?.name || product.Section?.name || '';
      const name = `${menu.name} - ${sectionName}: ${product.name}`;
      return {
        productId: product.id,
        stockChoice: stockChoiceFor(product),
        menuId: menu.id,
        printerId: product.printerId || product.Printer?.id || product.ProductionZone?.printerId || product.ProductionZone?.Printer?.id || null,
        productionZoneId: product.productionZoneId || product.ProductionZone?.id || null,
        name,
        kitchenName: name,
        quantity: 1,
        unitPrice: index === 0 ? unitPrice : 0,
        totalPrice: index === 0 ? unitPrice : 0,
        taxRate: Number(product.taxRate ?? menu.taxRate ?? 10),
        isMenu: true,
        selectionsJson: selectionGroups,
      };
    });
    pendingCartScrollKeyRef.current = key;
    setCart((current) => {
      const existing = current.find((item) => item.key === key);
      if (existing) {
        const nextQuantity = existing.quantity + 1;
        return current.map((item) => item.key === key
          ? {
              ...item,
              quantity: nextQuantity,
              totalPrice: computeLineTotal(item.unitPrice, nextQuantity, item.lineDiscountPercent, item.isComped),
            }
          : item);
      }
      return [
        ...current,
        {
          key,
          productId: null,
          menuId: menu.id,
          printerId: null,
          productionZoneId: null,
          name: menu.name,
          kitchenName: menu.name,
          sectionName: 'Formule',
          quantity: 1,
          unitPrice,
          lineDiscountPercent: 0,
          isComped: false,
          followUp: false,
          complaint: false,
          totalPrice: computeLineTotal(unitPrice, 1),
          taxRate: Number(menu.taxRate ?? selectedEntries[0]?.product?.taxRate ?? 10),
          selectedSupplements: [],
          note: '',
          isMenu: true,
          selectionsJson: selectionGroups,
          menuSelections,
          expandedOrderItems,
        },
      ];
    });
    if (!isPad) {
      setCartOpen(true);
    }
    setNotice('');
    setError('');
  };

  const confirmMenuComposer = () => {
    const menu = menuComposer.menu;
    if (!menu) {
      closeMenuComposer();
      return;
    }
    const invalidSection = safeArray(menu.Sections).find((section) => {
      const requiredQuantity = getMenuRequiredQuantity(section);
      const selectedCount = safeArray(menuComposer.selections[String(section.id)]).length;
      return selectedCount !== requiredQuantity;
    });
    if (invalidSection) {
      setMenuComposer((current) => ({
        ...current,
        error: `Choisis ${getMenuRequiredQuantity(invalidSection)} élément(s) dans ${invalidSection.name}.`,
      }));
      return;
    }
    if (getMenuSelectedProducts(menu, menuComposer.selections).some(entry => !stockChoiceValid({ ...entry.product, stockChoice: menuComposer.stockChoices?.[`${entry.section.id}:${entry.product.id}`] || stockChoiceFor(entry.product) }))) {
      setMenuComposer(current => ({ ...current, error: 'Choisissez la recette de chaque produit pour continuer.' })); return;
    }
    addMenuToCart(menu, menuComposer.selections, menuComposer.stockChoices);
    closeMenuComposer();
  };

  const addToCart = (product, options = {}) => {
    const selectedSupplements = safeArray(options.selectedSupplements);
    const quantity = Math.max(1, Number(options.quantity || 1));
    const note = String(options.note || '').trim();
    const baseUnitPrice = getProductPrice(product, orderMode);
    const supplementTotal = selectedSupplements.reduce((sum, supplement) => sum + Number(supplement.price || 0), 0);
    const unitPrice = Number((baseUnitPrice + supplementTotal).toFixed(2));
    const key = buildCartLineKey(product, selectedSupplements, note, orderMode);
    pendingCartScrollKeyRef.current = key;
    const supplementLabel = selectedSupplements.map((supplement) => supplement.name).join(', ');
    const kitchenNameParts = [
      product.kitchenName || product.name,
      supplementLabel ? `+ ${supplementLabel}` : '',
      note ? `Note: ${note}` : '',
    ].filter(Boolean);

    setCart((current) => {
      const existing = current.find((item) => item.key === key);
      if (existing) {
        const nextQuantity = existing.quantity + quantity;
        return current.map((item) => item.key === key
          ? {
              ...item,
              quantity: nextQuantity,
              totalPrice: computeLineTotal(item.unitPrice, nextQuantity, item.lineDiscountPercent, item.isComped),
            }
          : item);
      }
      return [
        ...current,
        {
          key,
          productId: product.id,
          stockChoices: product.stockChoices,
          stockChoice: stockChoiceFor(product),
          printerId: product.printerId || product.Printer?.id || null,
          productionZoneId: product.productionZoneId || product.ProductionZone?.id || null,
          name: product.name,
          kitchenName: kitchenNameParts.join(' · '),
          sectionName: product.Section?.name || '',
          quantity,
          unitPrice,
          lineDiscountPercent: 0,
          isComped: false,
          followUp: false,
          complaint: false,
          totalPrice: computeLineTotal(unitPrice, quantity),
          taxRate: Number(product.taxRate ?? 10),
          selectedSupplements,
          note,
        },
      ];
    });
    if (!isPad) {
      setCartOpen(true);
    }
    setNotice('');
    setError('');
  };

  const handleProductPress = (product) => {
    if (product.stockChoices) { setRecipeProduct(withInitialStockChoice(product)); return; }
    addToCart(product, { quantity: 1 });
  };

  useEffect(() => {
    const targetKey = pendingCartScrollKeyRef.current;
    if (!targetKey || !cart.length) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const targetNode = pendingCartScrollNodeRef.current;
      const container = cartLinesRef.current;
      if (targetNode) {
        targetNode.scrollIntoView({ block: 'end', behavior: 'smooth' });
      } else if (container) {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      }
      pendingCartScrollKeyRef.current = '';
      pendingCartScrollNodeRef.current = null;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [cart]);

  const selectTable = (number) => {
    const value = String(number || '');
    setOrderMode('sur_place');
    setTableNumber(value);
    setTransferTargetTable('');
    setMergeSourceTable('');
    setMergeModalOpen(false);
    setSelectedTableOrderIds([]);
    if (!tableCovers[value]) {
      setCoversTargetTable(value);
      setCoversDraft('');
      setCoversModalOpen(true);
    }
    setActiveView('sale');
    setNotice('');
    setError('');
  };

  const openPadTableSelection = () => {
    setActiveView('sale');
    setPadMenuOpen(false);
    setTablePickerOpen(true);
    setNotice('');
    setError('');
  };

  const openCoversEditor = () => {
    const value = String(tableNumber || '').trim();
    if (!value) return;
    setCoversTargetTable(value);
    setCoversDraft(String(tableCovers[value] || ''));
    setCoversModalOpen(true);
  };

  const openMergeModal = () => {
    if (orderMode !== 'sur_place') return;
    setMergeModalOpen(true);
  };

  const saveCovers = () => {
    const table = String(coversTargetTable || '').trim();
    if (!table) {
      setCoversModalOpen(false);
      return;
    }
    const value = Math.max(1, Math.min(30, parseInt(String(coversDraft || '1'), 10) || 1));
    const next = { ...tableCovers, [table]: value };
    setTableCovers(next);
    localStorage.setItem(CAISSE_TABLE_COVERS_KEY, JSON.stringify(next));
    setCoversModalOpen(false);
  };

  useEffect(() => {
    if (orderMode !== 'sur_place') {
      setSelectedTableOrderIds([]);
      return;
    }
    setSelectedTableOrderIds(tableOrders.map((order) => Number(order.id)));
  }, [orderMode, tableNumber, tableOrders]);

  useEffect(() => {
    setReclaimedSentLineIds([]);
    setReclaimedSentLineKeys([]);
  }, [tableNumber]);

  useEffect(() => {
    setSplitDraft((current) => {
      const next = {};
      cart.forEach((item) => {
        const max = Number(item.quantity || 0);
        const value = explicitSplitKeysRef.current.has(item.key) ? Number(current[item.key] ?? max) : max;
        next[item.key] = Math.max(0, Math.min(max, value));
      });
      return next;
    });
  }, [cart]);

  const toggleTableOrderSelection = (orderId) => {
    setSelectedTableOrderIds((current) => (
      current.includes(Number(orderId))
        ? current.filter((id) => Number(id) !== Number(orderId))
        : [...current, Number(orderId)]
    ));
  };

  const transferCurrentTableOrders = async () => {
    const fromTable = String(tableNumber || '').trim();
    const toTable = String(transferTargetTable || '').trim();
    if (!fromTable || !toTable || fromTable === toTable) {
      setError('Choisis une table destination valide.');
      return;
    }
    if (!tableOrders.length) {
      setError('Aucune commande ouverte sur cette table.');
      return;
    }
    try {
      setCheckoutLoading(true);
      setError('');
      for (const order of tableOrders) {
        // eslint-disable-next-line no-await-in-loop
        const response = await fetchWithAuth(`${API_URL}/api/orders/${order.id}/table`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tableNumber: toTable }),
        });
        // eslint-disable-next-line no-await-in-loop
        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload?.error || 'Transfert de table impossible.');
        }
      }
      setNotice(`Table ${fromTable} transférée vers table ${toTable}.`);
      setTableNumber(toTable);
      setTransferTargetTable('');
      await Promise.all([fetchOpenOrders(), fetchOrdersHistory()]);
    } catch (transferError) {
      if (!isOnline) {
        for (const order of tableOrders) {
          pushOfflineAction({
            method: 'PATCH',
            path: `/api/orders/${order.id}/table`,
            body: { tableNumber: toTable },
          });
        }
        setNotice(`Hors ligne: transfert table ${fromTable}→${toTable} mis en file.`);
      } else {
        setError(transferError.message || 'Transfert de table impossible.');
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  const mergeIntoCurrentTable = async () => {
    const targetTable = String(tableNumber || '').trim();
    const sourceTable = String(mergeSourceTable || '').trim();
    if (!targetTable || !sourceTable || sourceTable === targetTable) {
      setError('Choisis une table source valide.');
      return;
    }
    try {
      setCheckoutLoading(true);
      setError('');
      const response = await fetchWithAuth(`${API_URL}/api/tables/merge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sourceTable, targetTable }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Fusion de table impossible.');
      }
      setNotice(`Table ${sourceTable} fusionnée dans table ${targetTable}.`);
      setMergeSourceTable('');
      setMergeModalOpen(false);
      await Promise.all([fetchOpenOrders(), fetchOrdersHistory()]);
    } catch (mergeError) {
      if (!isOnline) {
        pushOfflineAction({
          method: 'POST',
          path: '/api/tables/merge',
          body: { sourceTable, targetTable },
        });
        setNotice(`Hors ligne: fusion table ${sourceTable}→${targetTable} mise en file.`);
      } else {
        setError(mergeError.message || 'Fusion de table impossible.');
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  const saveTables = () => {
    const nextTables = tablesDraft
      .split(/[\n,;]+/)
      .map((value) => value.trim())
      .filter(Boolean);
    const uniqueTables = [...new Set(nextTables)];
    const finalTables = uniqueTables.length ? uniqueTables : DEFAULT_TABLES;
    setTableSettings(finalTables);
    setTablesDraft(finalTables.join(', '));
    localStorage.setItem(TABLES_STORAGE_KEY, JSON.stringify(finalTables));
    const nextLayout = buildDefaultTableLayout(finalTables, tableLayout.rows, tableLayout.cols);
    setTableLayout(nextLayout);
    setTableLayoutDraft(nextLayout);
    localStorage.setItem(TABLE_LAYOUT_STORAGE_KEY, JSON.stringify(nextLayout));
    setNotice('Tables enregistrées sur cette caisse.');
    setError('');
  };

  const setTableLayoutDraftSize = (nextRows, nextCols) => {
    setTableLayoutDraft((current) => {
      const rows = Math.max(1, Math.min(20, Number(nextRows) || current.rows || 4));
      const cols = Math.max(1, Math.min(20, Number(nextCols) || current.cols || 4));
      const size = rows * cols;
      const cells = new Array(size).fill('').map((_, index) => String(current.cells?.[index] || '').trim());
      return { rows, cols, cells };
    });
  };

  const updateTableLayoutDraftCell = (index, value) => {
    setTableLayoutDraft((current) => {
      const nextCells = [...safeArray(current.cells)];
      nextCells[index] = String(value || '').trim();
      return { ...current, cells: nextCells };
    });
  };

  const saveTableLayout = () => {
    const rows = Math.max(1, Math.min(20, Number(tableLayoutDraft.rows) || 4));
    const cols = Math.max(1, Math.min(20, Number(tableLayoutDraft.cols) || 4));
    const size = rows * cols;
    const cells = new Array(size).fill('').map((_, index) => String(tableLayoutDraft.cells?.[index] || '').trim());
    const tablesFromLayout = normalizeTableValues(cells);
    const nextTables = tablesFromLayout.length ? tablesFromLayout : tableSettings;
    const normalizedLayout = { rows, cols, cells };
    setTableLayout(normalizedLayout);
    setTableLayoutDraft(normalizedLayout);
    setTableSettings(nextTables);
    setTablesDraft(nextTables.join(', '));
    localStorage.setItem(TABLE_LAYOUT_STORAGE_KEY, JSON.stringify(normalizedLayout));
    localStorage.setItem(TABLES_STORAGE_KEY, JSON.stringify(nextTables));
    setNotice('Plan de salle enregistré.');
    setError('');
  };

  const saveSecuritySettings = () => {
    const normalizedPin = String(pinDraft || '').replace(/\D/g, '').slice(0, 8);
    const minutes = Math.max(1, Math.min(120, parseInt(String(autoLockMinutes || 15), 10) || 15));
    setPinCode(normalizedPin);
    setPinDraft(normalizedPin);
    setAutoLockMinutes(minutes);
    localStorage.setItem(CAISSE_PIN_STORAGE_KEY, normalizedPin);
    localStorage.setItem(CAISSE_AUTOLOCK_MINUTES_STORAGE_KEY, String(minutes));
    setNotice('Sécurité caisse enregistrée.');
    setError('');
  };

  const saveReservationSettings = async () => {
    try {
      setCheckoutLoading(true);
      setError('');
      const openingHour = parseInt(reservationConfigDraft.reservationOpeningHour, 10);
      const parsedPaymentMethods = paymentMethodsDraft
        .split(',')
        .map((entry) => String(entry || '').trim())
        .filter(Boolean)
        .map((label) => ({
          id: label
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .replace(/[^\w]+/g, '_')
            .replace(/^_+|_+$/g, ''),
          label,
        }))
        .filter((entry) => entry.id);
      const response = await fetchWithAuth(`${API_URL}/api/printer-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reservationOpeningHour: Number.isInteger(openingHour) ? Math.max(0, Math.min(23, openingHour)) : 12,
          reservationClosingHour: Math.max(1, Math.min(23, parseInt(reservationConfigDraft.reservationClosingHour || 22, 10) || 22)),
          reservationSlotMinutes: Math.max(15, Math.min(120, parseInt(reservationConfigDraft.reservationSlotMinutes || 30, 10) || 30)),
          reservationMaxPartySize: Math.max(1, Math.min(30, parseInt(reservationConfigDraft.reservationMaxPartySize || 12, 10) || 12)),
          reservationTableCapacity: Math.max(1, Math.min(300, parseInt(reservationConfigDraft.reservationTableCapacity || 40, 10) || 40)),
          reservationDefaultDurationMinutes: Math.max(15, Math.min(360, parseInt(reservationConfigDraft.reservationDefaultDurationMinutes || 120, 10) || 120)),
          kdsItemTimerLimitEnabled: !!reservationConfigDraft.kdsItemTimerLimitEnabled,
          kdsItemTimerLimitSeconds: Math.max(30, Math.min(7200, parseInt(reservationConfigDraft.kdsItemTimerLimitSeconds || 900, 10) || 900)),
          paymentMethodsJson: parsedPaymentMethods,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || 'Enregistrement des paramètres réservation impossible.');
      }
      setTicketSettings(payload || {});
      setNotice('Paramètres réservation enregistrés.');
    } catch (saveError) {
      setError(saveError.message || 'Enregistrement des paramètres réservation impossible.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const startEditProfile = (profile) => {
    if (!profile) {
      resetProfileEditor();
      return;
    }
    setProfileEditor({
      id: profile.id,
      username: String(profile.username || ''),
      password: '',
      userCode: String(profile.userCode || ''),
      role: String(profile.role || 'operator'),
      profileType: String(profile.profileType || 'pad'),
      caisseEnabled: !!profile.caisseEnabled,
      borneEnabled: profile.borneEnabled !== false,
      isActive: profile.isActive !== false,
      permissions: {
        canOpenCloseCashSession: profile?.permissions?.canOpenCloseCashSession !== false,
        canOpenCashDrawer: profile?.permissions?.canOpenCashDrawer === true,
        canManageUsers: profile?.permissions?.canManageUsers === true,
        canManageProducts: profile?.permissions?.canManageProducts === true,
        canManageReservations: profile?.permissions?.canManageReservations !== false,
        canManageReports: profile?.permissions?.canManageReports !== false,
        canRefund: profile?.permissions?.canRefund === true,
        canApplyDiscountOver20: profile?.permissions?.canApplyDiscountOver20 === true,
      },
      printZoneIds: safeArray(profile?.printZoneIds).map((value) => Number(value)).filter((value) => Number.isFinite(value)),
    });
  };

  const saveProfile = async () => {
    try {
      if (!profileEditor.username.trim()) {
        throw new Error('Nom utilisateur obligatoire.');
      }
      const payload = {
        username: profileEditor.username.trim(),
        role: profileEditor.role,
        profileType: profileEditor.profileType,
        caisseEnabled: !!profileEditor.caisseEnabled,
        borneEnabled: !!profileEditor.borneEnabled,
        isActive: !!profileEditor.isActive,
        userCode: String(profileEditor.userCode || '').trim(),
        permissions: profileEditor.permissions,
        printZoneIds: safeArray(profileEditor.printZoneIds).map((value) => Number(value)).filter((value) => Number.isFinite(value) && value > 0),
      };
      if (!profileEditor.id) {
        payload.password = String(profileEditor.password || '').trim();
      } else if (String(profileEditor.password || '').trim()) {
        payload.password = String(profileEditor.password || '').trim();
      }
      const response = await fetchWithAuth(`${API_URL}/api/caisse/profiles${profileEditor.id ? `/${profileEditor.id}` : ''}`, {
        method: profileEditor.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result?.error || 'Enregistrement profil impossible.');
      }
      setNotice(profileEditor.id ? 'Profil mis à jour.' : 'Profil créé.');
      resetProfileEditor();
      await loadProfiles();
    } catch (saveError) {
      setError(saveError.message || 'Enregistrement profil impossible.');
    }
  };

  const resetDeviceEditor = () => {
    setDeviceEditor({
      id: null,
      name: '',
      deviceType: 'borne',
      ...EMPTY_DEVICE_PERIPHERALS,
      productionZoneId: '',
      isActive: true,
    });
  };

  const startEditDevice = (device) => {
    if (!device) {
      resetDeviceEditor();
      return;
    }
    setDeviceEditor({
      id: device.id,
      name: String(device.name || ''),
      deviceType: String(device.deviceType || 'borne'),
      ...devicePeripheralPayload(device),
      productionZoneId: device.productionZoneId ? String(device.productionZoneId) : '',
      isActive: device.isActive !== false,
    });
  };

  const saveDevice = async () => {
    try {
      if (deviceEditor.drawerEnabled && !deviceEditor.drawerPrinterId) throw new Error('Choisissez l’imprimante du tiroir.');
      if (!deviceEditor.name.trim()) {
        throw new Error('Nom appareil obligatoire.');
      }
      const response = await fetchWithAuth(`${API_URL}/api/caisse/devices${deviceEditor.id ? `/${deviceEditor.id}` : ''}`, {
        method: deviceEditor.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: deviceEditor.name.trim(),
          deviceType: deviceEditor.deviceType,
          productionZoneId: deviceEditor.productionZoneId || null,
          isActive: !!deviceEditor.isActive,
          ...devicePeripheralPayload(deviceEditor),
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || 'Enregistrement appareil impossible.');
      }
      setNotice(deviceEditor.id ? 'Appareil mis à jour.' : 'Appareil créé.');
      resetDeviceEditor();
      await loadRestaurantDevices();
      window.dispatchEvent(new Event('beresto-workstations-updated'));
    } catch (saveError) {
      setError(saveError.message || 'Enregistrement appareil impossible.');
    }
  };

  const deleteDevice = async (deviceId) => {
    if (!window.confirm('Supprimer cet appareil ?')) {
      return;
    }
    try {
      const response = await fetchWithAuth(`${API_URL}/api/caisse/devices/${deviceId}`, { method: 'DELETE' });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || 'Suppression appareil impossible.');
      }
      await loadRestaurantDevices();
      setNotice('Appareil supprimé.');
    } catch (deleteError) {
      setError(deleteError.message || 'Suppression appareil impossible.');
    }
  };

  useEffect(() => {
    if (!ticketSettings || typeof ticketSettings !== 'object') {
      return;
    }
    setReservationConfigDraft({
      reservationOpeningHour: Number(ticketSettings.reservationOpeningHour ?? 12),
      reservationClosingHour: Number(ticketSettings.reservationClosingHour ?? 22),
      reservationSlotMinutes: Number(ticketSettings.reservationSlotMinutes ?? 30),
      reservationMaxPartySize: Number(ticketSettings.reservationMaxPartySize ?? 12),
      reservationTableCapacity: Number(ticketSettings.reservationTableCapacity ?? 40),
      reservationDefaultDurationMinutes: Number(ticketSettings.reservationDefaultDurationMinutes ?? 120),
      kdsItemTimerLimitEnabled: !!ticketSettings.kdsItemTimerLimitEnabled,
      kdsItemTimerLimitSeconds: Number(ticketSettings.kdsItemTimerLimitSeconds ?? 900),
    });
    const configured = normalizePaymentMethodsConfig(ticketSettings.paymentMethods || []);
    setPaymentMethodsDraft(configured.map((entry) => entry.label).join(', '));
  }, [ticketSettings]);

  const unlockCaisse = () => {
    const expectedPin = String(pinCode || '');
    if (!expectedPin) {
      setIsLocked(false);
      return;
    }
    if (String(unlockPinInput || '') === expectedPin) {
      setIsLocked(false);
      setUnlockPinInput('');
      setUnlockError('');
      return;
    }
    setUnlockError('PIN incorrect.');
  };

  const updateCartQty = (key, delta) => {
    setCart((current) => current
      .map((item) => item.key === key
        ? (() => {
            const nextQuantity = Math.max(0, item.quantity + delta);
            return {
              ...item,
              quantity: nextQuantity,
              totalPrice: computeLineTotal(item.unitPrice, nextQuantity, item.lineDiscountPercent, item.isComped),
            };
          })()
        : item)
      .filter((item) => item.quantity > 0));
  };

  const removeCartLine = (key) => {
    setCart((current) => current.filter((item) => item.key !== key));
  };

  const setCartLineQty = (key, nextQuantityRaw) => {
    const parsed = parseInt(String(nextQuantityRaw || ''), 10);
    const nextQuantity = Number.isFinite(parsed) ? Math.max(1, parsed) : 1;
    setCart((current) => current.map((item) => (
      item.key === key
        ? {
            ...item,
            quantity: nextQuantity,
            totalPrice: computeLineTotal(item.unitPrice, nextQuantity, item.lineDiscountPercent, item.isComped),
          }
        : item
    )));
  };

  const openQtyPad = (item) => {
    if (!item?.key) return;
    setQtyModalItemKey(String(item.key));
    setQtyModalItemName(String(item.name || 'Produit'));
    setQtyModalValue(String(Math.max(1, parseInt(String(item.quantity || 1), 10) || 1)));
    setQtyModalOpen(true);
  };

  const confirmQtyPad = () => {
    if (!qtyModalItemKey) {
      setQtyModalOpen(false);
      return;
    }
    setCartLineQty(qtyModalItemKey, qtyModalValue);
    setQtyModalOpen(false);
  };

  const openLineNote = (item) => {
    if (!item?.key) return;
    setNoteModalItemKey(String(item.key));
    setNoteModalItemName(String(item.name || 'Produit'));
    setNoteModalValue(String(item.note || ''));
    setNoteModalOpen(true);
  };

  const confirmLineNote = () => {
    const targetKey = String(noteModalItemKey || '');
    if (!targetKey) {
      setNoteModalOpen(false);
      return;
    }
    const nextNote = String(noteModalValue || '').trim();
    setCart((current) => current.map((item) => (
      item.key === targetKey
        ? { ...item, note: nextNote }
        : item
    )));
    setNoteModalOpen(false);
  };

  const updateCartSplitQty = (key, nextValue) => {
    const line = cart.find((item) => item.key === key);
    if (!line) return;
    const max = Number(line.quantity || 0);
    const normalized = Math.max(0, Math.min(max, parseInt(String(nextValue), 10) || 0));
    explicitSplitKeysRef.current.add(key);
    setSplitDraft((current) => ({ ...current, [key]: normalized }));
  };

  const applyLineDiscount = (key, nextPercent) => {
    const capped = Math.max(0, Math.min(maxLineDiscountPercent, Number(nextPercent || 0)));
    setCart((current) => current.map((item) => {
      if (item.key !== key) {
        return item;
      }
      const discountPercent = capped;
      return {
        ...item,
        lineDiscountPercent: discountPercent,
        isComped: discountPercent >= 100 ? true : item.isComped,
        totalPrice: computeLineTotal(item.unitPrice, item.quantity, discountPercent, discountPercent >= 100 || item.isComped),
      };
    }));
    if (Number(nextPercent || 0) > maxLineDiscountPercent) {
      setNotice(`Remise ligne plafonnée à ${maxLineDiscountPercent}% pour ce rôle.`);
    }
  };

  const toggleLineComp = (key) => {
    if (!canCompLine) {
      setError('Action Offert réservée au manager/admin.');
      return;
    }
    setCart((current) => current.map((item) => {
      if (item.key !== key) {
        return item;
      }
      const isComped = !item.isComped;
      return {
        ...item,
        isComped,
        lineDiscountPercent: isComped ? 100 : 0,
        totalPrice: computeLineTotal(item.unitPrice, item.quantity, isComped ? 100 : 0, isComped),
      };
    }));
  };

  const toggleLineFollowUp = (key) => {
    setCart((current) => current.map((item) => (item.key === key ? { ...item, followUp: !item.followUp } : item)));
  };

  const buildReclaimCartLine = (sentLine, suffix = '') => {
    const normalizedName = String(sentLine?.name || '').trim();
    if (!normalizedName) {
      return null;
    }
    const reclaimKey = `reclaim:${sentLine.orderId}:${normalizedName}:${Date.now()}${suffix}`;
    const unitPrice = Number(sentLine.unitPrice || 0);
    const quantity = 1;
    return {
      key: reclaimKey,
      productId: sentLine.productId || null,
      printerId: sentLine.printerId || null,
      productionZoneId: sentLine.productionZoneId || null,
      name: normalizedName,
      kitchenName: `${sentLine.kitchenName || normalizedName} · RECLAMATION`,
      sectionName: '',
      quantity,
      unitPrice: Number(unitPrice.toFixed(2)),
      lineDiscountPercent: 0,
      isComped: false,
      followUp: false,
      complaint: true,
      totalPrice: computeLineTotal(unitPrice, quantity),
      taxRate: Number(sentLine.taxRate || 10),
      selectedSupplements: [],
      note: `Réclamation ticket #${sentLine.ticketNumber}`,
    };
  };

  const reclaimSentLine = (sentLine) => {
    const reclaimLine = buildReclaimCartLine(sentLine);
    if (!reclaimLine) {
      return;
    }
    setCart((current) => [...current, reclaimLine]);
    setReclaimedSentLineIds((current) => [...new Set([...current, String(sentLine.id)])]);
    setReclaimedSentLineKeys((current) => [...new Set([...current, getSentLineLogicalKey(sentLine)])]);
    setNotice(`Réclamation ajoutée depuis ticket #${sentLine.ticketNumber}.`);
    setError('');
  };

  const reclaimAllSentLines = () => {
    if (!reclaimableSentLines.length) {
      return;
    }
    const reclaimLines = reclaimableSentLines
      .map((line, index) => buildReclaimCartLine(line, `:${index}`))
      .filter(Boolean);
    if (!reclaimLines.length) {
      return;
    }
    setCart((current) => [...current, ...reclaimLines]);
    setReclaimedSentLineIds((current) => [
      ...new Set([...current, ...reclaimableSentLines.map((line) => String(line.id))]),
    ]);
    setReclaimedSentLineKeys((current) => [
      ...new Set([...current, ...reclaimableSentLines.map((line) => getSentLineLogicalKey(line))]),
    ]);
    setNotice(`${reclaimLines.length} réclamation(s) ajoutée(s) au panier.`);
    setError('');
  };

  const openSentLineCancel = (sentLine) => {
    if (!sentLine?.orderId || !sentLine?.itemId) {
      setError('Ligne impossible à annuler: identifiant manquant.');
      return;
    }
    setSentLineCancelTarget(sentLine);
    setSentLineCancelReason('Annulation produit');
    setError('');
  };

  const closeSentLineCancel = () => {
    if (sentLineCancelLoading) {
      return;
    }
    setSentLineCancelTarget(null);
    setSentLineCancelReason('Annulation produit');
  };

  const confirmSentLineCancel = async () => {
    const target = sentLineCancelTarget;
    if (!target?.orderId || !target?.itemId || sentLineCancelLoading || orderMutationBusyRef.current) {
      return;
    }
    const reason = String(sentLineCancelReason || '').trim();
    if (!reason) {
      setError('Motif obligatoire pour annuler le produit.');
      return;
    }
    const mutationPath = `/api/orders/${target.orderId}/items/${target.itemId}/cancel`;
    const mutation = getStableOrderMutation(mutationPath, { quantity: Math.max(1, Number(target.quantity || 1)), reason, printKitchenDelta: true }, target.quantity);
    orderMutationBusyRef.current = true;
    try {
      setSentLineCancelLoading(true);
      setError('');
      const response = await fetchWithAuth(`${API_URL}/api/orders/${target.orderId}/items/${target.itemId}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(mutation.body),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Annulation produit impossible.');
      }
      if (payload?.order) {
        upsertOpenOrderLocal(payload.order);
      }
      setNotice(`Produit annulé sur ticket #${target.ticketNumber || target.orderId}.`);
      setSentLineCancelTarget(null);
      setSentLineCancelReason('Annulation produit');
      await Promise.all([fetchOpenOrders(), fetchOrdersHistory()]);
    } catch (cancelError) {
      setError(cancelError.message || 'Annulation produit impossible.');
    } finally {
      orderMutationBusyRef.current = false;
      setSentLineCancelLoading(false);
    }
  };

  const openPayment = () => {
    if (checkoutLoading) {
      return;
    }
    const hasCart = cart.length > 0;
    const hasTableDue = (selectedTableDueTotal || tableDueTotal) > 0;
    if (!hasCart && !hasTableDue) {
      return;
    }
    if (hasCart && orderMode === 'sur_place' && !String(tableNumber || '').trim()) {
      setError('Indique la table pour une commande sur place.');
      return;
    }
    setError('');
    setPaymentTarget(hasCart ? 'cart' : 'table');
    setPaymentOpen(true);
  };

  const openOrderPayment = (order) => {
    if (!order || String(order.paymentStatus || '').toLowerCase() === 'paid') {
      return;
    }
    setSelectedOrderForPayment(order);
    setPaymentTarget('order');
    setPaymentOpen(true);
  };

  const updateOrderStatus = async (orderId, status, extra = {}) => {
    if (['prete', 'termine', 'terminee'].includes(status) && isAwaitingPreparation(ordersHistory.find(order => Number(order.id) === Number(orderId)))) return;
    try {
      setCheckoutLoading(true);
      setError('');
      const response = await fetchWithAuth(`${API_URL}/api/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, ...extra }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Mise à jour statut impossible.');
      }
      setNotice(`Commande #${payload.ticketNumber || payload.id} mise à jour: ${status}.`);
      await Promise.all([fetchOpenOrders(), fetchOrdersHistory()]);
    } catch (statusError) {
      if (!isOnline) {
        pushOfflineAction({
          method: 'PATCH',
          path: `/api/orders/${orderId}/status`,
          body: { status, ...extra },
        });
        setNotice(`Hors ligne: changement statut mis en file (${status}).`);
      } else {
        setError(statusError.message || 'Mise à jour statut impossible.');
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  const cancelSelectedOrder = async (reasonOverride = '') => {
    if (!selectedOrder) {
      return;
    }
    const reason = String(reasonOverride || cancelReasonValue || '').trim();
    if (!reason) {
      setError('Motif obligatoire pour annuler le ticket.');
      return;
    }
    await updateOrderStatus(selectedOrder.id, 'annulee', { reason });
    await fetchOrderAuditLogs(selectedOrder.id);
    setCancelModalOpen(false);
    setCancelReasonValue('');
  };

  const formatAuditAction = (action) => {
    const normalized = String(action || '').toLowerCase();
    const map = {
      order_created: 'Création ticket',
      order_status_changed: 'Changement statut',
      order_item_cancelled: 'Annulation ligne',
      order_reopened: 'Réouverture ticket',
      order_refund_issued: 'Remboursement',
      order_split_created: 'Split ticket',
      order_table_transferred: 'Transfert table',
      order_paid: 'Paiement',
      order_paid_and_printed: 'Paiement + impression',
    };
    return map[normalized] || String(action || '').replace(/_/g, ' ');
  };

  const refundOrder = async () => {
    const order = refundDraft?.order;
    if (refundBusyRef.current || !order || String(order.paymentStatus || '').toLowerCase() !== 'paid') {
      return;
    }
    refundBusyRef.current = true;
    try {
      if (!isOnline || !isOfflineScopeCurrent(offlineScope)) throw new Error('Connexion et poste vérifiés requis pour rembourser.');
      if (!refundDraft.reason.trim() || !refundDraft.paymentMethod || refundDraft.paymentMethod === 'mixte') throw new Error('Choisissez un moyen unique et indiquez le motif.');
      setCheckoutLoading(true);
      setError('');
      const response = await fetchWithAuth(`${API_URL}/api/orders/${order.id}/refund`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amountTtc: Number(refundDraft.amountTtc), reason: refundDraft.reason.trim(), paymentMethod: refundDraft.paymentMethod }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Remboursement impossible.');
      }
      await applyDrawerResponse(payload);
      setRefundDraft(null);
      setNotice(`Commande #${payload.ticketNumber || payload.id} remboursée.`);
      await fetchOrdersHistory();
    } catch (refundError) {
      setError(refundError.message || 'Remboursement impossible.');
    } finally {
      refundBusyRef.current = false;
      setCheckoutLoading(false);
    }
  };

  const exportOrdersCsv = () => {
    downloadAuthenticatedFile(`${API_URL}/api/orders/export.csv`, 'historique-commandes.csv')
      .catch((exportError) => setError(exportError.message));
  };

  useEffect(() => {
    if (!selectedOrder) {
      setSplitLinesDraft({});
      setCancelLinesDraft({});
      return;
    }
    const draft = {};
    const cancelDraft = {};
    getOrderItems(selectedOrder).forEach((item) => {
      draft[item.id] = 0;
      cancelDraft[item.id] = { quantity: 1, reason: '' };
    });
    setSplitLinesDraft(draft);
    setCancelLinesDraft(cancelDraft);
  }, [selectedOrderId, selectedOrder]);

  useEffect(() => {
    if (!selectedOrderId) {
      setOrderAuditLogs([]);
      return;
    }
    fetchOrderAuditLogs(selectedOrderId);
  }, [fetchOrderAuditLogs, selectedOrderId]);

  useEffect(() => {
    if (activeView !== 'control') {
      return;
    }
    fetchControlSummary(new Date().toISOString().slice(0, 10));
  }, [activeView, fetchControlSummary]);

  const updateSplitQty = (itemId, nextValue) => {
    const item = getOrderItems(selectedOrder).find((line) => Number(line.id) === Number(itemId));
    if (!item) {
      return;
    }
    const maxQty = Math.max(0, Number(item.quantity || 0));
    const parsed = Math.max(0, Math.min(maxQty, parseInt(nextValue, 10) || 0));
    setSplitLinesDraft((current) => ({ ...current, [itemId]: parsed }));
  };

  const splitSelectedOrder = async () => {
    if (!selectedOrder || orderMutationBusyRef.current) {
      return;
    }
    const lines = getOrderItems(selectedOrder)
      .map((item) => ({
        orderItemId: item.id,
        quantity: Number(splitLinesDraft[item.id] || 0),
      }))
      .filter((line) => line.quantity > 0);

    if (!lines.length) {
      setError('Choisis au moins une quantité à détacher.');
      return;
    }

    const mutation = getStableOrderMutation(`/api/orders/${selectedOrder.id}/split`, { lines }, selectedOrder.updatedAt);
    orderMutationBusyRef.current = true;
    try {
      setCheckoutLoading(true);
      setError('');
      const response = await fetchWithAuth(`${API_URL}/api/orders/${selectedOrder.id}/split`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(mutation.body),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Split de ticket impossible.');
      }
      setNotice(`Ticket #${payload?.sourceOrder?.ticketNumber || selectedOrder.id} splitté vers #${payload?.splitOrder?.ticketNumber || '-'}.`);
      await Promise.all([fetchOpenOrders(), fetchOrdersHistory()]);
      if (payload?.splitOrder?.id) {
        setSelectedOrderId(payload.splitOrder.id);
      }
    } catch (splitError) {
      setError(splitError.message || 'Split de ticket impossible.');
    } finally {
      orderMutationBusyRef.current = false;
      setCheckoutLoading(false);
    }
  };

  const updateCancelDraft = (itemId, nextPatch, maxQty = 1) => {
    setCancelLinesDraft((current) => {
      const previous = current[itemId] || { quantity: 1, reason: '' };
      const merged = { ...previous, ...nextPatch };
      const parsedQty = Math.max(1, Math.min(maxQty, parseInt(merged.quantity, 10) || 1));
      return {
        ...current,
        [itemId]: {
          quantity: parsedQty,
          reason: String(merged.reason || ''),
        },
      };
    });
  };

  const cancelOrderItem = async (orderId, item) => {
    if (orderMutationBusyRef.current) return;
    const maxQty = Math.max(1, Number(item?.quantity || 1));
    const draft = cancelLinesDraft[item.id] || { quantity: 1, reason: '' };
    const quantity = Math.max(1, Math.min(maxQty, parseInt(draft.quantity, 10) || 1));
    const reason = String(draft.reason || '').trim();
    if (!reason) {
      setError('Motif obligatoire pour annuler une ligne.');
      return;
    }

    const mutation = getStableOrderMutation(`/api/orders/${orderId}/items/${item.id}/cancel`, { quantity, reason, printKitchenDelta: true }, item.quantity);
    orderMutationBusyRef.current = true;
    try {
      setCheckoutLoading(true);
      setError('');
      const response = await fetchWithAuth(`${API_URL}/api/orders/${orderId}/items/${item.id}/cancel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(mutation.body),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Annulation de ligne impossible.');
      }
      setNotice(`Ligne annulée (${quantity}) sur ticket #${payload?.order?.ticketNumber || orderId}.`);
      await Promise.all([fetchOpenOrders(), fetchOrdersHistory(), fetchOrderAuditLogs(orderId)]);
    } catch (cancelError) {
      if (!isOnline) {
        pushOfflineAction({
          method: 'POST',
          path: `/api/orders/${orderId}/items/${item.id}/cancel`,
          body: mutation.body,
        });
        setNotice(`Hors ligne: annulation ligne mise en file (${item.name}).`);
      } else {
        setError(cancelError.message || 'Annulation de ligne impossible.');
      }
    } finally {
      orderMutationBusyRef.current = false;
      setCheckoutLoading(false);
    }
  };

  const reopenSelectedOrder = async () => {
    if (!selectedOrder) {
      return;
    }
    try {
      setCheckoutLoading(true);
      setError('');
      const response = await fetchWithAuth(`${API_URL}/api/orders/${selectedOrder.id}/reopen`, {
        method: 'POST',
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Réouverture impossible.');
      }
      setNotice(`Ticket #${payload?.ticketNumber || payload?.id || selectedOrder.id} réouvert.`);
      await Promise.all([fetchOpenOrders(), fetchOrdersHistory(), fetchOrderAuditLogs(selectedOrder.id)]);
    } catch (reopenError) {
      setError(reopenError.message || 'Réouverture impossible.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const resolveClientTicketPrinter = useCallback(() => {
    if (workstation.loading || workstation.error || workstation.bindingRequired) return null;
    if (workstation.device) return clientPrinters.find(printer => String(printer.id) === String(workstation.device.receiptPrinterId)) || null;
    const clientTicketZoneId = String(ticketSettings?.clientTicketZoneId || '');
    const targetZone = productionZones.find((zone) => String(zone.id) === clientTicketZoneId);
    return (
      targetZone?.Printer ||
      clientPrinters.find((printer) => String(printer.id) === String(targetZone?.printerId || '')) ||
      clientPrinters.find((printer) => String(printer.id) === String(ticketSettings?.dailySummaryPrinterId || '')) ||
      getPreferredClientPrinter(clientPrinters)
    );
  }, [clientPrinters, productionZones, ticketSettings, workstation]);

  const printSelectedOrderTicket = async (mode = 'final', targetOrder = selectedOrder, terminalReceipt = null) => {
    if (!targetOrder) {
      return;
    }
    const targetPrinter = resolveClientTicketPrinter();
    if (!targetPrinter) {
      if (terminalReceipt) throw new Error('Aucune imprimante ticket client disponible.');
      setError('Aucune imprimante ticket client disponible.');
      return;
    }
    try {
      setCheckoutLoading(true);
      setError('');
      const stripeReceipt = terminalReceipt || (mode !== 'prebill' && targetOrder.physicalPaymentProvider === 'stripe_terminal' ? await getTerminalReceipt(fetchWithAuth, targetOrder.id, offlineScope) : null);
      const normalizedPaymentMethod = String(targetOrder?.paymentMethod || '').trim().toLowerCase();
      const isCashPayment = normalizedPaymentMethod === 'espece' || normalizedPaymentMethod === 'cash';
      const isAlreadyPaid = String(targetOrder?.paymentStatus || '').trim().toLowerCase() === 'paid';
      const ticketHeaderText = mode === 'prebill'
        ? 'PRE-NOTE'
        : isCashPayment && isAlreadyPaid
          ? 'DEJA PAYE ESPECE'
          : (ticketSettings?.ticketHeaderCardPaidText || 'PAYE CB');

      await printOrderTickets(
        [targetPrinter],
        mapOrderToPrintableItems(targetOrder),
        Number(targetOrder.totalTtc || 0).toFixed(2),
        targetOrder.ticketNumber,
        {
          ticketHeaderText,
          ticketHeaderTextSize: ticketSettings?.ticketHeaderTextSize || 4,
          ticketLogoMedia: ticketSettings?.ticketLogoMedia || '',
          ticketStoreLine1: ticketSettings?.ticketStoreLine1 || '',
          ticketStoreLine2: ticketSettings?.ticketStoreLine2 || '',
          ticketStoreLine3: ticketSettings?.ticketStoreLine3 || '',
          ticketStoreLine4: ticketSettings?.ticketStoreLine4 || '',
          ticketStoreLine5: ticketSettings?.ticketStoreLine5 || '',
          ticketStoreLine6: ticketSettings?.ticketStoreLine6 || '',
          ticketQrText: ticketSettings?.ticketQrText || '',
          ticketQrUrl: ticketSettings?.ticketQrUrl || '',
          ticketFooterLine1: ticketSettings?.ticketFooterLine1 || '',
          ticketFooterLine2: ticketSettings?.ticketFooterLine2 || '',
          ticketFooterLine3: ticketSettings?.ticketFooterLine3 || '',
          customerFirstName: targetOrder?.Customer?.firstName || '',
          paymentMethod: targetOrder?.paymentMethod || '',
          stripeReceipt,
          isAlreadyPaid: mode === 'prebill' ? false : isAlreadyPaid,
          schedule: orderSchedule(targetOrder),
          customerComment: targetOrder.customerComment || '',
          includeCutlery: !!targetOrder.includeCutlery,
          skipProductionTickets: true,
        }
      );
      setNotice(mode === 'prebill' ? 'Pré-note imprimée.' : 'Note finale imprimée.');
    } catch (printError) {
      setError(printError.message || 'Impression ticket impossible.');
      if (terminalReceipt) throw printError;
    } finally {
      setCheckoutLoading(false);
    }
  };

  const printZReport = async () => {
    try {
      setDailySummaryPrinting(true);
      setError('');
      if (openCashSession) {
        throw new Error('Clôture session caisse requise avant impression Z.');
      }
      const summary = await fetchDailySummary(reportDate);
      const targetPrinter =
        clientPrinters.find((printer) => String(printer.id) === String(ticketSettings?.dailySummaryPrinterId || ''))
        || resolveClientTicketPrinter();
      if (!targetPrinter) {
        throw new Error('Aucune imprimante définie pour le récap journalier.');
      }
      const payload = summary || dailySummary;
      if (!payload) {
        throw new Error('Récap journalier indisponible.');
      }
      await printDailySummary(targetPrinter, payload);
      const response = await fetchWithAuth(`${API_URL}/api/printer-settings/mark-daily-summary-printed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: payload.date }),
      });
      const markPayload = await response.json();
      if (!response.ok) {
        throw new Error(markPayload?.error || 'Marquage impression Z impossible.');
      }
      setTicketSettings((current) => ({
        ...current,
        dailySummaryPrintedAt: markPayload.dailySummaryPrintedAt,
      }));
      setNotice('Clôture Z imprimée et marquée.');
    } catch (zError) {
      setError(zError.message || 'Impression Z impossible.');
    } finally {
      setDailySummaryPrinting(false);
    }
  };

  const exportDailyOrdersCsv = () => {
    const date = encodeURIComponent(reportDate || new Date().toISOString().slice(0, 10));
    downloadAuthenticatedFile(`${API_URL}/api/orders/export-day.csv?date=${date}`, `commandes-${date}.csv`)
      .catch((exportError) => setError(exportError.message));
  };

  const exportControlPdf = () => {
    const date = encodeURIComponent(reportDate || new Date().toISOString().slice(0, 10));
    downloadAuthenticatedFile(`${API_URL}/api/caisse/control-summary.pdf?date=${date}`, `controle-${date}.pdf`)
      .catch((exportError) => setError(exportError.message));
  };

  const exportAccountingCsv = () => {
    const date = encodeURIComponent(reportDate || new Date().toISOString().slice(0, 10));
    downloadAuthenticatedFile(`${API_URL}/api/caisse/accounting-export.csv?date=${date}`, `comptabilite-${date}.csv`)
      .catch((exportError) => setError(exportError.message));
  };

  const retryOfflineConflict = (entry) => {
    if (!entry?.originalAction) {
      return;
    }
    pushOfflineAction({ ...entry.originalAction, retries: 0 });
    setOfflineConflicts((current) => {
      const next = current.filter((item) => item !== entry);
      saveJsonArrayStorage(conflictsKey, next);
      return next;
    });
  };

  const discardOfflineConflict = (entry) => {
    setOfflineConflicts((current) => {
      const next = current.filter((item) => item !== entry);
      saveJsonArrayStorage(conflictsKey, next);
      return next;
    });
    pushOfflineHistory({
      actionId: entry?.actionId || '',
      status: 'discarded',
      method: entry?.method || '',
      path: entry?.path || '',
      reason: entry?.reason || 'discarded',
    });
  };

  const openCashSessionHandler = async () => {
    const openingFloat = Number(String(openingFloatInput || '0').replace(',', '.'));
    try {
      setCheckoutLoading(true);
      setError('');
      const response = await fetchWithAuth(`${API_URL}/api/cash-register/sessions/open`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ openingFloat }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Ouverture de caisse impossible.');
      }
      setNotice(`Session caisse ouverte (fond: ${formatCurrency(payload.openingFloat)}).`);
      setOpeningFloatInput('');
      await fetchCashSessions();
    } catch (openError) {
      setError(openError.message || 'Ouverture de caisse impossible.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const closeCashSessionHandler = async () => {
    if (!openCashSession) {
      return;
    }
    const countedCashTotal = Number(String(countedCashInput || '0').replace(',', '.'));
    try {
      setCheckoutLoading(true);
      setError('');
      const response = await fetchWithAuth(`${API_URL}/api/cash-register/sessions/${openCashSession.id}/close`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ countedCashTotal }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Clôture de caisse impossible.');
      }
      setNotice(`Session clôturée. Écart: ${formatCurrency(payload.differenceAmount)}.`);
      setCountedCashInput('');
      await fetchCashSessions();
      await fetchDailySummary(reportDate);
    } catch (closeError) {
      setError(closeError.message || 'Clôture de caisse impossible.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const upsertOpenOrderLocal = useCallback((orderPayload) => {
    if (!orderPayload?.id) {
      return;
    }
    setOpenOrders((current) => {
      const next = [...safeArray(current)];
      const index = next.findIndex((entry) => Number(entry.id) === Number(orderPayload.id));
      if (index >= 0) {
        next[index] = orderPayload;
      } else {
        next.unshift(orderPayload);
      }
      return next.filter((order) => String(order.paymentStatus || '').toLowerCase() !== 'paid');
    });
  }, []);

  const buildOrderItemPayloads = (item) => {
    const kitchenInstruction = String(item.note || '').trim();
    const prefixParts = [
      item.isComped ? 'OFFERT' : '',
      item.followUp ? 'A SUIVRE' : '',
      item.complaint ? 'RECLAMATION' : '',
      item.lineDiscountPercent > 0 && !item.isComped ? `REMISE ${item.lineDiscountPercent}%` : '',
      item.note ? `NOTE: ${item.note}` : '',
    ].filter(Boolean);
    if (item.isMenu && safeArray(item.expandedOrderItems).length) {
      const firstLineUnitPrice = item.quantity > 0 ? Number((Number(item.totalPrice || 0) / Number(item.quantity || 1)).toFixed(2)) : 0;
      return safeArray(item.expandedOrderItems).map((line, index) => ({
        stockCompositionVersion: 1,
        kitchenInstruction,
        stockChoice: line.stockChoice || null,
        productId: line.productId,
        menuId: line.menuId || item.menuId,
        printerId: line.printerId,
        productionZoneId: line.productionZoneId,
        name: line.name,
        kitchenName: [...prefixParts, line.kitchenName || line.name].filter(Boolean).join(' · '),
        quantity: item.quantity,
        unitPrice: index === 0 ? firstLineUnitPrice : 0,
        totalPrice: index === 0 ? Number(item.totalPrice || 0) : 0,
        taxRate: line.taxRate || item.taxRate,
        isMenu: true,
        selectionsJson: item.selectionsJson || line.selectionsJson || null,
      }));
    }
    return [{
      // Reclaimed lines do not retain the original choices; do not attest their composition.
      ...(item.complaint ? {} : { stockCompositionVersion: 1 }),
      kitchenInstruction,
      stockChoice: item.stockChoice || null,
      productId: item.productId,
      menuId: item.menuId || null,
      printerId: item.printerId,
      productionZoneId: item.productionZoneId,
      name: item.name,
      kitchenName: [
        ...prefixParts,
        item.kitchenName,
      ].filter(Boolean).join(' · '),
      quantity: item.quantity,
      unitPrice: item.unitPrice,
      totalPrice: item.totalPrice,
      taxRate: item.taxRate,
      isMenu: !!item.isMenu,
      selectionsJson: stockSupplementSelections(item.selectedSupplements, item.selectionsJson),
    }];
  };

  const createCurrentOrder = async ({ printProductionTickets = false, ticketHeaderText = 'A ENCAISSER', cartItems = null, totalsOverride = null, offlinePayment = null, requireOnline = false, paymentDraftKind = 'terminal' } = {}) => {
    const sourceCart = cartItems && cartItems.length ? cartItems : cart;
    const sourceTotals = totalsOverride || totals;
    if (!sourceCart.length) return null;
    if (pendingOrderRef.current?.body?.customerId && !befidCustomer) throw new Error('Un ticket BEFID est déjà en cours. Reprenez ce ticket dans les commandes avant une nouvelle vente.');
    if (befidCustomer && (befidCustomerScope.current !== getCustomerOperatorScope() || !navigator.onLine || String(befidCustomer.accountId) !== getCustomerSessionAccountId())) throw new Error('La fiche BEFID nécessite une connexion au même restaurant. Reconnectez-vous ou retirez le client avant une nouvelle vente.');
    if (!isOfflineScopeCurrent(offlineScope)) throw new Error('Compte modifié : rechargez la caisse avant de continuer.');
    if (orderMode === 'sur_place' && !String(tableNumber || '').trim()) {
      setError('Indique la table pour une commande sur place.');
      return null;
    }
    const orderLabel = orderMode === 'sur_place'
      ? `SUR PLACE${tableNumber ? ` · TABLE ${tableNumber}` : ''}` : 'A EMPORTER';
    const commentParts = [orderLabel,
      orderMode === 'sur_place' && currentCovers > 0 ? `COUVERTS: ${currentCovers}` : '',
      customerName ? `Client: ${customerName}` : '', orderNote ? `Note: ${orderNote}` : '',
    ].filter(Boolean);
    const requestContent = {
      salesChannel: 'caisse',
      ...(befidCustomer ? { customerId: befidCustomer.id } : {}),
      totalTtc: sourceTotals.total,
      customerComment: commentParts.join('\n'),
      promoDiscountAmount: sourceTotals.discountAmount || 0,
      promoRewardType: (sourceTotals.discountAmount || 0) > 0 ? `manual_cash_register_discount_${discountMode}` : '',
      printProductionTickets,
      ticketHeaderText,
      items: sourceCart.flatMap(buildOrderItemPayloads),
    };
    // A failed payment must retry the original order, including its exact creation payload.
    // Payment presentation (header/method) may change without creating another sale.
    const signature = JSON.stringify({ ...requestContent, ticketHeaderText: '', printProductionTickets: false });
    if (requireOnline && localStorage.getItem(terminalDraftKey) && pendingOrderRef.current?.signature !== signature) throw new Error('Le ticket précédent est en cours de récupération. Rechargez la caisse avant de modifier le panier.');
    if (pendingOrderRef.current?.signature !== signature) {
      const previous = pendingOrderRef.current;
      if (previous?.order) {
        if (!previous.paymentRefused) {
          throw new Error('Le paiement du ticket précédent doit être vérifié avant de modifier le panier. Reprenez ce ticket dans les commandes.');
        }
        if (!isOfflineScopeCurrent(offlineScope)) throw new Error('Compte modifié : rechargez la caisse.');
        const abandonResponse = await fetchWithAuth(`${API_URL}/api/orders/${previous.order.id}/abandon`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
        });
        const abandonPayload = await abandonResponse.json();
        if (!abandonResponse.ok) throw new Error(abandonPayload?.error || 'Le ticket précédent ne peut pas être remplacé. Vérifiez son paiement.');
        pendingOrderRef.current = null;
      }
      let ticketNumber = Date.now().toString().slice(-4);
      try {
        const response = await fetchWithAuth(`${API_URL}/api/printer-settings/next-ticket-number`, { method: 'POST' });
        const payload = await response.json();
        if (response.ok && payload?.ticketNumber) ticketNumber = payload.ticketNumber;
      } catch (_) { /* A stable request id, rather than the display ticket number, deduplicates retries. */ }
      pendingOrderRef.current = { signature, body: { ...requestContent, ticketNumber, orderedAt: new Date().toISOString(), clientRequestId: newOfflineId() }, order: null };
    }
    const pending = pendingOrderRef.current;
    if (requireOnline) localStorage.setItem(terminalDraftKey, JSON.stringify({ ...pending, kind: paymentDraftKind }));
    if (pending.order) return pending.order;
    let response;
    let payload;
    try {
      if (!isOfflineScopeCurrent(offlineScope)) throw new Error('Compte modifié : rechargez la caisse.');
      response = await fetchWithAuth(`${API_URL}/api/orders`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(pending.body),
      });
      payload = await response.json();
    } catch (networkError) {
      if (!isOfflineScopeCurrent(offlineScope)) throw networkError;
      if (pending.body.customerId) throw new Error('Connexion interrompue : le ticket avec client BEFID reste à reprendre en ligne. Il n’a pas été ajouté à la file hors ligne.');
      if (requireOnline) throw new Error('Connexion interrompue : reprenez ce même ticket après rétablissement. Aucun paiement n’a été envoyé au terminal.');
      pushOfflineAction(offlinePayment
        ? { type: 'create_order_and_pay', body: { order: pending.body, payment: offlinePayment } }
        : { type: 'create_order', body: pending.body });
      setNotice('Connexion interrompue : commande conservée en file de synchronisation.');
      pendingOrderRef.current = null;
      return { offlineQueued: true };
    }
    if (!response.ok) throw new Error(stockRecipeError(payload, 'Commande impossible.'));
    pending.order = payload;
    return payload;
  };

  const clearCurrentTicket = () => {
    pendingOrderRef.current = null;
    explicitSplitKeysRef.current.clear();
    setCart([]);
    setCartOpen(false);
    setPaymentOpen(false);
    setCustomerName('');
    setBefidCustomer(null);
    setBefidTicketGeneration((value) => value + 1);
    setOrderNote('');
    setDiscountValue('');
    if (orderMode === 'a_emporter') {
      setTableNumber('');
    }
  };

  const sendKitchen = async () => {
    if (!cart.length || checkoutLoading) {
      return;
    }
    try {
      setCheckoutLoading(true);
      setError('');
      setNotice('');
      let orderPayload = null;
      const targetTableOrder = orderMode === 'sur_place' && tableOrders.length
        ? [...tableOrders]
          .filter((order) => String(order.paymentStatus || '').toLowerCase() !== 'paid' && String(order.status || '').toLowerCase() !== 'annulee')
          .sort((a, b) => new Date(b.orderedAt || 0).getTime() - new Date(a.orderedAt || 0).getTime())[0]
        : null;

      if (targetTableOrder?.id) {
        const addItemsBody = {
          printKitchenDelta: true,
          ticketHeaderText: `AJOUT CUISINE TABLE ${tableNumber || ''}`.trim(),
          items: cart.flatMap(buildOrderItemPayloads),
        };
        const addResponse = await fetchWithAuth(`${API_URL}/api/orders/${targetTableOrder.id}/add-items`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(addItemsBody),
        });
        const addPayload = await addResponse.json();
        if (!addResponse.ok) {
          throw new Error(stockRecipeError(addPayload, 'Ajout cuisine impossible.'));
        }
        orderPayload = addPayload?.order || targetTableOrder;
        upsertOpenOrderLocal(orderPayload);
      } else {
        orderPayload = await createCurrentOrder({
          printProductionTickets: true,
          ticketHeaderText: 'A ENCAISSER',
        });
        upsertOpenOrderLocal(orderPayload);
      }

      if (!orderPayload) return;
      if (orderPayload.offlineQueued) { clearCurrentTicket(); return; }
      setNotice(`Commande #${orderPayload.ticketNumber || orderPayload.id} envoyée en cuisine.`);
      clearCurrentTicket();
      await fetchOpenOrders();
      if (orderMode === 'sur_place') {
        setActiveView('sale');
        setTablePickerOpen(true);
      }
    } catch (sendError) {
      setError(sendError.message || 'Envoi cuisine impossible.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const printClientTicketFromCart = async () => {
    if (checkoutLoading || !cart.length) return;
    try {
      setCheckoutLoading(true);
      setError('');
      const targetPrinter = resolveClientTicketPrinter();
      if (!targetPrinter) {
        throw new Error('Aucune imprimante ticket client disponible.');
      }
      const orderPayload = await createCurrentOrder({
        printProductionTickets: false,
        ticketHeaderText: 'A ENCAISSER',
      });
      if (!orderPayload || orderPayload.offlineQueued) {
        if (orderPayload?.offlineQueued) clearCurrentTicket();
        return;
      }
      await printOrderTickets(
        [targetPrinter],
        mapOrderToPrintableItems(orderPayload),
        Number(orderPayload.totalTtc || 0).toFixed(2),
        orderPayload.ticketNumber,
        {
          ticketType: 'client',
          ticketHeaderText: 'A ENCAISSER',
          ticketHeaderTextSize: ticketSettings?.ticketHeaderTextSize || 4,
          ticketLogoMedia: ticketSettings?.ticketLogoMedia || '',
          ticketStoreLine1: ticketSettings?.ticketStoreLine1 || '',
          ticketStoreLine2: ticketSettings?.ticketStoreLine2 || '',
          ticketStoreLine3: ticketSettings?.ticketStoreLine3 || '',
          ticketStoreLine4: ticketSettings?.ticketStoreLine4 || '',
          ticketStoreLine5: ticketSettings?.ticketStoreLine5 || '',
          ticketStoreLine6: ticketSettings?.ticketStoreLine6 || '',
          ticketQrText: ticketSettings?.ticketQrText || '',
          ticketQrUrl: ticketSettings?.ticketQrUrl || '',
          ticketFooterLine1: ticketSettings?.ticketFooterLine1 || '',
          ticketFooterLine2: ticketSettings?.ticketFooterLine2 || '',
          ticketFooterLine3: ticketSettings?.ticketFooterLine3 || '',
          schedule: orderSchedule(orderPayload),
          customerComment: orderPayload.customerComment || '',
          skipProductionTickets: true,
        }
      );
      setNotice(`Ticket client #${orderPayload.ticketNumber || orderPayload.id} imprimé.`);
      clearCurrentTicket();
      await Promise.all([fetchOpenOrders(), fetchOrdersHistory()]);
    } catch (printError) {
      setError(printError.message || 'Impression ticket client impossible.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const applyDrawerResponse = async payload => {
    const message = await consumeDrawerOperation(payload?.drawerOperation, fetchWithAuth, offlineScope);
    if (message) setDrawerMessage(message);
  };
  const openDrawer = async () => {
    if (drawerBusyRef.current || !drawerReason.trim()) return;
    drawerBusyRef.current = true; setDrawerBusy(true); setDrawerMessage('');
    try {
      if (!isOfflineScopeCurrent(offlineScope) || !isOnline) throw new Error('Connexion et poste vérifiés requis. Aucune ouverture mise en attente.');
      if (!canOpenCashDrawer || !openCashSession || !workstation.device?.drawerEnabled) throw new Error('Ouverture non autorisée sur ce poste.');
      const response = await fetchWithAuth(`${API_URL}/api/cash-register/drawer/open`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reason: drawerReason.trim(), idempotencyKey: newOfflineId() }) });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload?.error || 'Ouverture du tiroir refusée.');
      await applyDrawerResponse(payload);
      setDrawerFormOpen(false); setDrawerReason('');
    } catch (failure) { setDrawerMessage(`${failure.message} Vérifiez le tiroir avant une nouvelle demande.`); }
    finally { drawerBusyRef.current = false; setDrawerBusy(false); }
  };

  const handleCheckout = async (paymentOptions = {}) => {
    if (checkoutLoading || checkoutBusyRef.current) {
      return;
    }
    checkoutBusyRef.current = true;

    try {
      if (!isOfflineScopeCurrent(offlineScope)) throw new Error('Compte modifié : rechargez la caisse.');
      setCheckoutLoading(true);
      setError('');
      setNotice('');

      const existingTerminal = readPendingTerminalPayment(offlineScope);
      if (existingTerminal) { setPaymentOpen(false); setTerminalOrderId(existingTerminal.orderId); return; }
      const requestedMethod = paymentOptions.paymentMethod || paymentMethod;
      if (workstation.loading || workstation.error || workstation.bindingRequired) throw new Error(workstation.error || 'Associez ce poste avant encaissement.');
      const stationCash = Boolean(workstation.device) && paymentIncludesCash(requestedMethod, paymentOptions.paymentSplits);
      if (stationCash && !isOnline) throw new Error('Connexion requise pour enregistrer les espèces et commander le tiroir.');
      if (stationCash && !openCashSession) throw new Error('Ouvrez la session de caisse de ce poste avant encaissement.');
      if (localStorage.getItem(terminalDraftKey) && JSON.parse(localStorage.getItem(terminalDraftKey))?.kind !== 'cash' && !paymentIncludesCard(requestedMethod, paymentOptions.paymentSplits)) throw new Error('Le ticket carte précédent doit être récupéré avant un autre encaissement.');
      if (paymentIncludesCard(requestedMethod, paymentOptions.paymentSplits)) {
        const terminal = await getTerminalConfig(fetchWithAuth, offlineScope);
        if ((terminal.deviceId || workstation.device) && (!terminal.enabled || !terminal.configured)) throw new Error('Le terminal de ce poste doit être configuré avant encaissement CB.');
        if (terminal.enabled) {
          if (!terminal.configured) throw new Error(terminal.message || 'Le terminal de paiement doit être configuré avant encaissement.');
          if (!isOnline) throw new Error('Connexion nécessaire pour encaisser sur le terminal.');
          if (requestedMethod !== 'cb') throw new Error('Le terminal accepte un paiement CB complet. Encaissez chaque ticket séparément.');
          let order;
          if (paymentTarget === 'table') {
            const selected = selectedTableOrders.length ? selectedTableOrders : tableOrders;
            if (selected.length !== 1) throw new Error('Sélectionnez un seul ticket pour le paiement sur terminal.');
            [order] = selected;
          } else if (paymentTarget === 'order') order = selectedOrderForPayment;
          else {
            const selectedCount = selectedCartItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
            const cartCount = cart.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
            if (selectedCount > 0 && selectedCount < cartCount) throw new Error('Le terminal accepte le ticket entier. Séparez le ticket avant encaissement.');
            if (Math.round(Number(totals.total) * 100) < 50) throw new Error('Le paiement sur terminal nécessite un ticket d’au moins 0,50 €.');
            order = await createCurrentOrder({ requireOnline: true, printProductionTickets: false });
          }
          if (!order) return;
          if (Math.round(Number(order.totalTtc) * 100) < 50) throw new Error('Le paiement sur terminal nécessite un ticket d’au moins 0,50 €.');
          persistPendingTerminalPayment({ orderId: Number(order.id), state: 'ready', readerId: terminal.assignedReaderId || undefined, amountCents: Math.round(Number(order.totalTtc) * 100), context: { source: 'caisse', printProductionTickets: paymentTarget === 'cart' } }, offlineScope);
          localStorage.removeItem(terminalDraftKey);
          if (paymentTarget === 'cart') clearCurrentTicket();
          setPaymentOpen(false);
          setTerminalOrderId(order.id);
          return;
        }
      }

      if (paymentTarget === 'table' && (selectedTableOrders.length || tableOrders.length)) {
        const ordersToPay = selectedTableOrders.length ? selectedTableOrders : tableOrders;
        const method = paymentOptions.paymentMethod || paymentMethod;
        if (method === 'mixte' && ordersToPay.length > 1) throw new Error('Paiement mixte multi-tickets indisponible : encaissez chaque ticket séparément.');
        const response = await fetchWithAuth(`${API_URL}/api/tables/${encodeURIComponent(String(tableNumber || '').trim().toUpperCase())}/checkout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            paymentMethod: method,
            amountPaid: paymentOptions.amountPaid,
            paymentSplits: ordersToPay.length === 1 ? paymentOptions.paymentSplits : undefined,
            ...(selectedTableOrders.length ? { orderIds: selectedTableOrders.map(order => Number(order.id)) } : {}),
          }),
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload?.error || 'Encaissement table impossible.');
        await applyDrawerResponse(payload);
        setNotice(`${ordersToPay.length > 1 ? 'Tickets sélectionnés' : 'Ticket sélectionné'} encaissé(s) table ${tableNumber}.`);
        setPaymentOpen(false);
        await Promise.all([fetchOpenOrders(), fetchOrdersHistory()]);
        return;
      }

      if (paymentTarget === 'order' && selectedOrderForPayment) {
        const paymentResponse = await fetchWithAuth(`${API_URL}/api/orders/${selectedOrderForPayment.id}/payment`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            paymentMethod: paymentOptions.paymentMethod || paymentMethod,
            amountPaid: paymentOptions.amountPaid ?? Number(selectedOrderForPayment.totalTtc || 0),
            paymentSplits: paymentOptions.paymentSplits,
            printProductionTickets: false,
          }),
        });
        const paymentPayload = await paymentResponse.json();
        if (!paymentResponse.ok) {
          throw new Error(paymentPayload?.error || 'Encaissement commande impossible.');
        }
        await applyDrawerResponse(paymentPayload);
        localStorage.removeItem(terminalDraftKey);
        setNotice(`Commande #${selectedOrderForPayment.ticketNumber || selectedOrderForPayment.id} encaissée.`);
        setPaymentOpen(false);
        setSelectedOrderForPayment(null);
        await Promise.all([fetchOpenOrders(), fetchOrdersHistory()]);
        return;
      }

      const selectedCount = selectedCartItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      const cartCount = cart.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      const isPartialCartPayment = paymentTarget === 'cart' && selectedCount > 0 && selectedCount < cartCount;
      const cartItemsToPay = isPartialCartPayment ? selectedCartItems : cart;
      const cartTotalsToPay = isPartialCartPayment ? selectedCartTotals : totals;
      const paymentBody = {
        paymentMethod: paymentOptions.paymentMethod || paymentMethod,
        amountPaid: paymentOptions.amountPaid ?? cartTotalsToPay.total,
        paymentSplits: paymentOptions.paymentSplits,
        printProductionTickets: true,
      };
      const orderPayload = await createCurrentOrder({
        printProductionTickets: false,
        ticketHeaderText: paymentBody.paymentMethod === 'espece' ? 'PAYE ESPECE' : 'PAYE CB',
        cartItems: cartItemsToPay,
        totalsOverride: cartTotalsToPay,
        offlinePayment: stationCash ? undefined : paymentBody,
        requireOnline: stationCash,
        paymentDraftKind: 'cash',
      });
      if (!orderPayload) return;
      let queued = Boolean(orderPayload.offlineQueued);
      if (!queued) {
        let paymentResponse;
        let paymentPayload;
        try {
          if (!isOfflineScopeCurrent(offlineScope)) throw new Error('Compte modifié : rechargez la caisse.');
          paymentResponse = await fetchWithAuth(`${API_URL}/api/orders/${orderPayload.id}/payment`, {
            method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(paymentBody),
          });
          paymentPayload = await paymentResponse.json();
        } catch (networkError) {
          if (!isOfflineScopeCurrent(offlineScope)) throw networkError;
          if (stationCash) throw new Error('Connexion interrompue : paiement à vérifier sur le même ticket. Aucun envoi au tiroir ni synchronisation automatique.');
          pushOfflineAction({ path: `/api/orders/${orderPayload.id}/payment`, method: 'PATCH', body: paymentBody });
          queued = true;
        }
        if (!queued && !paymentResponse.ok) {
          if (pendingOrderRef.current) pendingOrderRef.current.paymentRefused = paymentResponse.status >= 400 && paymentResponse.status < 500;
          throw new Error(paymentPayload?.error || 'Encaissement impossible.');
        }
        if (!queued) await applyDrawerResponse(paymentPayload);
      }
      if (queued) setNotice('Connexion interrompue : encaissement conservé en file de synchronisation.');
      else setNotice(`Commande #${orderPayload.ticketNumber || orderPayload.id} encaissée.`);

      pendingOrderRef.current = null;
      localStorage.removeItem(terminalDraftKey);
      explicitSplitKeysRef.current.clear();
      if (isPartialCartPayment) {
        setCart((current) => current
          .map((item) => {
            const paidQty = Number(splitDraft[item.key] || 0);
            const remainingQty = Math.max(0, Number(item.quantity || 0) - paidQty);
            if (!paidQty) return item;
            if (!remainingQty) return null;
            return {
              ...item,
              quantity: remainingQty,
              totalPrice: computeLineTotal(item.unitPrice, remainingQty, item.lineDiscountPercent, item.isComped),
            };
          })
          .filter(Boolean));
        setPaymentOpen(false);
      } else {
        clearCurrentTicket();
      }
      await Promise.all([fetchOpenOrders(), fetchOrdersHistory()]);
    } catch (checkoutError) {
      setError(checkoutError.message || 'Encaissement impossible.');
    } finally {
      checkoutBusyRef.current = false;
      setCheckoutLoading(false);
    }
  };

  const renderProductCard = (product) => {
    const price = getProductPrice(product, orderMode);
    return (
      <article className="becaisse-product-card" key={product.id}>
        <button type="button" className="becaisse-product-hit" onClick={() => handleProductPress(product)}>
          <div className="becaisse-product-image">
            {product.image ? <img src={product.image} alt={product.name} /> : <div className="becaisse-image-fallback" />}
            {product.isVegetarian ? <span className="becaisse-product-badge">VÉGÉ</span> : null}
            <span className="becaisse-product-price">{formatCurrency(price)}</span>
          </div>
          <div className="becaisse-product-body">
            <strong>{product.name}</strong>
          </div>
        </button>
      </article>
    );
  };

  const renderMenuCard = (menu) => {
    const price = Number(menu.price || 0);
    return (
      <article className="becaisse-product-card is-menu" key={`menu-${menu.id}`}>
        <button type="button" className="becaisse-product-hit" onClick={() => openMenuComposer(menu)}>
          <div className="becaisse-product-image">
            {menu.image ? <img src={menu.image} alt={menu.name} /> : <div className="becaisse-image-fallback is-menu" />}
            <span className="becaisse-product-badge is-menu">FORMULE</span>
            <span className="becaisse-product-price">{formatCurrency(price)}</span>
          </div>
          <div className="becaisse-product-body">
            <strong>{menu.name}</strong>
            <small>{safeArray(menu.Sections).map((section) => section.name).join(' · ')}</small>
          </div>
        </button>
      </article>
    );
  };

  const productGrid = (
    <section className="becaisse-products">
      {loading ? (
        <div className="becaisse-empty">Chargement des produits...</div>
      ) : filteredProducts.length || filteredMenus.length ? (isPad ? [
        ...(
          filteredMenus.length ? [
            <div className="becaisse-subcategory-divider is-menu" key="menus-divider">
              <span>Formules</span>
            </div>,
            ...filteredMenus.map(renderMenuCard),
          ] : []
        ),
        ...groupedFilteredProducts.flatMap((group, groupIndex) => ([
        <div className={`becaisse-subcategory-divider is-tone-${groupIndex % 8}`} key={`subcat-${group.key}`}>
          <span>{group.name}</span>
        </div>,
        ...group.products.map(renderProductCard),
      ])),
      ] : [...filteredMenus.map(renderMenuCard), ...filteredProducts.map(renderProductCard)]) : (
        <div className="becaisse-empty">Aucun produit dans cette sélection.</div>
      )}
    </section>
  );

  const isPadSalesSplit = isPad && activeView === 'sale';

  const cartPanel = isPad && !cartOpen && !isPadSalesSplit ? (
    <aside className="becaisse-cart is-compact">
      <button type="button" className="becaisse-cart-summary" onClick={() => setCartOpen(true)}>
        <span>{totals.count}</span>
        <strong>{cart.length ? 'Voir panier' : 'Panier'}</strong>
        <b>{formatCurrency(totals.total)}</b>
      </button>
    </aside>
  ) : (
    <aside className={`becaisse-cart ${isPad ? 'is-open' : ''} ${isPadSalesSplit ? 'is-pad-split' : ''} ${isPadSalesSplit && padCartExpanded ? 'is-expanded' : ''}`}>
      {isPadSalesSplit ? (
        <button
          type="button"
          className="becaisse-cart-expand-toggle"
          onClick={() => setPadCartExpanded((current) => !current)}
          aria-label={padCartExpanded ? 'Réduire le panier' : 'Agrandir le panier'}
        >
          {padCartExpanded ? '-' : '+'}
        </button>
      ) : null}
      <div className="becaisse-cart-head">
        <div>
          {orderMode === 'sur_place' ? (
            <button type="button" className="becaisse-table-picker-trigger becaisse-table-chip" onClick={() => setTablePickerOpen(true)}>
              <span>🍽️</span>
              <strong>{tableNumber || '-'}</strong>
            </button>
          ) : (
            <span>Commande à emporter</span>
          )}
        </div>
        <div className="becaisse-cart-top-tools">
          <button type="button" className="becaisse-party" onClick={openCoversEditor}>
            <span>👥</span>
            <strong>{currentCovers || 2}</strong>
          </button>
          {orderMode === 'sur_place' && (!isPadSalesSplit || padCartExpanded) ? (
            <button type="button" className="becaisse-top-mini" onClick={openMergeModal}>Fusion</button>
          ) : null}
          {isPad && !isPadSalesSplit ? (
            <button type="button" className="becaisse-cart-close" onClick={() => setCartOpen(false)}>Fermer</button>
          ) : null}
        </div>
      </div>

      {!isPadSalesSplit ? (
        <div className="becaisse-cart-top-inline">
          <div className="becaisse-order-mode is-compact">
            <button type="button" className={orderMode === 'sur_place' ? 'is-active' : ''} onClick={() => setOrderMode('sur_place')}>Sur place</button>
            <button type="button" className={orderMode === 'a_emporter' ? 'is-active' : ''} onClick={() => setOrderMode('a_emporter')}>À emporter</button>
          </div>
          <div />
        </div>
      ) : null}
      <div className="becaisse-cart-scrollarea">
        <div className={`becaisse-cart-lines ${cart.length ? 'has-current-lines' : ''}`} ref={cartLinesRef}>
          {cart.length ? <strong className="becaisse-current-lines-title">À envoyer</strong> : null}
          {cart.length ? cart.map((item) => (
            <div
              className={`becaisse-cart-line ${item.complaint ? 'is-complaint' : item.followUp ? 'is-follow-up' : 'is-direct'}`}
              key={item.key}
              ref={(node) => {
                if (node && item.key === pendingCartScrollKeyRef.current) {
                  pendingCartScrollNodeRef.current = node;
                }
              }}
            >
              <button
                type="button"
                className="becaisse-line-remove"
                onClick={() => removeCartLine(item.key)}
                aria-label={`Supprimer ${item.name}`}
              >
                ×
              </button>
              <div className="becaisse-cart-line-main">
                <div className="becaisse-cart-line-head">
                  <button type="button" className="becaisse-qty-bubble" onClick={() => openQtyPad(item)}>
                    {item.quantity}
                  </button>
                  <div className="becaisse-cart-line-copy">
                    <strong>{item.name}</strong>
                    {stockChoiceSummary(item) && <small>{stockChoiceSummary(item)}</small>}
                    <span className="becaisse-cart-line-price">{formatCurrency(item.totalPrice)}</span>
                  </div>
                </div>
                {item.selectedSupplements?.length || item.lineDiscountPercent > 0 || item.isComped || item.complaint ? (
                  <small>
                    {[
                      safeArray(item.selectedSupplements).map((supplement) => supplement.name).join(', '),
                      item.complaint ? 'Réclamation' : '',
                      item.isComped ? 'Offert' : item.lineDiscountPercent > 0 ? `Remise ${item.lineDiscountPercent}%` : '',
                    ].filter(Boolean).join(' · ')}
                  </small>
                ) : null}
                {item.isMenu && safeArray(item.menuSelections).length ? (
                  <small className="becaisse-cart-menu-choices">
                    {safeArray(item.menuSelections).map((group) => (
                      <span key={`${item.key}-${group.sectionId}`}>
                        <b>{group.sectionName}</b>: {safeArray(group.products).map((product) => [product.name, stockChoiceSummary(product)].filter(Boolean).join(" — ")).join(', ')}
                      </span>
                    ))}
                  </small>
                ) : null}
                {item.note ? <p className="becaisse-line-note">✏️ {item.note}</p> : null}
                <div className="becaisse-line-quick-actions">
                  <button type="button" className={item.followUp ? 'is-active becaisse-followup-mini' : 'becaisse-followup-mini'} onClick={() => toggleLineFollowUp(item.key)}>A suivre</button>
                  <button
                    type="button"
                    className={item.note ? 'is-active becaisse-note-mini' : 'becaisse-note-mini'}
                    onClick={() => openLineNote(item)}
                    aria-label={`Ajouter une note à ${item.name}`}
                  >
                    ✏️
                  </button>
                </div>
                {isPadSalesSplit && !padCartExpanded ? null : (
                  <>
                    <div className="becaisse-qty-mini">
                      <button type="button" onClick={() => updateCartQty(item.key, -1)}>-</button>
                      <strong>{item.quantity}</strong>
                      <button type="button" onClick={() => updateCartQty(item.key, 1)}>+</button>
                    </div>
                    <div className="becaisse-line-actions">
                      <span className="becaisse-line-discount-label">%</span>
                      <select value={String(item.lineDiscountPercent || 0)} onChange={(event) => applyLineDiscount(item.key, Number(event.target.value || 0))}>
                        {Array.from({ length: 11 }).map((_, index) => index * 10).map((percent) => (
                          <option key={percent} value={percent}>{percent}%</option>
                        ))}
                      </select>
                      <button type="button" className={item.isComped ? 'is-active' : ''} onClick={() => toggleLineComp(item.key)} disabled={!canCompLine}>Offert</button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )) : (
            <p className="becaisse-empty-cart">Aucun article.</p>
          )}
        </div>

        {orderMode === 'sur_place' && visibleSentTableLines.length ? (
          <div className="becaisse-table-open-orders">
            <strong>Produits déjà envoyés cuisine</strong>
            {visibleSentTableLines.map((line) => {
              const isDone = ['ready', 'assembled', 'dispatched'].includes(line.workflowStatus) || ['prete', 'terminee'].includes(line.orderStatus);
              const stopAt = ['ready', 'assembled', 'dispatched'].includes(line.workflowStatus)
                ? (line.workflowUpdatedAt || line.orderReadyAt)
                : (['prete', 'terminee'].includes(line.orderStatus) ? line.orderReadyAt : null);
              const lineTimer = formatElapsedClock(line.lineCreatedAt || line.orderedAt, stopAt, timerNow);
              const elapsedSeconds = getElapsedSeconds(line.lineCreatedAt || line.orderedAt, stopAt, timerNow);
              const timerLimitEnabled = !!reservationConfigDraft.kdsItemTimerLimitEnabled;
              const timerLimitSeconds = Math.max(30, Number(reservationConfigDraft.kdsItemTimerLimitSeconds || 900));
              const isLate = !isDone && timerLimitEnabled && elapsedSeconds > timerLimitSeconds;
              const lineVariant = line.complaint ? 'complaint' : line.followUp ? 'follow-up' : 'direct';
              return (
                <div key={line.id} className={`becaisse-sent-line-card is-${lineVariant}`}>
                  <button
                    type="button"
                    className="becaisse-line-remove is-danger"
                    onClick={() => openSentLineCancel(line)}
                    aria-label={`Annuler ${line.name}`}
                  >
                    ×
                  </button>
                  <div className="becaisse-sent-line-card-head">
                    <span>{line.quantity} x {line.name}</span>
                    <b>{formatCurrency(line.totalPrice)}</b>
                  </div>
                  <small>
                    Ticket #{line.ticketNumber} · <span className="becaisse-sent-badge">Envoyé cuisine</span>
                    {line.followUp ? <span className="becaisse-line-mini-badge is-follow-up">A suivre</span> : null}
                    {line.complaint ? <span className="becaisse-line-mini-badge is-complaint">Réclamé</span> : null}
                    {!line.followUp && !line.complaint ? <span className="becaisse-line-mini-badge is-direct">Direct</span> : null}
                    <span className={`becaisse-line-mini-badge ${isDone ? 'is-ready' : isLate ? 'is-timer-late' : 'is-timer'}`}>{isDone ? `Prêt · ${lineTimer}` : lineTimer}</span>
                  </small>
                  {(line.followUp && !line.complaint && !reclaimedSentLineIds.includes(String(line.id))) ? (
                    <button type="button" className="becaisse-top-mini" onClick={() => reclaimSentLine(line)}>
                      Réclamer
                    </button>
                  ) : null}
                </div>
              );
            })}
            {reclaimableSentLines.length ? (
              <button type="button" className="becaisse-top-action" onClick={reclaimAllSentLines}>
                Tout réclamer
              </button>
            ) : null}
          </div>
        ) : null}

        {orderMode === 'sur_place' && tableOrders.length ? (
          <div className="becaisse-table-open-orders">
            <strong>Déjà envoyé table {tableNumber}</strong>
            {tableOrders.map((order) => (
              <div key={order.id} className="becaisse-table-order-row">
                <label className="becaisse-order-select">
                  <input
                    type="checkbox"
                    aria-label={`Sélectionner le ticket #${order.ticketNumber || order.id}`}
                    checked={selectedTableOrderIds.includes(Number(order.id))}
                    onChange={() => toggleTableOrderSelection(order.id)}
                  />
                </label>
                <span>#{order.ticketNumber || order.id}</span>
                <small>{getOrderItems(order).reduce((sum, item) => sum + Number(item.quantity || 0), 0)} art.</small>
                <b>{formatCurrency(order.totalTtc)}</b>
              </div>
            ))}
          </div>
        ) : null}

        {orderMode === 'sur_place' && tableOrders.length ? (
          <div className="becaisse-discount-control">
            <div className="becaisse-discount-row">
              <select value={transferTargetTable} onChange={(event) => setTransferTargetTable(event.target.value)}>
                <option value="">Transférer vers...</option>
                {tableSettings
                  .filter((table) => String(table) !== String(tableNumber))
                  .map((table) => (
                    <option key={table} value={table}>{table}</option>
                  ))}
              </select>
              <button type="button" onClick={transferCurrentTableOrders} disabled={checkoutLoading || !transferTargetTable}>
                Transférer
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {befidCustomer ? <div className="befid-device-customer">
        <strong>Client : {befidCustomer.firstName || 'Client BEFID'}</strong>
        <p>Fiche liée à ce nouveau ticket. Les prix et remises restent inchangés.</p>
        <button type="button" disabled={checkoutLoading || !!pendingOrderRef.current} onClick={() => { setBefidCustomer(null); setBefidTicketGeneration((value) => value + 1); }}>Retirer le client</button>
      </div> : <BefidDeviceCustomer key={`${offlineScope}:${befidTicketGeneration}`} channel="caisse" fetcher={fetchWithAuth}
        disabled={checkoutLoading || !!pendingOrderRef.current || !isOnline || !isOfflineScopeCurrent(offlineScope) || (orderMode === 'sur_place' && tableOrders.length > 0)}
        onCustomer={(customer) => { if (!pendingOrderRef.current && isOfflineScopeCurrent(offlineScope)) { befidCustomerScope.current = getCustomerOperatorScope(); setBefidCustomer(customer); } }} />}
      <div className="becaisse-totals">
        {isPadSalesSplit && !padCartExpanded ? null : <div><span>Sous-total</span><strong>{formatCurrency(displayedTotals.subtotal)}</strong></div>}
        {isPadSalesSplit && !padCartExpanded ? null : <div><span>Remise</span><strong>- {formatCurrency(displayedTotals.discountAmount)}</strong></div>}
        <div className={`is-total ${isPadSalesSplit && !padCartExpanded ? 'is-inline' : ''}`}>
          <span>{isPadSalesSplit && !padCartExpanded ? 'Total :' : 'Total'}</span>
          <strong>{formatCurrency(displayedTotals.total)}</strong>
        </div>
      </div>
      <div className="becaisse-actions-stack is-inline">
        <button type="button" className="becaisse-send-kitchen" onClick={sendKitchen} disabled={!cart.length || checkoutLoading}>
          <span>{isPadSalesSplit ? 'Envoyer' : 'Envoyer'}</span>
        </button>
        <button type="button" className="becaisse-top-mini" onClick={printClientTicketFromCart} disabled={!cart.length || checkoutLoading}>
          {isPadSalesSplit ? '🖨️' : 'Imprimer ticket'}
        </button>
        <button type="button" className="becaisse-checkout" onClick={openPayment} disabled={(!cart.length && (selectedTableDueTotal || tableDueTotal) <= 0) || checkoutLoading}>
          <span>{isPadSalesSplit ? '💶' : 'Encaisser'}</span>
        </button>
      </div>
    </aside>
  );

  const mainContent = activeView === 'tables' ? (
    <div className="becaisse-table-board">
      <div className="becaisse-section-title">
        <span>Plan de salle</span>
        <h1>Tables</h1>
      </div>
      <div className="becaisse-table-view-switch">
        <strong>Affichage</strong>
        <div className="becaisse-table-view-toggle">
          <button type="button" className={tableViewMode === 'plan' ? 'is-active' : ''} onClick={() => setTableViewMode('plan')}>
            🗺️ Vue plan
          </button>
          <button type="button" className={tableViewMode === 'list' ? 'is-active' : ''} onClick={() => setTableViewMode('list')}>
            📋 Vue liste
          </button>
        </div>
      </div>
      {tableViewMode === 'list' ? (
        <div className="becaisse-tables-grid">
          {tableCards.map((table) => (
            <button
              key={table.number}
              type="button"
              className={`becaisse-table-card ${String(tableNumber) === String(table.number) ? 'is-active' : ''} ${table.total > 0 ? 'has-order' : ''}`}
              onClick={() => selectTable(table.number)}
            >
              <span>Table {table.number}</span>
              <strong>{formatCurrency(table.total)}</strong>
              <small>{table.itemsCount ? `${table.itemsCount} article(s)` : 'Libre'}</small>
              <small className={`becaisse-table-status is-${table.status}`}>{table.status === 'servie' ? 'Servie' : table.status === 'ouverte' ? 'Ouverte' : 'Libre'}</small>
              {table.openedAt ? <small>Durée: {formatElapsed(table.openedAt)}</small> : null}
            </button>
          ))}
        </div>
      ) : (
        <div className="becaisse-room-layout" style={{ gridTemplateColumns: `repeat(${tableLayout.cols}, minmax(0, 1fr))` }}>
          {safeArray(tableLayout.cells).map((cellValue, index) => {
            const value = String(cellValue || '').trim();
            if (!value) {
              return <div key={`empty-${index}`} className="becaisse-room-cell is-empty">Vide</div>;
            }
            const table = tableCardsMap.get(value) || {
              number: value,
              total: 0,
              status: 'libre',
              itemsCount: 0,
              openedAt: null,
            };
            return (
              <button
                key={`cell-${index}-${value}`}
                type="button"
                className={`becaisse-room-cell ${String(tableNumber) === String(table.number) ? 'is-active' : ''} ${table.total > 0 ? 'has-order' : ''}`}
                onClick={() => selectTable(table.number)}
              >
                <span>Table {table.number}</span>
                <strong>{formatCurrency(table.total)}</strong>
                <small className={`becaisse-table-status is-${table.status}`}>{table.status === 'servie' ? 'Servie' : table.status === 'ouverte' ? 'Ouverte' : 'Libre'}</small>
                {table.openedAt ? <small>Durée: {formatElapsed(table.openedAt)}</small> : <small>{table.itemsCount ? `${table.itemsCount} article(s)` : 'Libre'}</small>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  ) : activeView === 'commandes' ? (
    <div className="becaisse-orders-view">
      <div className="becaisse-orders-toolbar">
        {[
          { id: 'all', label: 'Toutes' },
          { id: 'unpaid', label: 'À encaisser' },
          { id: 'en_cours', label: 'En cours' },
          { id: 'upcoming', label: 'À venir' },
          { id: 'termine', label: 'Terminées' },
          { id: 'annule', label: 'Annulées' },
        ].map((filter) => (
          <button
            key={filter.id}
            type="button"
            className={ordersFilter === filter.id ? 'is-active' : ''}
            onClick={() => setOrdersFilter(filter.id)}
          >
            {filter.label}
          </button>
        ))}
        <button type="button" className="becaisse-secondary-wide" onClick={exportOrdersCsv}>
          Export CSV
        </button>
        <label className="becaisse-orders-date-filter">
          <span>Du</span>
          <input type="date" value={ordersDateFrom} onChange={(event) => setOrdersDateFrom(event.target.value)} />
        </label>
        <label className="becaisse-orders-date-filter">
          <span>Au</span>
          <input type="date" value={ordersDateTo} onChange={(event) => setOrdersDateTo(event.target.value)} />
        </label>
        <button
          type="button"
          className="becaisse-top-action"
          onClick={() => {
            const today = new Date().toISOString().slice(0, 10);
            setOrdersDateFrom(today);
            setOrdersDateTo(today);
          }}
        >
          Aujourd'hui
        </button>
      </div>
      <div className="becaisse-orders-layout">
        <section className="becaisse-orders-list">
          {ordersLoading ? (
            <div className="becaisse-empty">Chargement commandes...</div>
          ) : filteredOrdersHistory.length ? filteredOrdersHistory.map((order) => {
            const isPaid = String(order.paymentStatus || '').toLowerCase() === 'paid';
            return (
              <button
                key={order.id}
                type="button"
                className={`becaisse-order-row ${Number(selectedOrderId) === Number(order.id) ? 'is-active' : ''}`}
                onClick={() => setSelectedOrderId(order.id)}
              >
                <div>
                  <strong>#{order.ticketNumber || order.id}</strong>
                  <small>{formatDateTime(order.orderedAt || order.createdAt)}</small>{scheduleLabel(order) && <small>{scheduleLabel(order)}</small>}
                  {order.fulfillmentType === 'delivery' ? <small>Uber Direct · {String(order.uberDirectStatus || 'en attente').replaceAll('_', ' ')}</small> : null}
                </div>
                <div>
                  <span>{isPaid ? 'Payée' : 'À encaisser'}</span>
                  <b>{formatCurrency(order.totalTtc)}</b>
                </div>
              </button>
            );
          }) : (
            <div className="becaisse-empty">Aucune commande.</div>
          )}
        </section>

        <section className="becaisse-orders-detail">
          {selectedOrder ? (
            <>
              <header className="becaisse-orders-detail-head">
                <div>
                  <span>Commande</span>
                  <h2>#{selectedOrder.ticketNumber || selectedOrder.id}</h2>
                </div>
                <div className="becaisse-orders-tags">
                  <span>{String(selectedOrder.status || '-').replace('_', ' ')}</span>
                  {scheduleLabel(selectedOrder) && <strong>{scheduleLabel(selectedOrder)}</strong>}
                  <span>{String(selectedOrder.paymentStatus || '-')}</span>
                </div>
              </header>

              <div className="becaisse-orders-meta">
                <div><span>Date</span><strong>{formatDateTime(selectedOrder.orderedAt || selectedOrder.createdAt)}</strong></div>
                <div><span>Total</span><strong>{formatCurrency(selectedOrder.totalTtc)}</strong></div>
                {selectedOrder.fulfillmentType === 'delivery' ? <div><span>Livraison Uber Direct</span><strong>{String(selectedOrder.uberDirectStatus || 'en attente').replaceAll('_', ' ')} · {formatCurrency(selectedOrder.deliveryFee)}</strong></div> : null}
              </div>

              {selectedOrder.fulfillmentType === 'delivery' && selectedOrder.uberDirectTrackingUrl ? <a className="becaisse-top-action" href={selectedOrder.uberDirectTrackingUrl} target="_blank" rel="noreferrer">Suivre le coursier</a> : null}

              <div className="becaisse-orders-items">
                {getOrderItems(selectedOrder).map((item) => (
                  <div key={item.id || `${item.name}-${item.quantity}`} className="becaisse-orders-item">
                    <span>{item.quantity} x {item.kitchenName || item.name}</span>
                    <b>{formatCurrency(item.totalPrice)}</b>
                    {String(selectedOrder.paymentStatus || '').toLowerCase() !== 'paid' ? (
                      <div className="becaisse-order-line-tools">
                        <div className="becaisse-qty-mini">
                          <button type="button" aria-label={`Retirer du ticket séparé : ${item.name}`} onClick={() => updateSplitQty(item.id, Number(splitLinesDraft[item.id] || 0) - 1)}>-</button>
                          <strong>{Number(splitLinesDraft[item.id] || 0)}</strong>
                          <button type="button" aria-label={`Ajouter au ticket séparé : ${item.name}`} onClick={() => updateSplitQty(item.id, Number(splitLinesDraft[item.id] || 0) + 1)}>+</button>
                        </div>
                        <div className="becaisse-order-cancel-line">
                          <div className="becaisse-qty-mini">
                            <button
                              type="button"
                              onClick={() => updateCancelDraft(item.id, { quantity: Number((cancelLinesDraft[item.id]?.quantity || 1)) - 1 }, Number(item.quantity || 1))}
                            >
                              -
                            </button>
                            <strong>{Number(cancelLinesDraft[item.id]?.quantity || 1)}</strong>
                            <button
                              type="button"
                              onClick={() => updateCancelDraft(item.id, { quantity: Number((cancelLinesDraft[item.id]?.quantity || 1)) + 1 }, Number(item.quantity || 1))}
                            >
                              +
                            </button>
                          </div>
                          <input
                            type="text"
                            value={cancelLinesDraft[item.id]?.reason || ''}
                            onChange={(event) => updateCancelDraft(item.id, { reason: event.target.value }, Number(item.quantity || 1))}
                            placeholder="Motif annulation"
                          />
                          <button
                            type="button"
                            className="becaisse-top-action"
                            disabled={checkoutLoading || !canManageSensitiveOrderActions}
                            onClick={() => cancelOrderItem(selectedOrder.id, item)}
                          >
                            Annuler ligne
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>

              <div className="becaisse-orders-audit">
                <h3>Journal commande</h3>
                <div className="becaisse-orders-audit-filters">
                  {[
                    { id: 'all', label: 'Tout' },
                    { id: 'cancel', label: 'Annulations' },
                    { id: 'reopen', label: 'Réouvertures' },
                    { id: 'payment', label: 'Paiements' },
                  ].map((filter) => (
                    <button
                      key={filter.id}
                      type="button"
                      className={orderAuditFilter === filter.id ? 'is-active' : ''}
                      onClick={() => setOrderAuditFilter(filter.id)}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
                {orderAuditLoading ? (
                  <p className="becaisse-empty-cart">Chargement...</p>
                ) : filteredOrderAuditLogs.length ? (
                  filteredOrderAuditLogs.map((log) => (
                    <div key={log.id || `${log.action}-${log.occurredAt}`} className="becaisse-orders-audit-row">
                      <strong>{formatAuditAction(log.action)}</strong>
                      <small>{formatDateTime(log.occurredAt)} · {log.actorType}:{log.actorId}</small>
                      {log.details?.reason ? <span>Motif: {log.details.reason}</span> : null}
                    </div>
                  ))
                ) : (
                  <p className="becaisse-empty-cart">Aucun événement.</p>
                )}
              </div>

              <div className="becaisse-orders-actions becaisse-orders-actions-sticky">
                <button
                  type="button"
                  className="becaisse-checkout"
                  disabled={checkoutLoading || String(selectedOrder.paymentStatus || '').toLowerCase() === 'paid'}
                  onClick={() => openOrderPayment(selectedOrder)}
                >
                  <span>Encaisser</span>
                  <strong>{formatCurrency(selectedOrder.totalTtc)}</strong>
                </button>
                <button
                  type="button"
                  className="becaisse-send-kitchen"
                  disabled={checkoutLoading || isAwaitingPreparation(selectedOrder) || String(selectedOrder.status || '').toLowerCase() === 'termine'}
                  onClick={() => updateOrderStatus(selectedOrder.id, 'termine')}
                >
                  <span>Marquer terminée</span>
                  <strong>OK</strong>
                </button>
                <button
                  type="button"
                  className="becaisse-top-action"
                  disabled={checkoutLoading || !canManageSensitiveOrderActions || String(selectedOrder.status || '').toLowerCase() === 'annule'}
                  onClick={() => {
                    setCancelReasonValue('');
                    setCancelModalOpen(true);
                  }}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="becaisse-top-action"
                  disabled={checkoutLoading || !canRefundOrder || String(selectedOrder.paymentStatus || '').toLowerCase() !== 'paid'}
                  onClick={() => setRefundDraft({ order: selectedOrder, amountTtc: Math.max(0, Number(selectedOrder.totalTtc || 0) - Number(selectedOrder.refundedTtc || 0)).toFixed(2), reason: '', paymentMethod: selectedOrder.paymentMethod === 'mixte' ? '' : selectedOrder.paymentMethod || '' })}
                >
                  Rembourser
                </button>
                <button
                  type="button"
                  className="becaisse-top-action"
                  disabled={checkoutLoading || !canSplitOrder || String(selectedOrder.paymentStatus || '').toLowerCase() === 'paid'}
                  onClick={splitSelectedOrder}
                >
                  Split ticket
                </button>
                <button
                  type="button"
                  className="becaisse-top-action"
                  disabled={checkoutLoading || !canManageSensitiveOrderActions || String(selectedOrder.paymentStatus || '').toLowerCase() === 'paid' || String(selectedOrder.status || '').toLowerCase() === 'en_cours'}
                  onClick={reopenSelectedOrder}
                >
                  Réouvrir ticket
                </button>
                <button
                  type="button"
                  className="becaisse-top-action"
                  disabled={checkoutLoading}
                  onClick={() => printSelectedOrderTicket('prebill')}
                >
                  Pré-note
                </button>
                <button
                  type="button"
                  className="becaisse-top-action"
                  disabled={checkoutLoading}
                  onClick={() => printSelectedOrderTicket('final')}
                >
                  Note finale
                </button>
              </div>
            </>
          ) : (
            <div className="becaisse-empty">Sélectionne une commande.</div>
          )}
        </section>
      </div>
    </div>
  ) : activeView === 'reports' || activeView === 'control' ? (
    <div className="becaisse-control-layout">
      <div className="becaisse-settings-panel">
        <div className="becaisse-section-title">
          <span>Pilotage caisse</span>
          <h1>Contrôle, rapports & session</h1>
        </div>

      <section className="becaisse-settings-card">
        <h2>Récap journalier (X)</h2>
        <div className="becaisse-discount-row">
          <input type="date" value={reportDate} onChange={(event) => setReportDate(event.target.value)} />
          <button type="button" onClick={() => fetchDailySummary(reportDate)} disabled={dailySummaryLoading}>
            {dailySummaryLoading ? 'Chargement...' : 'Rafraîchir'}
          </button>
          <button type="button" onClick={exportDailyOrdersCsv}>
            Export jour CSV
          </button>
        </div>
        <button
          type="button"
          className="becaisse-checkout"
          onClick={printZReport}
          disabled={dailySummaryPrinting || dailySummaryLoading || !canPrintDailyZ}
        >
          <span>{dailySummaryPrinting ? 'Impression Z...' : 'Imprimer clôture Z'}</span>
          <strong>{reportDate}</strong>
        </button>
        {dailySummary ? (
          <div className="becaisse-totals">
            <div><span>Total TTC</span><strong>{formatCurrency(dailySummary.totalTtc)}</strong></div>
            <div><span>Total CB</span><strong>{formatCurrency(dailySummary.totalCard)}</strong></div>
            <div><span>Total Espèces</span><strong>{formatCurrency(dailySummary.totalCash)}</strong></div>
            <div className="is-total"><span>Tickets</span><strong>{Number(dailySummary.ticketCount || 0)}</strong></div>
          </div>
        ) : <p className="becaisse-empty">Aucune donnée.</p>}
      </section>

      <section className="becaisse-settings-card">
        <h2>Session de caisse</h2>
        <p>Une seule session ouverte à la fois. Ouvre au début de service, clôture en fin de service.</p>
        {openCashSession ? (
          <>
            <div className="becaisse-totals">
              <div><span>Ouverte le</span><strong>{formatDateTime(openCashSession.openedAt)}</strong></div>
              <div><span>Fond ouverture</span><strong>{formatCurrency(openCashSession.openingFloat)}</strong></div>
            </div>
            <div className="becaisse-discount-row">
              <input
                value={countedCashInput}
                onChange={(event) => setCountedCashInput(event.target.value)}
                placeholder="Espèces comptées en fin de service"
                inputMode="decimal"
              />
              <button type="button" onClick={closeCashSessionHandler} disabled={checkoutLoading || !canOpenCloseCashSession}>
                {checkoutLoading ? 'Clôture...' : 'Clôturer'}
              </button>
            </div>
          </>
        ) : (
          <div className="becaisse-discount-row">
            <input
              value={openingFloatInput}
              onChange={(event) => setOpeningFloatInput(event.target.value)}
              placeholder="Fond de caisse d’ouverture"
              inputMode="decimal"
            />
            <button type="button" onClick={openCashSessionHandler} disabled={checkoutLoading || !canOpenCloseCashSession}>
              {checkoutLoading ? 'Ouverture...' : 'Ouvrir session'}
            </button>
          </div>
        )}
        <div className="becaisse-table-open-orders">
          <strong>Historique sessions</strong>
          {cashSessionsLoading ? (
            <p className="becaisse-empty-cart">Chargement...</p>
          ) : cashSessions.length ? cashSessions.slice(0, 6).map((session) => (
            <div key={session.id} className="becaisse-table-order-row">
              <span>{String(session.status || '').toLowerCase() === 'open' ? 'Ouverte' : 'Clôturée'}</span>
              <small>{formatDateTime(session.openedAt)}</small>
              <b>{formatCurrency(session.expectedCashTotal ?? session.openingFloat)}</b>
            </div>
          )) : (
            <p className="becaisse-empty-cart">Aucune session.</p>
          )}
        </div>
      </section>

      <section className="becaisse-settings-card">
        <h2>État instantané</h2>
        <div className="becaisse-totals">
          <div><span>Réseau</span><strong>{isOnline ? 'En ligne' : 'Hors ligne'}</strong></div>
          <div><span>Queue offline</span><strong>{offlineQueue.length}</strong></div>
          <div><span>Conflits offline</span><strong>{offlineConflicts.length}</strong></div>
          <div><span>Session caisse</span><strong>{openCashSession ? 'Ouverte' : 'Fermée'}</strong></div>
        </div>
        <div className="becaisse-discount-row">
          <button type="button" onClick={replayOfflineQueue} disabled={!isOnline || syncRunning || !offlineQueue.length}>
            {syncRunning ? 'Resync...' : 'Synchroniser offline'}
          </button>
          <button type="button" onClick={() => { fetchOpenOrders(); fetchOrdersHistory(); fetchCashSessions(); fetchControlSummary(new Date().toISOString().slice(0, 10)); }}>
            Rafraîchir données
          </button>
        </div>
      </section>
      <section className="becaisse-settings-card">
        <h2>Exports fin de service</h2>
        <div className="becaisse-discount-row">
          <button type="button" onClick={exportControlPdf}>Télécharger PDF contrôle</button>
          <button type="button" onClick={exportDailyOrdersCsv}>Télécharger CSV jour</button>
          <button type="button" onClick={exportAccountingCsv}>Télécharger CSV comptable</button>
        </div>
      </section>

      <section className="becaisse-settings-card">
        <h2>Performance du jour</h2>
        <div className="becaisse-totals">
          <div><span>Commandes jour</span><strong>{controlStats.todayOrdersCount}</strong></div>
          <div><span>Payées</span><strong>{controlStats.todayPaidCount}</strong></div>
          <div><span>Annulées</span><strong>{controlStats.todayCancelledCount}</strong></div>
          <div><span>Panier moyen</span><strong>{formatCurrency(controlStats.avgTicket)}</strong></div>
          <div><span>CB</span><strong>{formatCurrency(controlStats.cardToday)}</strong></div>
          <div><span>Espèces</span><strong>{formatCurrency(controlStats.cashToday)}</strong></div>
          <div><span>Mixte</span><strong>{formatCurrency(controlStats.mixteToday)}</strong></div>
          <div><span>Remboursé</span><strong>{formatCurrency(controlStats.refundedToday)}</strong></div>
        </div>
        {controlSummaryLoading ? <p className="becaisse-empty-cart">Chargement...</p> : null}
      </section>

      <section className="becaisse-settings-card">
        <h2>Alerte caisse</h2>
        <div className="becaisse-table-open-orders">
          {!openCashSession && (controlStats.cashToday > 0 || controlStats.mixteToday > 0) ? (
            <div className="becaisse-table-order-row">
              <span>Encaissement espèces/mixte détecté sans session ouverte</span>
              <small>Action: ouvrir une session avant prochain service</small>
              <b>Warning</b>
            </div>
          ) : null}
          {controlStats.lastClosedSession ? (
            <div className="becaisse-table-order-row">
              <span>Dernière clôture</span>
              <small>{formatDateTime(controlStats.lastClosedSession.closedAt)}</small>
              <b>Écart: {formatCurrency(controlStats.lastClosedSession.differenceAmount || 0)}</b>
            </div>
          ) : (
            <p className="becaisse-empty-cart">Aucune clôture enregistrée.</p>
          )}
        </div>
      </section>
      <section className="becaisse-settings-card">
        <h2>Remboursements récents</h2>
        <div className="becaisse-table-open-orders">
          {safeArray(controlStats.recentRefunds).length ? safeArray(controlStats.recentRefunds).map((refund) => (
            <div key={refund.id} className="becaisse-table-order-row">
              <span>Ticket #{refund.ticketNumber || refund.id}</span>
              <small>{formatDateTime(refund.lastRefundedAt)}</small>
              <b>{formatCurrency(refund.refundedTtc || 0)}</b>
            </div>
          )) : <p className="becaisse-empty-cart">Aucun remboursement.</p>}
        </div>
      </section>
      <section className="becaisse-settings-card">
        <h2>Conflits offline (résolution)</h2>
        <div className="becaisse-table-open-orders">
          {offlineConflicts.length ? offlineConflicts.slice(0, 10).map((entry) => (
            <div key={`${entry.at}-${entry.actionId}`} className="becaisse-table-order-row">
              <span>{entry.method || '-'} {entry.path || '-'}</span>
              <small>{entry.reason || '-'}</small>
              <div className="becaisse-order-cancel-line">
                <button type="button" className="becaisse-top-action" onClick={() => retryOfflineConflict(entry)}>Rejouer</button>
                <button type="button" className="becaisse-top-action" onClick={() => discardOfflineConflict(entry)}>Abandonner</button>
              </div>
            </div>
          )) : <p className="becaisse-empty-cart">Aucun conflit.</p>}
        </div>
      </section>
        <section className="becaisse-settings-card">
          <h2>Historique synchro offline</h2>
          <div className="becaisse-table-open-orders">
            {offlineHistory.length ? offlineHistory.slice(0, 12).map((entry) => (
              <div key={`${entry.at}-${entry.actionId}-${entry.status}`} className="becaisse-table-order-row">
                <span>{entry.status || '-'} · {entry.method || '-'} {entry.path || '-'}</span>
                <small>{entry.reason || '-'}</small>
                <b>{formatDateTime(entry.at)}</b>
              </div>
            )) : <p className="becaisse-empty-cart">Aucun historique.</p>}
          </div>
        </section>
      </div>
      <aside className="becaisse-control-side">
        <section className="becaisse-settings-card">
          <h2>Indicateurs rapides</h2>
          <div className="becaisse-kpi-grid">
            <div className="becaisse-kpi-tile"><span>Couverts jour</span><strong>{Number(controlStats.couvertCount || 0)}</strong></div>
            <div className="becaisse-kpi-tile"><span>À emporter</span><strong>{Number(controlStats.takeAwayCount || 0)}</strong></div>
            <div className="becaisse-kpi-tile"><span>Sur place</span><strong>{Number(controlStats.surPlaceCount || 0)}</strong></div>
            <div className="becaisse-kpi-tile"><span>Tables ouvertes</span><strong>{Math.max(Number(controlStats.openTablesCount || 0), Number(liveOpenTablesCount || 0))}</strong></div>
          </div>
        </section>
        <section className="becaisse-settings-card">
          <h2>Paiements du jour</h2>
          <div className="becaisse-totals">
            <div><span>Total TTC</span><strong>{formatCurrency(controlStats.totalTtc)}</strong></div>
            <div><span>Total HT</span><strong>{formatCurrency(controlStats.totalHt)}</strong></div>
            <div><span>TVA collectée</span><strong>{formatCurrency(controlStats.totalVatCollected)}</strong></div>
            <div><span>Avoirs générés</span><strong>{formatCurrency(controlStats.avoirsGenerated)}</strong></div>
          </div>
          <div className="becaisse-table-open-orders">
            <strong>Détail TVA</strong>
            {safeArray(controlStats.vatBreakdown).length ? safeArray(controlStats.vatBreakdown).map((row) => (
              <div key={`vat-${row.rate}`} className="becaisse-table-order-row">
                <span>TVA {Number(row.rate).toFixed(2)}%</span>
                <small>HT {formatCurrency(row.ht)}</small>
                <b>{formatCurrency(row.vat)}</b>
              </div>
            )) : <p className="becaisse-empty-cart">Aucun détail TVA.</p>}
          </div>
        </section>

        <section className="becaisse-settings-card">
          <h2>Modes de paiement</h2>
          <div className="becaisse-table-open-orders">
            {safeArray(controlStats.paymentMethodStats).length ? safeArray(controlStats.paymentMethodStats).map((row) => (
              <div key={`pm-${row.id}`} className="becaisse-table-order-row">
                <span>{row.label}</span>
                <small>{row.count} paiement(s)</small>
                <b>{formatCurrency(row.total)}</b>
              </div>
            )) : <p className="becaisse-empty-cart">Aucun paiement.</p>}
          </div>
        </section>

        <section className="becaisse-settings-card">
          <h2>Compte client</h2>
          <div className="becaisse-totals">
            <div><span>Accordé ce jour</span><strong>{formatCurrency(controlStats.clientAccount?.grantedToday)}</strong></div>
            <div><span>Reçu ce jour</span><strong>{formatCurrency(controlStats.clientAccount?.receivedToday)}</strong></div>
            <div><span>Balance générale</span><strong>{formatCurrency(controlStats.clientAccount?.globalBalance)}</strong></div>
          </div>
        </section>

        <section className="becaisse-settings-card">
          <h2>Total produits vendus</h2>
          <div className="becaisse-table-open-orders">
            {safeArray(controlStats.productsSummary).length ? safeArray(controlStats.productsSummary).map((row) => (
              <div key={`product-${row.name}`} className="becaisse-table-order-row">
                <span>{row.name}</span>
                <small>Qté {row.quantity}</small>
                <b>{formatCurrency(row.totalEur)}</b>
              </div>
            )) : <p className="becaisse-empty-cart">Aucune vente produit.</p>}
          </div>
        </section>
      </aside>
    </div>
  ) : activeView === 'settings' ? (
    <div className="becaisse-settings-panel">
      <div className="becaisse-section-title">
        <span>Paramètres caisse</span>
        <h1>Tables</h1>
      </div>
      <section className="becaisse-settings-card">
        <h2>Numéros de tables</h2>
        <p>Entre les tables séparées par une virgule ou une ligne. Exemple: 1, 2, 3, Terrasse 1.</p>
        <textarea value={tablesDraft} onChange={(event) => setTablesDraft(event.target.value)} />
        <button type="button" className="becaisse-checkout" onClick={saveTables}>
          <span>Enregistrer les tables</span>
          <strong>{tableSettings.length}</strong>
        </button>
      </section>
      <section className="becaisse-settings-card">
        <h2>Plan de salle (grille)</h2>
        <p>Définis la taille de salle, puis renseigne un numéro de table par case. Laisse vide pour représenter un espace libre.</p>
        <div className="becaisse-discount-row">
          <input
            type="number"
            min={1}
            max={20}
            value={tableLayoutDraft.rows}
            onChange={(event) => setTableLayoutDraftSize(event.target.value, tableLayoutDraft.cols)}
            placeholder="Lignes"
          />
          <input
            type="number"
            min={1}
            max={20}
            value={tableLayoutDraft.cols}
            onChange={(event) => setTableLayoutDraftSize(tableLayoutDraft.rows, event.target.value)}
            placeholder="Colonnes"
          />
          <button type="button" onClick={saveTableLayout}>Enregistrer plan</button>
        </div>
        <div className="becaisse-plan-editor-grid" style={{ gridTemplateColumns: `repeat(${tableLayoutDraft.cols}, minmax(0, 1fr))` }}>
          {safeArray(tableLayoutDraft.cells).map((cell, index) => (
            <input
              key={`draft-cell-${index}`}
              value={cell}
              onChange={(event) => updateTableLayoutDraftCell(index, event.target.value)}
              placeholder="Vide"
            />
          ))}
        </div>
      </section>
      <section className="becaisse-settings-card">
        <h2>Sécurité opérateur</h2>
        <p>Définis un code PIN pour verrouiller la caisse et une temporisation d’auto-verrouillage.</p>
        <div className="becaisse-discount-row">
          <input
            type="password"
            inputMode="numeric"
            value={pinDraft}
            onChange={(event) => setPinDraft(String(event.target.value || '').replace(/\D/g, '').slice(0, 8))}
            placeholder="PIN (4 à 8 chiffres)"
          />
          <input
            type="number"
            min={1}
            max={120}
            value={autoLockMinutes}
            onChange={(event) => setAutoLockMinutes(Math.max(1, Math.min(120, parseInt(event.target.value, 10) || 15)))}
            placeholder="Minutes"
          />
          <button type="button" onClick={saveSecuritySettings}>Enregistrer</button>
        </div>
        <button type="button" className="becaisse-top-action" onClick={lockNow} disabled={!pinCode}>
          Verrouiller maintenant
        </button>
      </section>
      <section className="becaisse-settings-card">
        <h2>Synchronisation hors ligne</h2>
        <div className="becaisse-totals">
          <div><span>Réseau</span><strong>{isOnline ? 'En ligne' : 'Hors ligne'}</strong></div>
          <div><span>Actions en file</span><strong>{offlineQueue.length}</strong></div>
          <div><span>Conflits</span><strong>{offlineConflicts.length}</strong></div>
        </div>
        <div className="becaisse-discount-row">
          <button type="button" onClick={replayOfflineQueue} disabled={!isOnline || syncRunning || !offlineQueue.length}>
            {syncRunning ? 'Resync...' : 'Relancer synchro'}
          </button>
          <button
            type="button"
            onClick={() => {
              setOfflineConflicts([]);
              saveJsonArrayStorage(conflictsKey, []);
            }}
            disabled={!offlineConflicts.length}
          >
            Vider conflits
          </button>
        </div>
        <div className="becaisse-table-open-orders">
          <strong>Derniers conflits</strong>
          {offlineConflicts.length ? offlineConflicts.slice(0, 6).map((entry) => (
            <div key={`${entry.at}-${entry.actionId}`} className="becaisse-table-order-row">
              <span>{entry.method} {entry.path}</span>
              <small>{entry.reason || '-'}</small>
              <b>{formatDateTime(entry.at)}</b>
            </div>
          )) : <p className="becaisse-empty-cart">Aucun conflit.</p>}
        </div>
      </section>
      {canManageUsers ? (
      <section className="becaisse-settings-card">
        <h2>Bornes & appareils</h2>
        <p>Nomme chaque poste, puis affecte son imprimante client et son terminal. La zone conserve le routage production. Les quotas viennent du MASTER.</p>
        <div className="becaisse-device-limits">
          {[
            ['borne', 'Bornes'],
            ['caisse', 'Caisses'],
            ['pad', 'PAD'],
            ['kitchen', 'Cuisine'],
            ['fid', 'FID'],
            ['beguest', 'BEGuest'],
          ].map(([type, label]) => {
            const limit = deviceLimits?.[type] || {};
            const count = restaurantDevices.filter((device) => device.deviceType === type && device.isActive !== false).length;
            return (
              <span key={`device-limit-${type}`} className={limit.enabled === false ? 'is-disabled' : ''}>
                <b>{label}</b> {count}/{limit.limit ?? 0}
              </span>
            );
          })}
        </div>
        <div className="becaisse-device-settings-layout">
          <div className="becaisse-profile-list">
            <strong>Appareils configurés</strong>
            {devicesLoading ? <p className="becaisse-empty-cart">Chargement...</p> : null}
            {!devicesLoading && !restaurantDevices.length ? <p className="becaisse-empty-cart">Aucun appareil.</p> : null}
            {!devicesLoading && restaurantDevices.map((device) => (
              <button key={`device-${device.id}`} type="button" className="becaisse-profile-list-item" onClick={() => startEditDevice(device)}>
                <b>{device.name}</b>
                <small>{device.deviceType} · {device.productionZoneName || 'Aucune zone'}</small>
                <span>{device.isActive ? 'Actif' : 'Désactivé'} · {device.assignedIp || 'IP non assignée'}</span>
              </button>
            ))}
          </div>
          <div className="becaisse-profile-editor">
            <div className="becaisse-discount-row">
              <input
                value={deviceEditor.name}
                onChange={(event) => setDeviceEditor((current) => ({ ...current, name: event.target.value }))}
                placeholder="Nom appareil (ex: Borne entrée)"
              />
              <select value={deviceEditor.deviceType} onChange={(event) => setDeviceEditor((current) => ({ ...current, deviceType: event.target.value }))}>
                <option value="borne">Borne client</option>
                <option value="caisse">Caisse centrale</option>
                <option value="pad">PAD serveur</option>
                <option value="kitchen">Écran cuisine</option>
                <option value="fid">Écran FID</option>
                <option value="beguest">BEGuest table</option>
              </select>
            </div>
            <label className="becaisse-resa-setting-field becaisse-resa-setting-field-full">
              <span>Zone d’impression liée</span>
              <select
                value={deviceEditor.productionZoneId}
                onChange={(event) => setDeviceEditor((current) => ({ ...current, productionZoneId: event.target.value }))}
              >
                <option value="">Aucune zone spécifique</option>
                {profilesZones.map((zone) => (
                  <option key={`device-zone-${zone.id}`} value={zone.id}>{zone.name}</option>
                ))}
              </select>
              <small>Cette zone sert à router les impressions de l’appareil.</small>
            </label>
            <div className="becaisse-profile-switches">
              <DevicePeripheralFields value={deviceEditor} onChange={setDeviceEditor} printers={clientPrinters} />
              <label><input type="checkbox" checked={!!deviceEditor.isActive} onChange={(event) => setDeviceEditor((current) => ({ ...current, isActive: event.target.checked }))} /> Appareil actif</label>
            </div>
            <div className="becaisse-discount-row">
              <button type="button" onClick={resetDeviceEditor}>Nouvel appareil</button>
              <button type="button" onClick={saveDevice}>{deviceEditor.id ? 'Mettre à jour' : 'Créer appareil'}</button>
              {deviceEditor.id ? <button type="button" onClick={() => deleteDevice(deviceEditor.id)}>Supprimer</button> : null}
            </div>
          </div>
        </div>
      </section>
      ) : null}
      {canManageUsers ? (
      <section className="becaisse-settings-card">
        <h2>Profils & droits caisse / PAD</h2>
        <p>Crée des profils caisse centrale et PAD, gère les droits utilisateur, les codes et les zones d’impression autorisées.</p>
        <div className="becaisse-profile-settings-layout">
          <div className="becaisse-profile-list">
            <strong>Profils existants</strong>
            {profilesLoading ? <p className="becaisse-empty-cart">Chargement...</p> : null}
            {!profilesLoading && !profilesList.length ? <p className="becaisse-empty-cart">Aucun profil.</p> : null}
            {!profilesLoading && profilesList.map((profile) => (
              <button key={`profile-${profile.id}`} type="button" className="becaisse-profile-list-item" onClick={() => startEditProfile(profile)}>
                <b>{profile.username}</b>
                <small>{profile.profileType === 'central' ? 'Caisse centrale' : 'Utilisateur PAD'} · rôle {profile.role}</small>
                <span>{profile.isActive ? 'Actif' : 'Inactif'} · {profile.caisseEnabled ? 'Caisse' : '-'} {profile.borneEnabled ? 'PAD' : ''}</span>
              </button>
            ))}
          </div>
          <div className="becaisse-profile-editor">
            <div className="becaisse-discount-row">
              <input
                value={profileEditor.username}
                onChange={(event) => setProfileEditor((current) => ({ ...current, username: event.target.value }))}
                placeholder="Nom utilisateur"
              />
              <input
                type="password"
                value={profileEditor.password}
                onChange={(event) => setProfileEditor((current) => ({ ...current, password: event.target.value }))}
                placeholder={profileEditor.id ? 'Nouveau code (optionnel)' : 'Code utilisateur'}
              />
            </div>
            <div className="becaisse-discount-row">
              <select value={profileEditor.profileType} onChange={(event) => setProfileEditor((current) => ({ ...current, profileType: event.target.value }))}>
                <option value="central">Caisse centrale</option>
                <option value="pad">Utilisateur PAD</option>
              </select>
              <select value={profileEditor.role} onChange={(event) => setProfileEditor((current) => ({ ...current, role: event.target.value }))}>
                <option value="admin">Admin</option>
                <option value="manager">Manager</option>
                <option value="operator">Opérateur</option>
              </select>
              <input
                inputMode="numeric"
                value={profileEditor.userCode}
                onChange={(event) => setProfileEditor((current) => ({ ...current, userCode: String(event.target.value || '').replace(/\D/g, '').slice(0, 8) }))}
                placeholder="Code PIN utilisateur"
              />
            </div>
            <div className="becaisse-profile-switches">
              <label><input type="checkbox" checked={profileEditor.caisseEnabled} onChange={(event) => setProfileEditor((current) => ({ ...current, caisseEnabled: event.target.checked }))} /> Accès caisse</label>
              <label><input type="checkbox" checked={profileEditor.borneEnabled} onChange={(event) => setProfileEditor((current) => ({ ...current, borneEnabled: event.target.checked }))} /> Accès PAD</label>
              <label><input type="checkbox" checked={profileEditor.isActive} onChange={(event) => setProfileEditor((current) => ({ ...current, isActive: event.target.checked }))} /> Compte actif</label>
            </div>
            <div className="becaisse-profile-switches">
              <label><input type="checkbox" checked={!!profileEditor.permissions.canOpenCashDrawer} onChange={(event) => setProfileEditor((current) => ({ ...current, permissions: { ...current.permissions, canOpenCashDrawer: event.target.checked } }))} /> Ouvrir le tiroir de caisse</label>
              <label><input type="checkbox" checked={!!profileEditor.permissions.canManageUsers} onChange={(event) => setProfileEditor((current) => ({ ...current, permissions: { ...current.permissions, canManageUsers: event.target.checked } }))} /> Gérer utilisateurs</label>
              <label><input type="checkbox" checked={!!profileEditor.permissions.canManageProducts} onChange={(event) => setProfileEditor((current) => ({ ...current, permissions: { ...current.permissions, canManageProducts: event.target.checked } }))} /> Gérer produits</label>
              <label><input type="checkbox" checked={!!profileEditor.permissions.canManageReservations} onChange={(event) => setProfileEditor((current) => ({ ...current, permissions: { ...current.permissions, canManageReservations: event.target.checked } }))} /> Gérer réservations</label>
              <label><input type="checkbox" checked={!!profileEditor.permissions.canManageReports} onChange={(event) => setProfileEditor((current) => ({ ...current, permissions: { ...current.permissions, canManageReports: event.target.checked } }))} /> Voir rapports</label>
              <label><input type="checkbox" checked={!!profileEditor.permissions.canRefund} onChange={(event) => setProfileEditor((current) => ({ ...current, permissions: { ...current.permissions, canRefund: event.target.checked } }))} /> Remboursements</label>
              <label><input type="checkbox" checked={!!profileEditor.permissions.canApplyDiscountOver20} onChange={(event) => setProfileEditor((current) => ({ ...current, permissions: { ...current.permissions, canApplyDiscountOver20: event.target.checked } }))} /> Remise > 20%</label>
            </div>
            <label className="becaisse-resa-setting-field becaisse-resa-setting-field-full">
              <span>Zones d’impression autorisées</span>
              <select
                multiple
                value={safeArray(profileEditor.printZoneIds).map((value) => String(value))}
                onChange={(event) => {
                  const values = Array.from(event.target.selectedOptions).map((option) => Number(option.value)).filter((value) => Number.isFinite(value));
                  setProfileEditor((current) => ({ ...current, printZoneIds: values }));
                }}
              >
                {profilesZones.map((zone) => (
                  <option key={`profile-zone-${zone.id}`} value={zone.id}>{zone.name}</option>
                ))}
              </select>
              <small>Maintiens Ctrl (ou Cmd) pour sélectionner plusieurs zones.</small>
            </label>
            <div className="becaisse-discount-row">
              <button type="button" onClick={resetProfileEditor}>Nouveau profil</button>
              <button type="button" onClick={saveProfile}>{profileEditor.id ? 'Mettre à jour' : 'Créer profil'}</button>
              <button type="button" onClick={loadProfiles}>Rafraîchir</button>
            </div>
          </div>
        </div>
      </section>
      ) : null}
      <section className="becaisse-settings-card">
        <h2>Réservations</h2>
        <p>Configurez les horaires et la capacité utilisés par la page de réservation et la caisse.</p>
        <div className="becaisse-resa-settings-grid">
          <label className="becaisse-resa-setting-field">
            <span>Heure d’ouverture</span>
            <input
              type="number"
              min={0}
              max={23}
              value={reservationConfigDraft.reservationOpeningHour}
              onChange={(event) => setReservationConfigDraft((current) => ({ ...current, reservationOpeningHour: event.target.value }))}
            />
            <small>Début des réservations (ex: 12).</small>
          </label>
          <label className="becaisse-resa-setting-field">
            <span>Heure de fermeture</span>
            <input
              type="number"
              min={1}
              max={23}
              value={reservationConfigDraft.reservationClosingHour}
              onChange={(event) => setReservationConfigDraft((current) => ({ ...current, reservationClosingHour: event.target.value }))}
            />
            <small>Dernier créneau généré (ex: 22).</small>
          </label>
          <label className="becaisse-resa-setting-field">
            <span>Pas des créneaux (minutes)</span>
            <input
              type="number"
              min={15}
              max={120}
              step={5}
              value={reservationConfigDraft.reservationSlotMinutes}
              onChange={(event) => setReservationConfigDraft((current) => ({ ...current, reservationSlotMinutes: event.target.value }))}
            />
            <small>Intervalle entre créneaux: 15, 30, 60...</small>
          </label>
          <label className="becaisse-resa-setting-field">
            <span>Max couverts par réservation</span>
            <input
              type="number"
              min={1}
              max={30}
              value={reservationConfigDraft.reservationMaxPartySize}
              onChange={(event) => setReservationConfigDraft((current) => ({ ...current, reservationMaxPartySize: event.target.value }))}
            />
            <small>Limite pour une seule réservation client.</small>
          </label>
          <label className="becaisse-resa-setting-field">
            <span>Capacité totale simultanée</span>
            <input
              type="number"
              min={1}
              max={300}
              value={reservationConfigDraft.reservationTableCapacity}
              onChange={(event) => setReservationConfigDraft((current) => ({ ...current, reservationTableCapacity: event.target.value }))}
            />
            <small>Total de couverts acceptés sur un même créneau.</small>
          </label>
          <label className="becaisse-resa-setting-field">
            <span>Durée moyenne d’une table (minutes)</span>
            <input
              type="number"
              min={15}
              max={360}
              step={15}
              value={reservationConfigDraft.reservationDefaultDurationMinutes}
              onChange={(event) => setReservationConfigDraft((current) => ({ ...current, reservationDefaultDurationMinutes: event.target.value }))}
            />
            <small>Prise en compte dans le calcul des places disponibles lorsque les réservations se chevauchent.</small>
          </label>
          <label className="becaisse-resa-setting-field becaisse-resa-setting-field-full">
            <span>Modes de paiement activés (séparés par virgule)</span>
            <input
              value={paymentMethodsDraft}
              onChange={(event) => setPaymentMethodsDraft(event.target.value)}
              placeholder="CB, Espèces, Ticket resto, Chèque vacances, Compte client"
            />
            <small>Exemple: CB, Espèces, Ticket resto, Chèque vacances, Compte client.</small>
          </label>
          <label className="becaisse-resa-setting-field">
            <span>Chrono produits: alerte dépassement</span>
            <select
              value={reservationConfigDraft.kdsItemTimerLimitEnabled ? 'on' : 'off'}
              onChange={(event) => setReservationConfigDraft((current) => ({
                ...current,
                kdsItemTimerLimitEnabled: event.target.value === 'on',
              }))}
            >
              <option value="off">Désactivé</option>
              <option value="on">Activé</option>
            </select>
            <small>Quand activé, le chrono passe rouge clignotant au-delà du délai.</small>
          </label>
          <label className="becaisse-resa-setting-field">
            <span>Délai limite chrono (secondes)</span>
            <input
              type="number"
              min={30}
              max={7200}
              step={10}
              value={reservationConfigDraft.kdsItemTimerLimitSeconds}
              onChange={(event) => setReservationConfigDraft((current) => ({ ...current, kdsItemTimerLimitSeconds: event.target.value }))}
              disabled={!reservationConfigDraft.kdsItemTimerLimitEnabled}
            />
            <small>Exemple: 300 = 5 min.</small>
          </label>
        </div>
        <button type="button" className="becaisse-checkout" onClick={saveReservationSettings} disabled={checkoutLoading}>
          <span>{checkoutLoading ? 'Enregistrement...' : 'Enregistrer paramètres réservation'}</span>
          <strong>OK</strong>
        </button>
      </section>
    </div>
  ) : activeView === 'clients' ? (
    <div className="becaisse-settings-panel">
      <div className="becaisse-section-title">
        <span>Clients caisse</span>
        <h1>Compte client</h1>
      </div>
      <BefidAddonEntry brand="becaisse" returnTo={isPad ? 'pad' : 'caisse'} />
      <section className="becaisse-settings-card">
        <h2>Recherche client</h2>
        <p>Retrouve un client pour consulter son solde, ses points et ses coordonnées.</p>
        <div className="becaisse-discount-row">
          <input
            value={customersSearch}
            onChange={(event) => setCustomersSearch(event.target.value)}
            placeholder="Nom, email ou téléphone"
          />
          <button type="button" onClick={() => fetchCustomers(customersSearch)} disabled={customersLoading}>
            {customersLoading ? 'Recherche...' : 'Rechercher'}
          </button>
        </div>
      </section>
      <section className="becaisse-settings-card">
        <h2>Liste clients</h2>
        <div className="becaisse-table-open-orders">
          {customersList.length ? customersList.map((customer) => (
            <div key={customer.id} className="becaisse-table-order-row">
              <span>{customer.firstName} {customer.lastName}</span>
              <small>{customer.phone || customer.email || 'Aucun contact'}</small>
              <b>{formatCurrency(customer.accountBalance || 0)} · {Number(customer.points || 0)} pts</b>
            </div>
          )) : <p className="becaisse-empty-cart">{customersLoading ? 'Chargement...' : 'Aucun client trouvé.'}</p>}
        </div>
      </section>
    </div>
  ) : activeView === 'reservations' ? (
    <CaisseReservationsView embedded onBack={() => setActiveView('sale')} />
  ) : (
    <div className={isPadSalesSplit ? `becaisse-pad-sales-menu-pane ${padCartExpanded ? 'is-hidden' : ''}` : ''}>
      {productGrid}
    </div>
  );

  const isManagementView = ['control', 'reports', 'settings', 'reservations', 'commandes', 'clients'].includes(activeView);

  return (
    <main className={`becaisse-page ${isPad ? 'is-pad' : 'is-desktop'} view-${activeView} ${sidebarCollapsed ? 'is-sidebar-collapsed' : ''}`}>
      {!isPad && (
        <aside className="becaisse-sidebar">
          <button
            type="button"
            className="becaisse-sidebar-toggle"
            onClick={() => setSidebarCollapsed((value) => !value)}
            aria-label={sidebarCollapsed ? 'Déplier le volet' : 'Replier le volet'}
          >
            {sidebarCollapsed ? '›' : '‹'}
          </button>
          <div className="becaisse-brand">
            <picture className="suite-brand-v3"><source media="(max-width: 600px)" srcSet="/static/branding-v5/becaisse/compact.svg" /><img src={becaisseLogo} alt="BECAISSE" /></picture>
            <div>
              <strong>BE Caisse</strong>
              <button type="button" className="becaisse-account-switch-trigger" onClick={openAccountSwitch}>
                {currentAccountName}
              </button>
            </div>
          </div>
          <div className="becaisse-sidebar-tools">
            <label className="becaisse-sidebar-search">
              <span>🔎</span>
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Rechercher" />
            </label>
            <button type="button">
              <span>📷</span>
              <b>Scanner</b>
            </button>
          </div>
          <nav>
            <button type="button" className={activeView === 'sale' ? 'is-active' : ''} onClick={() => setActiveView('sale')}><span>🛒</span><b>Vente</b></button>
            <button type="button" className={activeView === 'commandes' ? 'is-active' : ''} onClick={() => setActiveView('commandes')}><span>🧾</span><b>Commandes</b></button>
            <button type="button" className={activeView === 'tables' ? 'is-active' : ''} onClick={() => setActiveView('tables')}><span>🍽️</span><b>Tables</b></button>
            <button type="button" className={activeView === 'clients' ? 'is-active' : ''} onClick={() => setActiveView('clients')}><span>👤</span><b>Clients</b></button>
            {canManageReports ? (
              <button type="button" className={activeView === 'control' || activeView === 'reports' ? 'is-active' : ''} onClick={() => setActiveView('control')}><span>🛡️</span><b>Pilotage</b></button>
            ) : null}
            {canManageReservations ? (
              <button type="button" className={activeView === 'reservations' ? 'is-active' : ''} onClick={() => setActiveView('reservations')}><span>📅</span><b>Réservations</b></button>
            ) : null}
            {canOpenSettingsView ? (
              <button type="button" className={activeView === 'settings' ? 'is-active' : ''} onClick={() => setActiveView('settings')}><span>⚙</span><b>Paramètres</b></button>
            ) : null}
          </nav>
          <div className="becaisse-sidebar-foot">
            <span>{new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
            <small>Module connecté</small>
          </div>
        </aside>
      )}

      {isPad && padMenuOpen ? (
        <div className="becaisse-pad-menu-layer" role="dialog" aria-modal="true" aria-label="Menu navigation">
          <button type="button" className="becaisse-pad-menu-backdrop" onClick={() => setPadMenuOpen(false)} aria-label="Fermer le menu" />
          <aside className="becaisse-pad-menu-drawer">
            <header className="becaisse-pad-menu-head">
              <strong>Navigation</strong>
              <button type="button" onClick={() => setPadMenuOpen(false)}>✕</button>
            </header>
            <nav className="becaisse-pad-menu-list">
              <button type="button" className={activeView === 'sale' ? 'is-active' : ''} onClick={() => { setActiveView('sale'); setPadMenuOpen(false); }}>Vente</button>
              <button type="button" className={activeView === 'commandes' ? 'is-active' : ''} onClick={() => { setActiveView('commandes'); setPadMenuOpen(false); }}>Commandes</button>
              <button type="button" className={activeView === 'tables' || tablePickerOpen ? 'is-active' : ''} onClick={openPadTableSelection}>Tables</button>
              <button type="button" className={activeView === 'clients' ? 'is-active' : ''} onClick={() => { setActiveView('clients'); setPadMenuOpen(false); }}>Clients</button>
              {canManageReports ? (
                <button type="button" className={activeView === 'control' || activeView === 'reports' ? 'is-active' : ''} onClick={() => { setActiveView('control'); setPadMenuOpen(false); }}>Pilotage</button>
              ) : null}
              {canManageReservations ? (
                <button type="button" className={activeView === 'reservations' ? 'is-active' : ''} onClick={() => { setActiveView('reservations'); setPadMenuOpen(false); }}>Réservations</button>
              ) : null}
              {canOpenSettingsView ? (
                <button type="button" className={activeView === 'settings' ? 'is-active' : ''} onClick={() => { setActiveView('settings'); setPadMenuOpen(false); }}>Paramètres</button>
              ) : null}
            </nav>
          </aside>
        </div>
      ) : null}

      <section className="becaisse-workspace">
        <picture className="pad-brand-v3 workspace-brand-v3"><source media="(max-width:600px)" srcSet="/static/branding-v5/becaisse/compact.svg" /><img src="/static/branding-v4/becaisse/horizontal.svg" alt="BECAISSE" /></picture>
        <div className="workstation-cash-controls">
          <WorkstationSettings state={workstation} canBind={accountRole === 'admin'} />
          {canOpenCashDrawer && workstation.device?.drawerEnabled ? <button type="button" disabled={drawerBusy || !openCashSession || !isOnline || workstation.loading || !!workstation.error} onClick={() => setDrawerFormOpen(true)}>Ouvrir le tiroir</button> : null}
          {drawerFormOpen ? <form onSubmit={event => { event.preventDefault(); openDrawer(); }}><label>Motif d’ouverture<input required maxLength={200} value={drawerReason} disabled={drawerBusy} onChange={event => setDrawerReason(event.target.value)} /></label><button type="submit" disabled={drawerBusy || !drawerReason.trim()}>Confirmer l’ouverture</button><button type="button" disabled={drawerBusy} onClick={() => setDrawerFormOpen(false)}>Annuler</button></form> : null}
          {drawerMessage ? <p role="status">{drawerMessage}</p> : null}
        </div>

        {activeView === 'sale' ? (
          <nav className="becaisse-categories" aria-label="Catégories caisse">
            {isPad ? (
              <button
                type="button"
                className="becaisse-pad-menu-trigger"
                onClick={() => setPadMenuOpen(true)}
                aria-label="Ouvrir le menu"
              >
                ☰
              </button>
            ) : null}
            {categories.map((category) => (
              <button
                key={category.id}
                type="button"
                className={String(activeSectionId) === String(category.id) ? 'is-active' : ''}
                onClick={() => setActiveSectionId(String(category.id))}
              >
                {!isPad ? <span>{category.icon}</span> : null}
                {category.name}
              </button>
            ))}
          </nav>
        ) : null}

        {(notice || error) && (
          <div className={`becaisse-message ${error ? 'is-error' : 'is-success'}`}>
            {error || notice}
          </div>
        )}

        <div className={`becaisse-content ${isManagementView ? 'is-management-view' : ''} ${isPadSalesSplit ? 'is-pad-sales-split' : ''}`}>
          {mainContent}
          {(!isPad && !isManagementView) || isPadSalesSplit ? cartPanel : null}
        </div>
      </section>

      {isPad ? (
        <>
          {activeView === 'commandes' || isPadSalesSplit ? null : cartPanel}
        </>
      ) : null}

      <AccountSwitchModal
        open={accountSwitchOpen}
        loading={accountSwitchLoading}
        form={accountSwitchForm}
        accountOptions={accountSwitchOptions}
        error={accountSwitchError}
        onClose={() => setAccountSwitchOpen(false)}
        onChange={(field, value) => setAccountSwitchForm((current) => ({ ...current, [field]: value }))}
        onSubmit={submitAccountSwitch}
      />

      {hasLegacyQueue ? <div role="status" className="becaisse-notice">Anciennes actions hors ligne conservées, sans compte identifié : reprise automatique désactivée.</div> : null}
      {terminalOrderId ? <TerminalPaymentDialog
        orderId={terminalOrderId}
        fetchWithAuth={fetchWithAuth}
        onPaid={async () => {
          setNotice(`Commande #${terminalOrderId} encaissée sur le terminal.`);
          pendingOrderRef.current = null;
          await Promise.all([fetchOpenOrders(), fetchOrdersHistory()]);
        }}
        onPrintReceipt={async payload => {
          const order = ordersHistory.find(row => Number(row.id) === Number(terminalOrderId));
          if (!order || order.paymentStatus !== 'paid') throw new Error('Actualisez la commande avant impression.');
          await printSelectedOrderTicket('final', order, payload.receipt || await getTerminalReceipt(fetchWithAuth, terminalOrderId, offlineScope));
        }}
        onClose={() => { setTerminalOrderId(null); Promise.all([fetchOpenOrders(), fetchOrdersHistory()]); }}
      /> : null}
      {refundDraft ? <div className="becaisse-modal-backdrop"><section className="becaisse-payment-modal" role="dialog" aria-modal="true" aria-label="Rembourser le ticket"><h2>Rembourser le ticket #{refundDraft.order.ticketNumber || refundDraft.order.id}</h2><form className="admin-form" onSubmit={event => { event.preventDefault(); refundOrder(); }}>
        <label>Montant à rembourser<input type="number" min="0.01" step="0.01" max={Math.max(0, Number(refundDraft.order.totalTtc) - Number(refundDraft.order.refundedTtc || 0))} required value={refundDraft.amountTtc} onChange={event => setRefundDraft(current => ({ ...current, amountTtc: event.target.value }))} /></label>
        <label>Moyen du remboursement<select required value={refundDraft.paymentMethod} onChange={event => setRefundDraft(current => ({ ...current, paymentMethod: event.target.value }))}><option value="">Choisir un moyen unique</option>{configuredPaymentMethods.filter(method => method.id !== 'mixte').map(method => <option key={method.id} value={method.id}>{method.label}</option>)}</select></label>
        <label>Motif du remboursement<input required maxLength={200} value={refundDraft.reason} onChange={event => setRefundDraft(current => ({ ...current, reason: event.target.value }))} /></label>
        <p>Les paiements Stripe se remboursent dans Stripe. Cette action enregistre uniquement les remboursements autorisés par la caisse.</p>
        <button type="submit" disabled={checkoutLoading || !refundDraft.reason.trim() || !refundDraft.paymentMethod}>Confirmer le remboursement</button><button type="button" disabled={checkoutLoading} onClick={() => setRefundDraft(null)}>Annuler</button>
      </form></section></div> : null}

      {paymentOpen && (
        <PaymentModal
          totals={activePaymentTotals}
          initialMethod={paymentMethod}
          availablePaymentMethods={configuredPaymentMethods}
          paymentTarget={paymentTarget}
          allowMixedPayment={paymentTarget !== 'table' || (selectedTableOrders.length || tableOrders.length) <= 1}
          cart={cart}
          splitDraft={splitDraft}
          onSplitQtyChange={updateCartSplitQty}
          discountMode={discountMode}
          discountValue={discountValue}
          onDiscountModeChange={setDiscountMode}
          onDiscountValueChange={setDiscountValue}
          maxGlobalDiscountPercent={maxGlobalDiscountPercent}
          loading={checkoutLoading}
          onClose={() => setPaymentOpen(false)}
          onSubmit={(paymentOptions) => {
            setPaymentMethod(paymentOptions.paymentMethod || paymentMethod);
            handleCheckout(paymentOptions);
          }}
        />
      )}
      <CancelReasonModal
        open={cancelModalOpen}
        loading={checkoutLoading}
        value={cancelReasonValue}
        onChange={setCancelReasonValue}
        onClose={() => {
          if (checkoutLoading) return;
          setCancelModalOpen(false);
        }}
        onSubmit={() => cancelSelectedOrder(cancelReasonValue)}
      />
      <SentLineCancelModal
        open={!!sentLineCancelTarget}
        loading={sentLineCancelLoading}
        line={sentLineCancelTarget}
        value={sentLineCancelReason}
        onChange={setSentLineCancelReason}
        onClose={closeSentLineCancel}
        onConfirm={confirmSentLineCancel}
      />
      {recipeProduct && <div className="becaisse-modal-backdrop" role="dialog" aria-modal="true" aria-label="Choisir la composition"><div className="becaisse-modal"><div className="becaisse-modal-body"><h2>{recipeProduct.name}</h2><StockRecipeChoices product={recipeProduct} onChange={stockChoice => setRecipeProduct(current => ({ ...current, stockChoice }))} /><div className="becaisse-modal-actions"><button type="button" onClick={() => setRecipeProduct(null)}>Annuler</button><button type="button" className="becaisse-checkout" disabled={!stockChoiceValid(recipeProduct)} onClick={() => { if (stockChoiceValid(recipeProduct)) { addToCart(recipeProduct); setRecipeProduct(null); } }}>Ajouter au panier</button></div></div></div></div>}
      {menuComposer.open && menuComposer.menu ? (
        <div className="becaisse-modal-backdrop" role="dialog" aria-modal="true" aria-label="Composer une formule">
          <div className="becaisse-modal becaisse-menu-composer-modal">
            <button type="button" className="becaisse-modal-close" onClick={closeMenuComposer} aria-label="Fermer">×</button>
            <div className="becaisse-modal-body">
              <span className="becaisse-pill">Formule</span>
              <h2>{menuComposer.menu.name}</h2>
              {menuComposer.menu.description ? <p>{menuComposer.menu.description}</p> : null}
              <div className="becaisse-menu-composer-price">
                <span>Total formule</span>
                <strong>{formatCurrency(getMenuUnitPrice(menuComposer.menu, menuComposer.selections))}</strong>
              </div>
              <div className="becaisse-menu-composer-groups">
                {safeArray(menuComposer.menu.Sections).map((section) => {
                  const sectionId = String(section.id);
                  const requiredQuantity = getMenuRequiredQuantity(section);
                  const selectedIds = safeArray(menuComposer.selections[sectionId]).map(String);
                  const sectionProducts = getMenuProductsForSection(menuComposer.menu, section.id);
                  return (
                    <section className="becaisse-menu-composer-group" key={`menu-section-${section.id}`}>
                      <header>
                        <strong>{section.name}</strong>
                        <span>{selectedIds.length}/{requiredQuantity}</span>
                      </header>
                      <div className="becaisse-menu-composer-options">
                        {sectionProducts.length ? sectionProducts.map((product) => {
                          const selected = selectedIds.includes(String(product.id));
                          const additionalPrice = getMenuProductAdditionalPrice(product);
                          return (
                            <button
                              type="button"
                              className={selected ? 'is-selected' : ''}
                              key={`menu-product-${section.id}-${product.id}`}
                              onClick={() => toggleMenuComposerProduct(section, product)}
                            >
                              <span>{product.name}</span>
                              {additionalPrice > 0 ? <small>+ {formatCurrency(additionalPrice)}</small> : <small>Inclus</small>}
                            </button>
                          );
                        }) : <p className="becaisse-empty-cart">Aucun produit disponible dans cette étape.</p>}
                      </div>
                      {sectionProducts.filter(p => selectedIds.includes(String(p.id))).map(product => <StockRecipeChoices key={product.id} product={{ ...product, stockChoice: menuComposer.stockChoices?.[`${section.id}:${product.id}`] || stockChoiceFor(product) }} onChange={stockChoice => setMenuComposer(current => ({ ...current, stockChoices: { ...current.stockChoices, [`${section.id}:${product.id}`]: stockChoice } }))} />)}
                    </section>
                  );
                })}
              </div>
              {menuComposer.error ? <p className="becaisse-menu-composer-error">{menuComposer.error}</p> : null}
              <div className="becaisse-modal-actions">
                <button type="button" className="becaisse-top-mini" onClick={closeMenuComposer}>Annuler</button>
                <button type="button" className="becaisse-checkout" onClick={confirmMenuComposer}>Ajouter la formule</button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      <LockScreenModal
        open={isLocked}
        pinInput={unlockPinInput}
        error={unlockError}
        onChange={(value) => {
          setUnlockPinInput(String(value || '').replace(/\D/g, '').slice(0, 8));
          if (unlockError) setUnlockError('');
        }}
        onUnlock={unlockCaisse}
      />
      <CoversModal
        open={coversModalOpen}
        tableNumber={coversTargetTable}
        value={coversDraft}
        onChange={setCoversDraft}
        onClose={() => setCoversModalOpen(false)}
        onConfirm={saveCovers}
      />
      <QtyPadModal
        open={qtyModalOpen}
        itemName={qtyModalItemName}
        value={qtyModalValue}
        onChange={setQtyModalValue}
        onClose={() => setQtyModalOpen(false)}
        onConfirm={confirmQtyPad}
      />
      <NoteLineModal
        open={noteModalOpen}
        itemName={noteModalItemName}
        value={noteModalValue}
        onChange={setNoteModalValue}
        onClose={() => setNoteModalOpen(false)}
        onConfirm={confirmLineNote}
      />
      <MergeTableModal
        open={mergeModalOpen}
        currentTable={tableNumber}
        mergeSourceTable={mergeSourceTable}
        options={tableCards.filter((table) => String(table.number) !== String(tableNumber) && table.total > 0)}
        loading={checkoutLoading}
        onClose={() => setMergeModalOpen(false)}
        onSelect={setMergeSourceTable}
        onConfirm={mergeIntoCurrentTable}
      />
      <TablePickerModal
        open={tablePickerOpen}
        currentTable={tableNumber}
        options={tableCards}
        onClose={() => setTablePickerOpen(false)}
        onSelect={(nextTable) => {
          setTablePickerOpen(false);
          selectTable(nextTable);
        }}
      />
      <BeguestServiceAlertModal
        alert={activeBeguestAlert}
        loading={beguestAlertLoading}
        onDismiss={() => dismissBeguestAlert(activeBeguestAlert?.id)}
        onClaim={() => claimBeguestAlert(activeBeguestAlert)}
      />
    </main>
  );
}

export default CaisseView;
